import React, { useState, useRef, useEffect, useMemo } from "react";
import * as htmlToImage from "html-to-image";
import { BrandProfile } from "../types";
import {
  Upload,
  Image as ImageIcon,
  Sparkles,
  Layout,
  Type,
  Plus,
  Trash2,
  ShieldAlert,
  Wand2,
  CheckCircle2,
  Download,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ArrowRight,
  Star,
  X,
  Menu,
  Settings,
  Layers,
} from "lucide-react";
import * as Lucide from "lucide-react";
import { auth, db } from "../firebase";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { generateArtwork, suggestLucideIcon } from "../lib/gemini";

interface TextBoundingBox {
  id: string;
  type:
    | "heading"
    | "subheading"
    | "body"
    | "eyebrow"
    | "footer"
    | "icon-list"
    | "logo"
    | "cta"
    | "divider"
    | "image";
  top: number; // percentage
  left: number; // percentage
  width: number; // percentage
  height: number; // percentage
  text: string;
  color: string;
  fontFamily: string;
  fontSize: number;
  lineHeight?: number;
  letterSpacing?: string;
  yOffset?: number; // em offset
  xOffset?: number; // em offset
  textAlign?: "left" | "center" | "right";
  iconType?:
    | "check"
    | "star"
    | "arrow"
    | "plus"
    | "sparkles"
    | "check-double"
    | "award"
    | "heart"
    | "dot"
    | "custom";
  customIcon?: string;
  flowNaturally?: boolean;
  renderAsButton?: boolean;
  backgroundColor?: string;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  padding?: number;
  iconPlacement?: "none" | "before" | "after";
  imageUrl?: string;
}

export function parseOverlayTextToBoxes(overlayText: string, boxes: any[]): any[] {
  if (!boxes || boxes.length === 0) return [];
  // Exclude random internal control tags
  const cleanOverlayText = (overlayText || "").replace(/\[DIVIDER\]/g, "").replace(/\[DIVIDER\]\s*/gi, "");
  const overlayLines = cleanOverlayText.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  if (overlayLines.length === 0) {
    return boxes.map(b => b.type === 'logo' ? b : { ...b, text: "" });
  }

  // Pre-initialize mapped texts for different box types
  const mapped: Record<string, string> = {};
  const bulletLines: string[] = [];
  const normalLines: string[] = [];

  let currentSection: string | null = null;

  for (const line of overlayLines) {
    const upperLine = line.toUpperCase();
    
    // Check for explicit prefix labels
    if (upperLine.startsWith("EYEBROW:") || upperLine.startsWith("[EYEBROW]")) {
      currentSection = "eyebrow";
      const content = line.replace(/^(eyebrow:|\[eyebrow\])\s*/i, "").trim();
      if (content) mapped["eyebrow"] = (mapped["eyebrow"] ? mapped["eyebrow"] + "\n" + content : content);
      continue;
    }
    if (upperLine.startsWith("HEADING:") || upperLine.startsWith("[HEADING]") || upperLine.startsWith("TITLE:") || upperLine.startsWith("[TITLE]")) {
      currentSection = "heading";
      const content = line.replace(/^(heading:|\[heading\]|title:|\[title\])\s*/i, "").trim();
      if (content) mapped["heading"] = (mapped["heading"] ? mapped["heading"] + "\n" + content : content);
      continue;
    }
    if (upperLine.startsWith("SUBHEADING:") || upperLine.startsWith("[SUBHEADING]")) {
      currentSection = "subheading";
      const content = line.replace(/^(subheading:|\[subheading\])\s*/i, "").trim();
      if (content) mapped["subheading"] = (mapped["subheading"] ? mapped["subheading"] + "\n" + content : content);
      continue;
    }
    if (upperLine.startsWith("BODY:") || upperLine.startsWith("[BODY]") || upperLine.startsWith("CONTENT:") || upperLine.startsWith("[CONTENT]")) {
      currentSection = "body";
      const content = line.replace(/^(body:|\[body\]|content:|\[content\])\s*/i, "").trim();
      if (content) mapped["body"] = (mapped["body"] ? mapped["body"] + "\n" + content : content);
      continue;
    }
    if (upperLine.startsWith("BULLETS:") || upperLine.startsWith("[BULLETS]") || upperLine.startsWith("LIST:") || upperLine.startsWith("[LIST]")) {
      currentSection = "bullets";
      const content = line.replace(/^(bullets:|\[bullets\]|list:|\[list\])\s*/i, "").trim();
      if (content) {
        bulletLines.push(content);
      }
      continue;
    }
    if (upperLine.startsWith("CTA:") || upperLine.startsWith("[CTA]") || upperLine.startsWith("BUTTON:") || upperLine.startsWith("[BUTTON]")) {
      currentSection = "cta";
      const content = line.replace(/^(cta:|\[cta\]|button:|\[button\])\s*/i, "").trim();
      if (content) mapped["cta"] = content;
      continue;
    }

    // If no prefix but starts with a bullet character, treat as a bullet line
    const isBulletChar = /^[-•*✔️☑️✅⚡🚀📍✓]\s/.test(line) || /^[\u2700-\u27bf] | [\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF]/.test(line);
    if (isBulletChar) {
      bulletLines.push(line);
      continue;
    }

    // Append to current section if active
    if (currentSection) {
      if (currentSection === "bullets") {
        bulletLines.push(line);
      } else {
        mapped[currentSection] = (mapped[currentSection] ? mapped[currentSection] + "\n" + line : line);
      }
    } else {
      // Collect into generic lines for fallback matching
      normalLines.push(line);
    }
  }

  // If we have mapped fields from explicit labels, use them to populate the boxes
  const hasLabeledFields = Object.keys(mapped).length > 0 || bulletLines.length > 0;

  // Find index of boxes by their designated types
  const headingBox = boxes.find(b => (b.type || "").toLowerCase().includes("heading") || b.id === "h1");
  const eyebrowBox = boxes.find(b => (b.type || "").toLowerCase().includes("eyebrow"));
  const subheadingBox = boxes.find(b => (b.type || "").toLowerCase() === "subheading");
  const bodyBox = boxes.find(b => (b.type || "").toLowerCase().includes("body") || b.id === "b1");
  const bulletBox = boxes.find(b => (b.type || "").toLowerCase() === "icon-list" || b.id === "bullets" || (b.type || "").toLowerCase().includes("list"));
  const ctaBox = boxes.find(b => (b.type || "").toLowerCase() === "cta" || !!b.renderAsButton);

  if (hasLabeledFields) {
    return boxes.map((box) => {
      if (box.type === "logo") return box;
      
      let textVal = "";
      if (box === headingBox) textVal = mapped["heading"] || "";
      else if (box === eyebrowBox) textVal = mapped["eyebrow"] || "";
      else if (box === subheadingBox) textVal = mapped["subheading"] || "";
      else if (box === bodyBox) textVal = mapped["body"] || "";
      else if (box === bulletBox) textVal = bulletLines.join("\n");
      else if (box === ctaBox) textVal = mapped["cta"] || "";

      // Fallback: if the box remains empty but the original template had text, keep it
      return {
        ...box,
        text: textVal || box.text || ""
      };
    });
  }

  // FALLBACK: Traditional unlabeled sequential mapping but much safer and smarter
  let currentNormalIdx = 0;
  let fallbackEyebrow = "";
  let fallbackHeading = "";
  const fallbackBodyLines: string[] = [];

  // 1. If eyebrow exists and first line is short (< 40 chars) and there are at least 2 remaining lines, treat first line as eyebrow
  if (eyebrowBox && normalLines.length > 2 && normalLines[0].length < 40) {
    fallbackEyebrow = normalLines[currentNormalIdx++];
  }
  
  // 2. Map next line to Heading
  if (headingBox && currentNormalIdx < normalLines.length) {
    fallbackHeading = normalLines[currentNormalIdx++];
  }

  // 3. Map any remaining lines to body
  for (let i = currentNormalIdx; i < normalLines.length; i++) {
    fallbackBodyLines.push(normalLines[i]);
  }

  return boxes.map((box) => {
    if (box.type === "logo") return box;

    let textVal = box.text || "";
    if (box === headingBox && fallbackHeading) {
      textVal = fallbackHeading;
    } else if (box === eyebrowBox && fallbackEyebrow) {
      textVal = fallbackEyebrow;
    } else if (box === bodyBox) {
      textVal = fallbackBodyLines.length > 0 ? fallbackBodyLines.join("\n") : (bulletLines.length > 0 ? bulletLines.join("\n") : textVal);
    } else if (box === bulletBox) {
      textVal = bulletLines.length > 0 ? bulletLines.join("\n") : "";
    } else if (box === ctaBox && fallbackBodyLines.length > 0 && !bodyBox) {
      textVal = fallbackBodyLines[fallbackBodyLines.length - 1];
    }
    return {
      ...box,
      text: textVal
    };
  });
}

interface ParsedLine {
  text: string;
  noIcon?: boolean;
  iconType?: string;
  customIcon?: string;
  containerShape?: "none" | "circle" | "square";
  containerStyle?: "outline" | "fill" | "none";
  iconColor?: string;
  backgroundColor?: string;
  index: number;
}

interface SemanticIconMapping {
  category: string;
  icon: string;
  keywords: string[];
  description: string;
}

export const SEMANTIC_ICON_DICTIONARY: SemanticIconMapping[] = [
  {
    category: "Communication",
    icon: "Phone",
    keywords: ["phone", "call", "tel:", "mobile", "hotline", "reach", "telephone"],
    description: "Matches phone numbers, dials, calls, or hotlines"
  },
  {
    category: "Communication",
    icon: "Mail",
    keywords: ["email", "mail", "inbox", "send", "newsletter", "message", "@", "letter"],
    description: "Matches email addresses, inboxes, newsletters, or mail"
  },
  {
    category: "Communication",
    icon: "MessageSquare",
    keywords: ["chat", "comment", "discuss", "speech", "talk", "feedback", "forum", "ask", "q&a", "inquire"],
    description: "Matches conversations, discussion items, or user questions"
  },
  {
    category: "Location & Venues",
    icon: "MapPin",
    keywords: ["address", "location", "map", "street", "st", "rd", "ave", "highway", "plaza", "suite", "pin", "hq", "branch", "office", "store", "city", "country", "venue"],
    description: "Matches business offices, branches, or street locations"
  },
  {
    category: "Web & Digital",
    icon: "Globe",
    keywords: ["website", "www.", "http", "globe", "site", "online", "url", "link", "portal", "browser", "internet"],
    description: "Matches domains, website links, or online platforms"
  },
  {
    category: "Time & Scheduling",
    icon: "Clock",
    keywords: [
      "time", "clock", "hour", "schedule", "calendar", "date", "daily", "weekly",
      "mon-", "tue-", "wed-", "thu-", "fri-", "sat-", "sun-", "am", "pm",
      "duration", "timeline", "routine", "annual", "monthly", "year", "month", "day", "midnight"
    ],
    description: "Matches operating hours, dates, recurring routines, or calendars"
  },
  {
    category: "Dental & Health",
    icon: "Heart",
    keywords: ["health", "care", "wellness", "fitness", "healthy", "vitality", "body", "med", "medical", "clinic", "root canal", "veneer", "teeth", "tooth", "dental", "dentist", "oral"],
    description: "Matches clinic operations, health instructions, dentists, and care"
  },
  {
    category: "Luxury & Gems",
    icon: "Gem",
    keywords: ["diamond", "jewel", "gem", "crystal", "ring", "luxury", "crown", "sparkle", "gold", "silver", "premium", "deluxe", "platinum", "high-end", "elite", "glamour"],
    description: "Matches premium items, jewelry, diamonds, upscale aesthetic"
  },
  {
    category: "People & Community",
    icon: "Users",
    keywords: ["people", "team", "person", "human", "staff", "client", "member", "group", "user", "expert", "consultant", "doctor", "specialist", "patients", "audiences", "employees", "community", "partners", "directors", "advisors", "crew", "mentors"],
    description: "Matches teams, customer personas, audiences, or specialists"
  },
  {
    category: "Smile & Emotions",
    icon: "Smile",
    keywords: ["smile", "happy", "laugh", "cheerful", "enjoy", "pleased", "satisfaction", "smiles", "friendly", "delighted"],
    description: "Matches happy vibes, customer satisfaction, or friendly steps"
  },
  {
    category: "Tools & Utilities",
    icon: "Wrench",
    keywords: ["tools", "wrench", "hammer", "build", "construct", "repair", "maintenance", "fix", "screw", "plumbing", "service", "mechanic", "diy", "installation", "setup", "gear"],
    description: "Matches physical building, utility services, or configurations"
  },
  {
    category: "Business & Growth",
    icon: "BarChart3",
    keywords: ["chart", "graph", "growth", "stats", "upward", "marketing", "analytics", "trends", "revenue", "leads", "scale", "performance", "sales", "roi", "data", "metrics", "profit"],
    description: "Matches financial graphs, data analytics, or performance indicators"
  },
  {
    category: "Business & Growth",
    icon: "Briefcase",
    keywords: ["work", "job", "career", "portfolio", "business", "corporate", "agency", "project", "industry", "contract", "enterprise", "resume", "position"],
    description: "Matches professional context, corporate work, or positions"
  },
  {
    category: "Gifts & Rewards",
    icon: "Gift",
    keywords: ["gift", "present", "bonus", "reward", "surprise", "freebie", "offer", "holiday", "package", "goodie", "coupon", "voucher"],
    description: "Matches giveaways, campaign rewards, and incentives"
  },
  {
    category: "Finance & Cost",
    icon: "CircleDollarSign",
    keywords: ["money", "price", "cost", "dollar", "finance", "budget", "saving", "cash", "payment", "$", "eur", "gbp", "billing", "fees", "expensive", "cheap", "charge", "refund"],
    description: "Matches transaction lists, pricing points, fees or saves"
  },
  {
    category: "Trust & Security",
    icon: "ShieldCheck",
    keywords: ["protect", "security", "safe", "shield", "guarded", "encryption", "privacy", "lock", "secure", "verification", "authentic", "trust", "guarantee"],
    description: "Matches trust seals, privacy terms, or data locks"
  },
  {
    category: "Star & Review",
    icon: "Star",
    keywords: ["star", "review", "rating", "testimonial", "recommend", "praise", "fabulous", "highly rated", "feedback", "kudos", "awards"],
    description: "Matches star ratings, reviews, client testimonials"
  },
  {
    category: "Ideas & Creative",
    icon: "Lightbulb",
    keywords: ["idea", "creative", "brain", "insight", "concept", "tip", "hack", "thought", "strategy", "innovation", "clue", "inspiration", "reveal"],
    description: "Matches educational secrets, design brainstorms, or tips"
  },
  {
    category: "Target & Goals",
    icon: "Target",
    keywords: ["target", "goal", "mission", "aim", "strategy", "purpose", "focus", "okr", "benchmark", "destination", "achieve"],
    description: "Matches tactical milestones, goals, or critical focus areas"
  },
  {
    category: "Education & Learning",
    icon: "BookOpen",
    keywords: ["book", "read", "study", "learn", "academy", "education", "course", "lesson", "guide", "handbook", "tutorial", "syllabus", "homework", "training"],
    description: "Matches manuals, standard guidelines, training, or study programs"
  }
];

function suggestLucideIconLocal(text: string): string | undefined {
  const lower = text.toLowerCase().trim();
  
  // High reliability regex checks first
  if (/\+?\d[\d-\s()]{7,}\d/.test(lower)) {
    return "Phone";
  }
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lower) || (lower.includes("@") && lower.includes(".com"))) {
    return "Mail";
  }
  if (/\d+\s+[a-zA-Z0-9\s,.-]+(street|st|road|rd|avenue|ave|drive|dr|highway|hwy|way|court|ct|loop|circle|pl|plaza|blvd|boulevard)/i.test(lower)) {
    return "MapPin";
  }

  // Loop through dictionary mappings
  for (const group of SEMANTIC_ICON_DICTIONARY) {
    if (group.keywords.some(k => lower.includes(k))) {
      return group.icon;
    }
  }

  return undefined;
}

export function parseListLine(
  line: string,
  defaultBoxIconType?: string,
  defaultBoxCustomIcon?: string,
  index: number = 0,
): ParsedLine {
  const trimmed = line.trim();

  const result: ParsedLine = {
    text: trimmed,
    noIcon: false,
    iconType: defaultBoxIconType || "check",
    customIcon: defaultBoxCustomIcon || "",
    containerShape: "none",
    containerStyle: "none",
    index,
  };

  if (trimmed.startsWith("[")) {
    const closingIdx = trimmed.indexOf("]");
    if (closingIdx !== -1) {
      const configStr = trimmed.slice(1, closingIdx).trim();
      const restText = trimmed.slice(closingIdx + 1).trim();
      result.text = restText;

      if (configStr === "none") {
        result.noIcon = true;
        return result;
      }

      const parts = configStr.split(/[,|]/);
      parts.forEach((part) => {
        const p = part.trim();
        if (p.includes(":")) {
          const colonIdx = p.indexOf(":");
          const key = p.slice(0, colonIdx).trim().toLowerCase();
          const val = p.slice(colonIdx + 1).trim();
          const valLower = val.toLowerCase();

          if (key === "icon") {
            result.iconType = valLower;
          } else if (key === "shape") {
            result.containerShape = valLower as any;
          } else if (key === "style") {
            result.containerStyle = valLower as any;
          } else if (key === "color") {
            result.iconColor = val;
          } else if (key === "bg") {
            result.backgroundColor = val;
          } else if (key === "custom") {
            result.customIcon = val;
            result.iconType = "custom";
          }
        } else {
          const lower = p.toLowerCase();
          if (["circle", "square", "none"].includes(lower)) {
            result.containerShape = lower as any;
          } else if (["outline", "fill", "none"].includes(lower)) {
            result.containerStyle = lower as any;
          } else if (
            [
              "check",
              "star",
              "arrow",
              "plus",
              "sparkles",
              "check-double",
              "award",
              "heart",
              "dot",
              "-",
              ">",
              "•",
              "number",
            ].includes(lower)
          ) {
            result.iconType = lower;
          } else if (
            p.startsWith("#") ||
            lower.startsWith("rgb") ||
            lower.startsWith("hsl")
          ) {
            result.iconColor = p;
          } else {
            result.customIcon = p;
            result.iconType = "custom";
          }
        }
      });
      return result;
    }
  }

  // Fallback check for leading structural bullets/symbols in text
  if (trimmed.startsWith("- ")) {
    result.iconType = "-";
    result.text = trimmed.slice(2).trim();
  } else if (trimmed.startsWith("> ")) {
    result.iconType = ">";
    result.text = trimmed.slice(2).trim();
  } else if (trimmed.startsWith("• ")) {
    result.iconType = "•";
    result.text = trimmed.slice(2).trim();
  } else {
    const numMatch = trimmed.match(/^(\d+)[.)]\s+(.*)$/);
    if (numMatch) {
      result.iconType = "number";
      result.index = parseInt(numMatch[1], 10) - 1;
      result.text = numMatch[2].trim();
    }
  }

  // Auto-detect optimal icon style and type without resetting manually configured elements
  if (result.iconType === "check" && !result.customIcon && !result.noIcon) {
    const localIcon = suggestLucideIconLocal(result.text);
    if (localIcon) {
      result.iconType = "custom";
      result.customIcon = localIcon;
      // Set to modern circle and outline container styles automatically!
      if (["Phone", "Mail", "MapPin", "Globe", "Clock"].includes(localIcon)) {
        result.containerShape = "circle";
        result.containerStyle = "outline";
      }
    }
  }

  return result;
}

function serializeListLine(item: ParsedLine): string {
  if (item.noIcon) {
    return `[none] ${item.text}`;
  }

  const configs: string[] = [];
  if (item.iconType && item.iconType !== "check") {
    configs.push(`icon:${item.iconType}`);
  }
  if (item.containerShape && item.containerShape !== "none") {
    configs.push(`shape:${item.containerShape}`);
  }
  if (item.containerStyle && item.containerStyle !== "none") {
    configs.push(`style:${item.containerStyle}`);
  }
  if (item.iconColor) {
    configs.push(`color:${item.iconColor}`);
  }
  if (item.backgroundColor) {
    configs.push(`bg:${item.backgroundColor}`);
  }
  if (item.customIcon) {
    configs.push(`custom:${item.customIcon}`);
  }

  if (configs.length > 0) {
    return `[${configs.join(",")}] ${item.text}`;
  }

  return item.text;
}

function CoreBrandPaletteSelect({
  palette = [],
  selectedColor,
  onSelect,
}: {
  palette?: string[];
  selectedColor: string;
  onSelect: (color: string) => void;
}) {
  if (!palette || palette.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1 pb-1">
      {palette.map((color) => {
        const isSelected =
          selectedColor?.toLowerCase() === color?.toLowerCase();
        return (
          <button
            key={color}
            type="button"
            onClick={() => onSelect(color)}
            style={{ backgroundColor: color }}
            className={`w-5 h-5 rounded-full border shadow-sm transition-all cursor-pointer ${
              isSelected
                ? "ring-2 ring-indigo-500 scale-110 border-white z-10"
                : "border-gray-200 hover:scale-105 hover:border-gray-400"
            }`}
            title={color}
          />
        );
      })}
    </div>
  );
}

function CompactColorPicker({
  label,
  value,
  onChange,
  palette = [],
}: {
  label: string;
  value: string | undefined | null;
  onChange: (color: string | undefined) => void;
  palette?: string[];
}) {
  const displayValue = value || "";
  const isNone =
    !value ||
    value === "none" ||
    value === "transparent" ||
    value === "" ||
    value === "rgba(0,0,0,0)" ||
    value === "rgba(255,255,255,0)";

  return (
    <div className="bg-white border border-gray-150 p-2 rounded-lg space-y-1.5 shadow-sm">
      <div className="flex justify-between items-center">
        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider block">
          {label}
        </span>
        {!isNone && (
          <button
            type="button"
            onClick={() => onChange("transparent")}
            className="text-[9px] text-red-500 hover:text-red-700 font-bold transition-colors cursor-pointer"
          >
            Clear (None)
          </button>
        )}
      </div>

      <div className="flex gap-1.5 items-center">
        <div
          className={`w-6 h-6 rounded-md border border-gray-255 relative shrink-0 shadow-sm overflow-hidden flex items-center justify-center cursor-pointer ${
            isNone ? "bg-gray-50" : ""
          }`}
          style={{
            backgroundColor: isNone ? "transparent" : displayValue,
          }}
          title={isNone ? "No color Selected (Transparent/None)" : displayValue}
        >
          {isNone && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[130%] h-[1.5px] bg-red-500 rotate-45 transform" />
            </div>
          )}
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            value={isNone || !displayValue.startsWith("#") ? "#ffffff" : displayValue}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>

        <input
          type="text"
          className="flex-1 text-[11px] border border-gray-200 rounded p-1 font-mono text-gray-950 bg-white placeholder-gray-400 h-6 leading-none"
          placeholder="None (transparent)"
          value={isNone ? "" : displayValue}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "") {
              onChange("transparent");
            } else {
              onChange(val);
            }
          }}
        />
      </div>

      {palette && palette.length > 0 && (
        <CoreBrandPaletteSelect
          palette={palette}
          selectedColor={displayValue}
          onSelect={(color) => onChange(color)}
        />
      )}
    </div>
  );
}

function CompactNumberInput({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  unit = "px",
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  unit?: string;
  step?: number | string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 p-1.5 bg-white border border-gray-150 rounded-lg shadow-sm h-8">
      <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider pl-1 select-none font-sans">
        {label}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          className="w-10 text-center text-xs border-0 focus:ring-0 p-0 bg-transparent font-mono font-bold text-indigo-600 border-b border-gray-200 focus:border-indigo-500 h-5"
          value={value ?? 0}
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            if (!isNaN(val)) {
              onChange(val);
            } else {
              onChange(0);
            }
          }}
        />
        <span className="text-[9px] text-gray-400 pr-1 select-none font-mono font-bold">{unit}</span>
      </div>
    </div>
  );
}

interface RenderListIconProps {
  box?: TextBoundingBox;
  iconType?: string;
  customIcon?: string;
  containerShape?: "none" | "circle" | "square";
  containerStyle?: "outline" | "fill" | "none";
  iconColor?: string;
  backgroundColor?: string;
  index?: number;
  textColor?: string;
}

export function RenderListIcon({
  box,
  iconType,
  customIcon,
  containerShape,
  containerStyle,
  iconColor,
  backgroundColor,
  index = 0,
  textColor,
}: RenderListIconProps) {
  const isButton = box ? box.type === "cta" || !!box.renderAsButton : false;
  const marginClass = isButton ? "mt-0 self-center" : "mt-0.5";

  const finalIconType = (box ? box.iconType : iconType) || (isButton ? "arrow" : "check");
  const finalCustomIcon = (box ? box.customIcon : customIcon) || "";
  const finalContainerShape =
    (box ? (box as any).containerShape || "none" : containerShape) || "none";
  const finalContainerStyle =
    (box ? (box as any).containerStyle || "none" : containerStyle) || "none";
  const finalIconColor =
    (box ? (box as any).iconColor || box.color : iconColor) ||
    textColor ||
    "#4b5563";
  const finalBgColor =
    (box ? (box as any).backgroundColor || finalIconColor : backgroundColor) ||
    finalIconColor;

  const iconStyle: React.CSSProperties = {
    width: "0.85em",
    height: "0.85em",
    stroke: finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
    flexShrink: 0,
  };

  const isNumeric = finalIconType === "number";

  const renderInnerIcon = () => {
    if (finalCustomIcon) {
      if (
        finalCustomIcon.startsWith("data:") ||
        finalCustomIcon.startsWith("http") ||
        finalCustomIcon.startsWith("/")
      ) {
        return (
          <img
            src={finalCustomIcon}
            alt="custom icon"
            style={{ width: "0.9em", height: "0.9em" }}
            className={`shrink-0 ${marginClass} object-contain`}
          />
        );
      }
      // Draw dynamic Lucide icons
      const LucideIcon = (Lucide as any)[finalCustomIcon];
      if (LucideIcon) {
        return (
          <LucideIcon
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
          />
        );
      }
      return (
        <span
          style={{
            fontSize: "0.85em",
            color: finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
          }}
          className={`shrink-0 ${marginClass} select-none leading-none`}
        >
          {finalCustomIcon}
        </span>
      );
    }

    if (isNumeric) {
      return (
        <span
          style={{
            fontSize: "0.7em",
            fontWeight: "bold",
            color: finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
            fontFamily: "sans-serif",
          }}
          className={`shrink-0 ${marginClass} select-none leading-none`}
        >
          {index + 1}
        </span>
      );
    }

    switch (finalIconType) {
      case "star":
        return (
          <Star
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
          />
        );
      case "arrow":
        return (
          <ArrowRight
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
          />
        );
      case "plus":
        return (
          <Plus
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
          />
        );
      case "sparkles":
        return (
          <Sparkles
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
          />
        );
      case "-":
        return (
          <span
            style={{
              fontSize: "0.9em",
              fontWeight: "bold",
              color:
                finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
            }}
            className={`shrink-0 ${marginClass} select-none leading-none`}
          >
            —
          </span>
        );
      case ">":
        return (
          <span
            style={{
              fontSize: "0.8em",
              fontWeight: "black",
              color:
                finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
            }}
            className={`shrink-0 ${marginClass} select-none leading-none`}
          >
            ▸
          </span>
        );
      case "•":
        return (
          <span
            style={{
              fontSize: "1.2em",
              fontWeight: "black",
              color:
                finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
              marginTop: "-0.15em",
            }}
            className={`shrink-0 ${marginClass} select-none leading-none`}
          >
            •
          </span>
        );
      case "check-double":
        return (
          <svg
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 5L9.17 12.83 5 8.66" />
            <path d="M19 12.83L14.83 17" />
          </svg>
        );
      case "award":
        return (
          <svg
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="8" r="7" />
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
          </svg>
        );
      case "heart":
        return (
          <svg
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
          </svg>
        );
      case "dot":
        return (
          <svg
            style={{
              ...iconStyle,
              fill: finalContainerStyle === "fill" ? "#ffffff" : finalIconColor,
            }}
            className={`shrink-0 ${marginClass} opacity-80`}
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="5" />
          </svg>
        );
      case "check":
      default:
        if (finalIconType && typeof finalIconType === "string") {
          const capitalized = finalIconType.charAt(0).toUpperCase() + finalIconType.slice(1);
          const LucideIcon = (Lucide as any)[finalIconType] || (Lucide as any)[capitalized];
          if (LucideIcon) {
            return (
              <LucideIcon
                style={iconStyle}
                className={`shrink-0 ${marginClass} opacity-80`}
              />
            );
          }
        }
        return (
          <svg
            style={iconStyle}
            className={`shrink-0 ${marginClass} opacity-80`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        );
    }
  };

  if (finalContainerShape === "none") {
    return (
      <div className="w-[1.25em] h-[1.25em] flex items-center justify-center shrink-0 mt-0.5">
        {renderInnerIcon()}
      </div>
    );
  }

  const containerBaseStyle: React.CSSProperties = {
    width: "1.4em",
    height: "1.4em",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: finalContainerShape === "circle" ? "9999px" : "4px",
    flexShrink: 0,
    marginTop: "0.1em",
  };

  if (finalContainerStyle === "fill") {
    containerBaseStyle.backgroundColor = finalBgColor;
  } else if (finalContainerStyle === "outline") {
    containerBaseStyle.border = `1.5px solid ${finalIconColor}`;
    containerBaseStyle.backgroundColor = "transparent";
  }

  return (
    <div
      style={containerBaseStyle}
      className="shrink-0 flex items-center justify-center"
    >
      {renderInnerIcon()}
    </div>
  );
}

export function AutoFitText({ text, box }: { text: string; box: TextBoundingBox }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [fontScale, setFontScale] = useState(100);

  useEffect(() => {
    if (!containerRef.current || !textRef.current || !text) return;
    const container = containerRef.current;
    const textNode = textRef.current;

    const measure = () => {
      if (!container || container.clientWidth === 0) return;
      textNode.style.fontSize = "100%";
      let currentScale = 100;
      if (box.type === "heading") currentScale = 300;
      else if (box.type === "subheading") currentScale = 200;
      else if (box.type === "body" || box.type === "icon-list") currentScale = 120;
      else currentScale = 150;
      textNode.style.fontSize = `${currentScale}%`;

      const maxLoops = box.type === "heading" ? 60 : 35;
      let loop = 0;
      while (loop < maxLoops && currentScale > 15) {
        const cWidth = container.clientWidth;
        const cHeight = Math.max(container.clientHeight, 12);
        const tWidth = textNode.scrollWidth;
        const tHeight = textNode.scrollHeight;

        if (tWidth > cWidth || tHeight > cHeight) {
          if (currentScale > 150) {
            currentScale -= 10;
          } else if (currentScale > 80) {
            currentScale -= 5;
          } else {
            currentScale -= 3;
          }
          textNode.style.fontSize = `${currentScale}%`;
          loop++;
        } else {
          break;
        }
      }
      setFontScale(currentScale);
    };

    measure();

    let lastWidth = 0;
    let lastHeight = 0;
    let rafId: number;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        if (width !== lastWidth || height !== lastHeight) {
          lastWidth = width;
          lastHeight = height;
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(() => {
            measure();
          });
        }
      }
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [text, box.width, box.height, box.type]);

  const isMultiline = box.type !== "footer" && box.type !== "cta" && !box.renderAsButton;

  const justifyMap: Record<string, string> = {
    center: "justify-center",
    right: "justify-end",
    left: "justify-start",
  };
  const justifyClass =
    box.textAlign && justifyMap[box.textAlign]
      ? justifyMap[box.textAlign]
      : "justify-start";
  const textAlignStr = box.textAlign || "left";
  const isButton = box.type === "cta" || !!box.renderAsButton;
  const alignClass = isButton ? "items-center" : box.type === "heading" ? "items-end" : "items-start";

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative flex ${alignClass} ${justifyClass}`}
    >
      <div
        ref={textRef}
        style={{
          fontSize: `${fontScale}%`,
          whiteSpace: isMultiline ? "pre-wrap" : "nowrap",
          width: isMultiline ? "100%" : "max-content",
          textAlign: textAlignStr,
        }}
      >
        {text}
      </div>
    </div>
  );
}

const aiSuggestEmoji = (text: string): string => {
  const lower = text.toLowerCase();
  if (
    lower.includes("money") ||
    lower.includes("price") ||
    lower.includes("cost") ||
    lower.includes("dollar") ||
    lower.includes("finance") ||
    lower.includes("revenue") ||
    lower.includes("profit")
  )
    return "💰";
  if (
    lower.includes("time") ||
    lower.includes("clock") ||
    lower.includes("hour") ||
    lower.includes("schedule") ||
    lower.includes("calendar") ||
    lower.includes("date")
  )
    return "📅";
  if (
    lower.includes("phone") ||
    lower.includes("call") ||
    lower.includes("contact") ||
    lower.includes("mobile")
  )
    return "📞";
  if (
    lower.includes("email") ||
    lower.includes("mail") ||
    lower.includes("send") ||
    lower.includes("inbox")
  )
    return "✉️";
  if (
    lower.includes("award") ||
    lower.includes("badge") ||
    lower.includes("trophy") ||
    lower.includes("first") ||
    lower.includes("winner") ||
    lower.includes("champ")
  )
    return "🏆";
  if (
    lower.includes("heart") ||
    lower.includes("love") ||
    lower.includes("health") ||
    lower.includes("care") ||
    lower.includes("wellness") ||
    lower.includes("heal") ||
    lower.includes("clinic")
  )
    return "❤️";
  if (
    lower.includes("star") ||
    lower.includes("premium") ||
    lower.includes("gold") ||
    lower.includes("quality") ||
    lower.includes("shine")
  )
    return "⭐";
  if (
    lower.includes("light") ||
    lower.includes("idea") ||
    lower.includes("tip") ||
    lower.includes("learn") ||
    lower.includes("think") ||
    lower.includes("smart") ||
    lower.includes("creative")
  )
    return "💡";
  if (
    lower.includes("warn") ||
    lower.includes("alert") ||
    lower.includes("danger") ||
    lower.includes("stop") ||
    lower.includes("caution")
  )
    return "⚠️";
  if (
    lower.includes("document") ||
    lower.includes("report") ||
    lower.includes("file") ||
    lower.includes("text") ||
    lower.includes("page") ||
    lower.includes("write")
  )
    return "📄";
  if (
    lower.includes("growth") ||
    lower.includes("chart") ||
    lower.includes("metrics") ||
    lower.includes("increase") ||
    lower.includes("analytics") ||
    lower.includes("sale")
  )
    return "📈";
  if (
    lower.includes("people") ||
    lower.includes("user") ||
    lower.includes("client") ||
    lower.includes("team") ||
    lower.includes("member") ||
    lower.includes("group") ||
    lower.includes("customer")
  )
    return "👥";
  if (
    lower.includes("smile") ||
    lower.includes("happy") ||
    lower.includes("person") ||
    lower.includes("doctor") ||
    lower.includes("friend")
  )
    return "😊";
  if (
    lower.includes("dentist") ||
    lower.includes("tooth") ||
    lower.includes("dental") ||
    lower.includes("oral") ||
    lower.includes("teeth")
  )
    return "🦷";
  if (
    lower.includes("check") ||
    lower.includes("done") ||
    lower.includes("yes") ||
    lower.includes("success") ||
    lower.includes("approve") ||
    lower.includes("confirm")
  )
    return "✔️";
  if (
    lower.includes("pin") ||
    lower.includes("map") ||
    lower.includes("address") ||
    lower.includes("location") ||
    lower.includes("site")
  )
    return "📍";
  if (
    lower.includes("chat") ||
    lower.includes("bubble") ||
    lower.includes("say") ||
    lower.includes("comment") ||
    lower.includes("discuss")
  )
    return "💬";
  if (
    lower.includes("lock") ||
    lower.includes("secure") ||
    lower.includes("safe") ||
    lower.includes("shield") ||
    lower.includes("protect")
  )
    return "🛡️";
  if (
    lower.includes("target") ||
    lower.includes("goal") ||
    lower.includes("aim") ||
    lower.includes("hit")
  )
    return "🎯";
  if (
    lower.includes("rocket") ||
    lower.includes("launch") ||
    lower.includes("fast") ||
    lower.includes("speed")
  )
    return "🚀";
  if (
    lower.includes("fire") ||
    lower.includes("hot") ||
    lower.includes("trend")
  )
    return "🔥";
  if (
    lower.includes("bell") ||
    lower.includes("notify") ||
    lower.includes("alert") ||
    lower.includes("ring")
  )
    return "🔔";
  if (
    lower.includes("wrench") ||
    lower.includes("tool") ||
    lower.includes("fix") ||
    lower.includes("setting")
  )
    return "🔧";
  return "✨";
};

interface ListItemsEditorProps {
  value: string;
  onChange: (newValue: string) => void;
  defaultIconType?: string;
  defaultCustomIcon?: string;
}

export function ListItemsEditor({
  value,
  onChange,
  defaultIconType = "check",
  defaultCustomIcon = "",
}: ListItemsEditorProps) {
  const lines = useMemo(() => {
    return (value || "").split("\n").filter(Boolean);
  }, [value]);

  const parsedItems = useMemo(() => {
    return lines.map((line, idx) =>
      parseListLine(line, defaultIconType, defaultCustomIcon, idx),
    );
  }, [lines, defaultIconType, defaultCustomIcon]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [suggestingIndex, setSuggestingIndex] = useState<number | null>(null);
  const [isDictOpen, setIsDictOpen] = useState(false);
  const [dictSearch, setDictSearch] = useState("");

  const updateItems = (newItems: ParsedLine[]) => {
    const serialized = newItems
      .map((item) => serializeListLine(item))
      .join("\n");
    onChange(serialized);
  };

  const handleTextChange = (idx: number, newText: string) => {
    const updated = [...parsedItems];
    updated[idx] = { ...updated[idx], text: newText };
    updateItems(updated);
  };

  const handleOptionChange = (idx: number, updates: Partial<ParsedLine>) => {
    const updated = [...parsedItems];
    updated[idx] = { ...updated[idx], ...updates };
    updateItems(updated);
  };

  const handleAddItem = () => {
    const newItem: ParsedLine = {
      text: "New list item",
      noIcon: false,
      iconType: defaultIconType,
      customIcon: defaultCustomIcon,
      containerShape: "none",
      containerStyle: "none",
      index: parsedItems.length,
    };
    const updated = [...parsedItems, newItem];
    updateItems(updated);
    setEditingIndex(updated.length - 1);
  };

  const handleDeleteItem = (idx: number) => {
    const updated = parsedItems
      .filter((_, i) => i !== idx)
      .map((item, i) => ({ ...item, index: i }));
    updateItems(updated);
    if (editingIndex === idx) {
      setEditingIndex(null);
    } else if (editingIndex !== null && editingIndex > idx) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const updated = [...parsedItems];
    const temp = updated[idx];
    updated[idx] = updated[idx - 1];
    updated[idx - 1] = temp;

    const final = updated.map((item, i) => ({ ...item, index: i }));
    updateItems(final);
    if (editingIndex === idx) setEditingIndex(idx - 1);
    else if (editingIndex === idx - 1) setEditingIndex(idx);
  };

  const moveDown = (idx: number) => {
    if (idx === parsedItems.length - 1) return;
    const updated = [...parsedItems];
    const temp = updated[idx];
    updated[idx] = updated[idx + 1];
    updated[idx + 1] = temp;

    const final = updated.map((item, i) => ({ ...item, index: i }));
    updateItems(final);
    if (editingIndex === idx) setEditingIndex(idx + 1);
    else if (editingIndex === idx + 1) setEditingIndex(idx);
  };

  const getEmojiSuggestion = async (idx: number) => {
    const itemText = parsedItems[idx].text;
    if (!itemText || itemText.trim() === "") return;

    setSuggestingIndex(idx);
    try {
      // 1. Check local mapper first to avoid API call completely and keep it free & fast!
      const localIcon = suggestLucideIconLocal(itemText);
      if (localIcon) {
        handleOptionChange(idx, {
          iconType: "custom",
          customIcon: localIcon,
          noIcon: false,
          containerShape: ["Phone", "Mail", "MapPin", "Globe", "Clock"].includes(localIcon) ? "circle" : "none",
          containerStyle: ["Phone", "Mail", "MapPin", "Globe", "Clock"].includes(localIcon) ? "outline" : "none",
        });
        return;
      }

      // 2. Call remote suggestion as fallback
      const iconName = await suggestLucideIcon(itemText);
      handleOptionChange(idx, {
        iconType: "custom",
        customIcon: iconName,
        noIcon: false,
      });
    } catch (err) {
      console.warn("Lucide suggestion failed, falling back to static mapping...", err);
      const emoji = aiSuggestEmoji(itemText);
      handleOptionChange(idx, {
        iconType: "custom",
        customIcon: emoji,
        noIcon: false,
      });
    } finally {
      setSuggestingIndex(null);
    }
  };

  return (
    <div className="space-y-3.5" id="list-items-editor">
      <div className="flex items-center justify-between pb-1">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
          Configure List Items
        </span>
        <button
          type="button"
          onClick={handleAddItem}
          className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded text-xs font-bold transition flex items-center gap-1 cursor-pointer"
        >
          <Plus className="w-3.5 h-3.5" /> Add Bullet
        </button>
      </div>

      {parsedItems.length === 0 ? (
        <div className="text-center p-6 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-xs text-gray-400">
          No items. Click "Add Bullet" to start.
        </div>
      ) : (
        <div className="space-y-2">
          {parsedItems.map((item, idx) => {
            const isAdvancedOpen = editingIndex === idx;
            return (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm flex flex-col"
              >
                <div className="flex items-center gap-2 p-2 bg-gray-50">
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 transition cursor-pointer"
                      title="Move Up"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 15.75l7.5-7.5 7.5 7.5"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(idx)}
                      disabled={idx === parsedItems.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 transition cursor-pointer"
                      title="Move Down"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="shrink-0 scale-90">
                    {!item.noIcon && (
                      <RenderListIcon
                        iconType={item.iconType}
                        customIcon={item.customIcon}
                        containerShape={item.containerShape}
                        containerStyle={item.containerStyle}
                        iconColor={item.iconColor}
                        backgroundColor={item.backgroundColor}
                        index={item.index}
                        textColor="#4f46e5"
                      />
                    )}
                  </div>

                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => handleTextChange(idx, e.target.value)}
                    placeholder="Enter list item text..."
                    className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-900 placeholder:text-gray-400 font-medium focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                  />

                  <button
                    type="button"
                    onClick={() => setEditingIndex(isAdvancedOpen ? null : idx)}
                    className={`p-1.5 rounded transition cursor-pointer ${isAdvancedOpen ? "bg-indigo-50 text-indigo-700" : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"}`}
                    title="Customize item icon style"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteItem(idx)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition cursor-pointer"
                    title="Delete item"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {isAdvancedOpen && (
                  <div className="p-3 border-t border-gray-100 bg-white/50 space-y-3 text-xs">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block">
                          Bullet Symbol / Icon
                        </span>
                        <button
                          type="button"
                          onClick={() => getEmojiSuggestion(idx)}
                          disabled={suggestingIndex !== null}
                          className={`text-[10px] font-bold flex items-center gap-0.5 transition ${suggestingIndex === idx ? "text-indigo-400 select-none cursor-wait" : "text-indigo-600 hover:text-indigo-800 cursor-pointer"}`}
                          title="Auto-detect icon based on text keywords"
                        >
                          {suggestingIndex === idx ? (
                            <div className="w-2.5 h-2.5 border border-indigo-600 border-t-white animate-spin rounded-full shrink-0" />
                          ) : (
                            <Sparkles className="w-2.5 h-2.5 shrink-0" />
                          )}
                          {suggestingIndex === idx ? "Detecting Icon..." : "AI Suggest Icon"}
                        </button>
                      </div>
                      <div className="grid grid-cols-6 gap-1 mb-2">
                        {[
                          { value: "check", label: "Check" },
                          { value: "check-double", label: "Double" },
                          { value: "star", label: "Star" },
                          { value: "arrow", label: "Arrow" },
                          { value: "plus", label: "Plus" },
                          { value: "sparkles", label: "Glow" },
                          { value: "heart", label: "Heart" },
                          { value: "dot", label: "Bullet" },
                          { value: "-", label: "Dash —" },
                          { value: ">", label: "Chevron ▸" },
                          { value: "•", label: "Dot •" },
                          { value: "number", label: "Number 🔢" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              handleOptionChange(idx, {
                                iconType: opt.value,
                                customIcon: "",
                                noIcon: false,
                              })
                            }
                            className={`p-1 py-1.5 text-[10px] rounded text-center truncate border cursor-pointer ${item.iconType === opt.value && !item.customIcon ? "border-indigo-500 bg-indigo-50/50 text-indigo-700 font-bold" : "border-gray-200 hover:bg-gray-50 text-gray-600"}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight shrink-0">
                          Custom Custom/Emoji:
                        </span>
                        <input
                          type="text"
                          placeholder="e.g. 🚀, 📞, http://..."
                          value={item.customIcon || ""}
                          onChange={(e) =>
                            handleOptionChange(idx, {
                              iconType: "custom",
                              customIcon: e.target.value,
                              noIcon: false,
                            })
                          }
                          className="flex-1 text-[11px] border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-950 placeholder:text-gray-400 font-sans focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleOptionChange(idx, { noIcon: !item.noIcon })
                          }
                          className={`px-2 py-1 rounded border text-[10px] font-bold transition cursor-pointer ${item.noIcon ? "bg-red-50 text-red-600 border-red-200" : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"}`}
                        >
                          {item.noIcon ? "Bullets Hidden" : "Hide Bullet"}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pb-1">
                      <div>
                        <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">
                          Shape Container
                        </span>
                        <div className="flex border border-gray-200 rounded overflow-hidden">
                          {[
                            { value: "none", label: "None" },
                            { value: "circle", label: "Circle" },
                            { value: "square", label: "Square" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() =>
                                handleOptionChange(idx, {
                                  containerShape: opt.value as any,
                                })
                              }
                              className={`flex-1 py-1 text-[10px] text-center font-bold font-sans transition-colors cursor-pointer ${item.containerShape === opt.value ? "bg-indigo-600 text-white" : "bg-white hover:bg-gray-50 text-gray-600"}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">
                          Container Style
                        </span>
                        <div className="flex border border-gray-200 rounded overflow-hidden">
                          {[
                            { value: "none", label: "None" },
                            { value: "outline", label: "Outline" },
                            { value: "fill", label: "Fill" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() =>
                                handleOptionChange(idx, {
                                  containerStyle: opt.value as any,
                                })
                              }
                              className={`flex-1 py-1 text-[10px] text-center font-bold font-sans transition-colors cursor-pointer ${item.containerStyle === opt.value ? "bg-indigo-600 text-white" : "bg-white hover:bg-gray-50 text-gray-600"}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                      <div>
                        <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">
                          Icon/Outline Color
                        </span>
                        <div className="flex gap-1.5 items-center">
                          <input
                            type="color"
                            value={item.iconColor || "#4f46e5"}
                            onChange={(e) =>
                              handleOptionChange(idx, {
                                iconColor: e.target.value,
                              })
                            }
                            className="w-5 h-5 border border-gray-200 rounded shrink-0 p-0 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={item.iconColor || ""}
                            onChange={(e) =>
                              handleOptionChange(idx, {
                                iconColor: e.target.value,
                              })
                            }
                            placeholder="e.g. #4f46e5"
                            className="flex-1 text-[10px] border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-950 font-mono"
                          />
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider block mb-1">
                          Fill Background Color
                        </span>
                        <div className="flex gap-1.5 items-center">
                          <input
                            type="color"
                            value={item.backgroundColor || "#4f46e5"}
                            onChange={(e) =>
                              handleOptionChange(idx, {
                                backgroundColor: e.target.value,
                              })
                            }
                            className="w-5 h-5 border border-gray-200 rounded shrink-0 p-0 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={item.backgroundColor || ""}
                            onChange={(e) =>
                              handleOptionChange(idx, {
                                backgroundColor: e.target.value,
                              })
                            }
                            placeholder="e.g. #4f46e5"
                            className="flex-1 text-[10px] border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-950 font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dynamic Auto-Icon Dictionary */}
      <div className="border border-indigo-100 rounded-xl bg-indigo-50/20 overflow-hidden mt-3">
        <button
          type="button"
          onClick={() => setIsDictOpen(!isDictOpen)}
          className="w-full px-3 py-2 flex items-center justify-between text-indigo-900 bg-indigo-50/50 hover:bg-indigo-50 font-bold text-[11px] uppercase tracking-wider transition-all cursor-pointer"
        >
          <div className="flex items-center gap-1.5">
            <Lucide.Sparkles className="w-3.5 h-3.5 text-indigo-600 animate-pulse" />
            <span>📖 AI Auto-Icon Dictionary</span>
          </div>
          <span className="text-[10px] text-indigo-500 font-sans font-semibold">
            {isDictOpen ? "Collapse [-]" : "Expand mappings [+]"}
          </span>
        </button>
        {isDictOpen && (
          <div className="p-3 bg-white border-t border-indigo-100 space-y-3 max-h-80 overflow-y-auto">
            <div className="text-[11px] text-gray-500 leading-relaxed font-sans">
              When typing bullet points, our semantic parser automatically matches key words and pairs them with modern Lucide icons.
            </div>
            
            {/* Search filter */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search keywords (e.g. smile, dollar, address)..."
                value={dictSearch}
                onChange={(e) => setDictSearch(e.target.value)}
                className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg text-gray-900 bg-gray-50 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-sans"
              />
            </div>

            <div className="space-y-3">
              {Object.entries(
                SEMANTIC_ICON_DICTIONARY.reduce<Record<string, typeof SEMANTIC_ICON_DICTIONARY>>((acc, curr) => {
                  const searchTerm = dictSearch.trim().toLowerCase();
                  if (searchTerm) {
                    const matchesCategory = curr.category.toLowerCase().includes(searchTerm);
                    const matchesIcon = curr.icon.toLowerCase().includes(searchTerm);
                    const matchesKeywords = curr.keywords.some(k => k.toLowerCase().includes(searchTerm));
                    if (!matchesCategory && !matchesIcon && !matchesKeywords) {
                      return acc;
                    }
                  }
                  if (!acc[curr.category]) acc[curr.category] = [];
                  acc[curr.category].push(curr);
                  return acc;
                }, {})
              ).map(([category, mappings]) => (
                <div key={category} className="space-y-1.5">
                  <h4 className="text-[9px] font-black text-indigo-700 tracking-wider uppercase bg-indigo-50 px-1.5 py-0.5 rounded-md w-fit">
                    {category}
                  </h4>
                  <div className="grid gap-1.5">
                    {mappings.map((m, idx) => {
                      const LucideIcon = (Lucide as any)[m.icon];
                      return (
                        <div key={idx} className="flex items-start gap-2 p-1.5 border border-gray-100 rounded-lg text-[11px] bg-gray-50/30 font-sans">
                          <div className="p-1 bg-white border border-gray-200 rounded text-indigo-600 shrink-0 mt-0.5 shadow-sm">
                            {LucideIcon ? <LucideIcon className="w-3.5 h-3.5" /> : null}
                          </div>
                          <div className="flex-1 space-y-0.5">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-gray-800">{m.icon}</span>
                              <span className="text-[9px] font-mono text-gray-400">{m.description}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {m.keywords.map((kw) => (
                                <span key={kw} className="px-1 py-0.2 bg-white border border-gray-200 rounded text-[9.5px] font-mono text-gray-500">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface BrandColorPickerProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  brandColors?: string[];
}

function BrandColorPicker({
  label,
  value,
  onChange,
  brandColors = [],
}: BrandColorPickerProps) {
  const defaultColors = [
    "#4f46e5", // Brand Indigo
    "#0ea5e9", // Sky Blue
    "#10b981", // Emerald
    "#f59e0b", // Amber
    "#ef4444", // Red
    "#111827", // Dark Gray
    "#6b7280", // Cool Gray
    "#f3f4f6", // Light Gray
    "#ffffff", // White
  ];

  const palette =
    brandColors && brandColors.length > 0 ? brandColors : defaultColors;

  return (
    <div className="space-y-1.5 text-gray-900">
      <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block">
        {label}
      </label>

      {/* Quick brand colors palette row */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {palette.map((color, index) => {
          const isSelected =
            value && value.toLowerCase() === color.toLowerCase();
          return (
            <button
              key={`${color}-${index}`}
              type="button"
              className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 shrink-0 ${
                isSelected
                  ? "border-indigo-600 ring-2 ring-indigo-200 shadow-md scale-105"
                  : "border-gray-200"
              }`}
              style={{ backgroundColor: color }}
              title={color}
              onClick={() => onChange(color)}
            />
          );
        })}
      </div>

      {/* Hex input and Native color picker */}
      <div className="flex gap-2">
        <div className="relative w-9 h-9 rounded-lg border border-gray-300 overflow-hidden shrink-0 shadow-sm flex items-center justify-center bg-white">
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            value={
              value && value.startsWith("#") && value.length === 7
                ? value
                : "#4f46e5"
            }
            onChange={(e) => onChange(e.target.value)}
          />
          <div
            className="w-5 h-5 rounded border border-gray-200"
            style={{ backgroundColor: value || "transparent" }}
          />
        </div>
        <input
          type="text"
          className="flex-1 text-xs border border-gray-300 rounded-lg px-2.5 bg-white text-gray-950 font-mono focus:ring-1 focus:ring-indigo-500 font-semibold"
          placeholder="Hex/RGBA color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

export function TemplateBakerLab({
  brand,
  brands,
  onComplete,
  initialBlueprint,
}: {
  brand: BrandProfile | null;
  brands: BrandProfile[];
  onComplete?: () => void;
  initialBlueprint?: any;
}) {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [imageAspectRatio, setImageAspectRatio] = useState<
    "1:1" | "16:9" | "9:16"
  >("1:1");
  const [strippedImage, setStrippedImage] = useState<string | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingShell, setIsGeneratingShell] = useState(false);
  const [isFineTuning, setIsFineTuning] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [activeBoxId, setActiveBoxId] = useState<string | null>(null);
  const [activeRenderedBoxId, setActiveRenderedBoxId] = useState<string | null>(
    null,
  );
  const [expandedPayloadIds, setExpandedPayloadIds] = useState<string[]>([]);
  const [boxes, setBoxes] = useState<TextBoundingBox[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [draggingRendered, setDraggingRendered] = useState<{
    id: string;
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [draggingLogo, setDraggingLogo] = useState<{
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [draggingLogoRendered, setDraggingLogoRendered] = useState<{
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [draggingPlaqueRendered, setDraggingPlaqueRendered] = useState<{
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [snapGuide, setSnapGuide] = useState<{
    x: number | null;
    y: number | null;
  }>({ x: null, y: null });
  const renderedContainerRef = useRef<HTMLDivElement>(null);

  // Carousel Blueprint Engine Integration states
  const [isBlueprintModalOpen, setIsBlueprintModalOpen] = useState(false);
  const [blueprintStylePreset, setBlueprintStylePreset] = useState("BENTO_GRID");
  const [blueprintCustomStyle, setBlueprintCustomStyle] = useState("");
  const [blueprintCategory, setBlueprintCategory] = useState<"Educational" | "Promotional" | "Tension Challenge" | "Technical Anatomy" | "Case Study" | "Generic">("Educational");
  const [blueprintSlideCount, setBlueprintSlideCount] = useState(5);
  const [blueprintDescription, setBlueprintDescription] = useState("");
  const [blueprintNarrative, setBlueprintNarrative] = useState("");
  const [isSavingBlueprint, setIsSavingBlueprint] = useState(false);
  const [blueprintSaveSuccess, setBlueprintSaveSuccess] = useState(false);

  // Background generation configuration strategy and custom prompt
  const [bgGenStrategy, setBgGenStrategy] = useState<"reference" | "prompt">("reference");
  const [bgGenPrompt, setBgGenPrompt] = useState<string>("");

  // Modular Canvas Layout & Underlays states
  const [activePanelTab, setActivePanelTab] = useState<"boxes" | "layout">(
    "boxes",
  );

  const [backgroundType, setBackgroundType] = useState<
    | "image-only"
    | "solid-color"
    | "gradient"
    | "image-with-solid"
    | "image-with-gradient"
  >("image-only");
  const [backgroundColor, setBackgroundColor] = useState<string>("#ffffff");
  const [backgroundGradient, setBackgroundGradient] = useState<string>(
    "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)",
  );

  const [enableContentPlaque, setEnableContentPlaque] =
    useState<boolean>(false);
  const [plaqueStyle, setPlaqueStyle] = useState<
    | "glass"
    | "split-left"
    | "split-right"
    | "educational-tip"
    | "magazine-diagonal"
    | "floating-modern"
    | "bottom-story"
    | "custom"
  >("glass");
  const [plaqueBgColor, setPlaqueBgColor] = useState<string>(
    "rgba(255, 255, 255, 0.95)",
  );
  const [plaqueOpacity, setPlaqueOpacity] = useState<number>(95);
  const [plaqueBorderRadius, setPlaqueBorderRadius] = useState<number>(16);
  const [plaquePadding, setPlaquePadding] = useState<number>(6);
  const [plaqueTop, setPlaqueTop] = useState<number>(15);
  const [plaqueLeft, setPlaqueLeft] = useState<number>(15);
  const [plaqueWidth, setPlaqueWidth] = useState<number>(70);
  const [plaqueHeight, setPlaqueHeight] = useState<number>(70);

  const [enableHeader, setEnableHeader] = useState<boolean>(false);
  const [headerStyle, setHeaderStyle] = useState<
    "minimal" | "bordered" | "glass" | "solid" | "accent-bar"
  >("minimal");
  const [headerText, setHeaderText] = useState<string>("INDUSTRY INSIGHTS");
  const [headerTextColor, setHeaderTextColor] = useState<string>("#4b5563");
  const [headerBgColor, setHeaderBgColor] = useState<string>(
    "rgba(255, 255, 255, 0.8)",
  );
  const [headerHeight, setHeaderHeight] = useState<number>(8);
  const [headerShowLogo, setHeaderShowLogo] = useState<boolean>(false);
  const [headerShowEyebrow, setHeaderShowEyebrow] = useState<boolean>(true);
  const [headerFontSize, setHeaderFontSize] = useState<number>(1.8);

  const [enableFooter, setEnableFooter] = useState<boolean>(false);
  const [footerStyle, setFooterStyle] = useState<
    "minimal" | "bordered" | "glass" | "solid" | "accent-bar"
  >("minimal");
  const [footerText, setFooterText] = useState<string>("Swipe to read more");
  const [footerTextColor, setFooterTextColor] = useState<string>("#9ca3af");
  const [footerFontSize, setFooterFontSize] = useState<number>(1.8);
  const [footerBgColor, setFooterBgColor] = useState<string>(
    "rgba(255, 255, 255, 0.8)",
  );
  const [footerHeight, setFooterHeight] = useState<number>(8);
  const [footerShowLogo, setFooterShowLogo] = useState<boolean>(false);
  const [footerShowPageNum, setFooterShowPageNum] = useState<boolean>(true);
  const [footerShowUrl, setFooterShowUrl] = useState<boolean>(true);

  const [footerUrlAlign, setFooterUrlAlign] = useState<"left" | "right">(
    "left",
  );
  const [footerTextAlign, setFooterTextAlign] = useState<"left" | "right">(
    "right",
  );
  const [footerPageNumAlign, setFooterPageNumAlign] = useState<
    "left" | "right"
  >("right");

  const [footerUrlBgColor, setFooterUrlBgColor] =
    useState<string>("rgba(0,0,0,0)");
  const [footerUrlBorderColor, setFooterUrlBorderColor] =
    useState<string>("rgba(0,0,0,0)");
  const [footerUrlBorderWidth, setFooterUrlBorderWidth] = useState<number>(0);
  const [footerUrlBorderRadius, setFooterUrlBorderRadius] = useState<number>(4);
  const [footerUrlPadding, setFooterUrlPadding] = useState<number>(0);
  const [footerUrlTextColor, setFooterUrlTextColor] = useState<string>("");

  const [footerLegalBgColor, setFooterLegalBgColor] =
    useState<string>("rgba(0,0,0,0)");
  const [footerLegalBorderColor, setFooterLegalBorderColor] =
    useState<string>("rgba(0,0,0,0)");
  const [footerLegalBorderWidth, setFooterLegalBorderWidth] =
    useState<number>(0);
  const [footerLegalBorderRadius, setFooterLegalBorderRadius] =
    useState<number>(4);
  const [footerLegalPadding, setFooterLegalPadding] = useState<number>(0);
  const [footerLegalTextColor, setFooterLegalTextColor] = useState<string>("");

  const [footerPageNumBgColor, setFooterPageNumBgColor] =
    useState<string>("rgba(0,0,0,0.05)");
  const [footerPageNumBorderColor, setFooterPageNumBorderColor] =
    useState<string>("rgba(0,0,0,0)");
  const [footerPageNumBorderWidth, setFooterPageNumBorderWidth] =
    useState<number>(0);
  const [footerPageNumBorderRadius, setFooterPageNumBorderRadius] =
    useState<number>(4);
  const [footerPageNumPadding, setFooterPageNumPadding] = useState<number>(4);
  const [footerPageNumTextColor, setFooterPageNumTextColor] =
    useState<string>("");

  const [logoPosition, setLogoPosition] = useState<
    "none" | "header" | "footer" | "content-top" | "content-bottom" | "floating"
  >("none");
  const [logoAlignment, setLogoAlignment] = useState<
    "left" | "center" | "right"
  >("right");
  const [logoSize, setLogoSize] = useState<number>(15);
  const [logoTop, setLogoTop] = useState<number>(6);
  const [logoLeft, setLogoLeft] = useState<number>(79);

  useEffect(() => {
    if (initialBlueprint && initialBlueprint.layoutTemplate) {
      const t = initialBlueprint.layoutTemplate;
      setStrippedImage(t.backgroundImage || null);
      if (t.backgroundType) setBackgroundType(t.backgroundType);
      if (t.backgroundColor) setBackgroundColor(t.backgroundColor);
      if (t.backgroundGradient) setBackgroundGradient(t.backgroundGradient);
      
      if (t.enableContentPlaque !== undefined) setEnableContentPlaque(t.enableContentPlaque);
      if (t.plaqueStyle) setPlaqueStyle(t.plaqueStyle);
      if (t.plaqueBgColor) setPlaqueBgColor(t.plaqueBgColor);
      if (t.plaqueOpacity !== undefined) setPlaqueOpacity(t.plaqueOpacity);
      if (t.plaqueBorderRadius !== undefined) setPlaqueBorderRadius(t.plaqueBorderRadius);
      if (t.plaquePadding !== undefined) setPlaquePadding(t.plaquePadding);
      if (t.plaqueTop !== undefined) setPlaqueTop(t.plaqueTop);
      if (t.plaqueLeft !== undefined) setPlaqueLeft(t.plaqueLeft);
      if (t.plaqueWidth !== undefined) setPlaqueWidth(t.plaqueWidth);
      if (t.plaqueHeight !== undefined) setPlaqueHeight(t.plaqueHeight);

      if (t.enableHeader !== undefined) setEnableHeader(t.enableHeader);
      if (t.headerStyle) setHeaderStyle(t.headerStyle);
      if (t.headerTextColor) setHeaderTextColor(t.headerTextColor);
      if (t.headerBgColor) setHeaderBgColor(t.headerBgColor);
      if (t.headerHeight !== undefined) setHeaderHeight(t.headerHeight);
      if (t.headerShowLogo !== undefined) setHeaderShowLogo(t.headerShowLogo);
      if (t.headerShowEyebrow !== undefined) setHeaderShowEyebrow(t.headerShowEyebrow);
      if (t.headerFontSize !== undefined) setHeaderFontSize(t.headerFontSize);

      if (t.enableFooter !== undefined) setEnableFooter(t.enableFooter);
      if (t.footerStyle) setFooterStyle(t.footerStyle);
      if (t.footerTextColor) setFooterTextColor(t.footerTextColor);
      if (t.footerBgColor) setFooterBgColor(t.footerBgColor);
      if (t.footerHeight !== undefined) setFooterHeight(t.footerHeight);
      if (t.footerShowLogo !== undefined) setFooterShowLogo(t.footerShowLogo);
      if (t.footerShowPageNum !== undefined) setFooterShowPageNum(t.footerShowPageNum);
      if (t.footerShowUrl !== undefined) setFooterShowUrl(t.footerShowUrl);
      if (t.footerUrlAlign) setFooterUrlAlign(t.footerUrlAlign);
      if (t.footerTextAlign) setFooterTextAlign(t.footerTextAlign);
      if (t.footerPageNumAlign) setFooterPageNumAlign(t.footerPageNumAlign);
      if (t.footerFontSize !== undefined) setFooterFontSize(t.footerFontSize);

      if (t.logoPosition) setLogoPosition(t.logoPosition);
      if (t.logoAlignment) setLogoAlignment(t.logoAlignment);
      if (t.logoSize !== undefined) setLogoSize(t.logoSize);
      if (t.logoTop !== undefined) setLogoTop(t.logoTop);
      if (t.logoLeft !== undefined) setLogoLeft(t.logoLeft);

      if (t.boxes) setBoxes(t.boxes.map((b: any) => ({ ...b, text: b.text === "" ? "DUMMY TEXT" : b.text })));
      
      // Auto-set the active blueprint state based on incoming name/sliders
      if (initialBlueprint.name) {
        if (initialBlueprint.name.includes("CUSTOM -")) {
           setBlueprintCustomStyle("CUSTOM");
           setBlueprintStylePreset("CUSTOM");
        }
      }
      
      if (initialBlueprint.category) setBlueprintCategory(initialBlueprint.category);
      if (initialBlueprint.slideCount) setBlueprintSlideCount(initialBlueprint.slideCount);

      // Force to boxes tab with the items
      setActivePanelTab("boxes");
    }
  }, [initialBlueprint]);

  const handleDeployToBlueprintEngine = async () => {
    if (!strippedImage || boxes.length === 0) {
      alert("Please design a visual layout first.");
      return;
    }

    const styleName = blueprintStylePreset === "CUSTOM" ? blueprintCustomStyle || "CUSTOM_STYLE" : blueprintStylePreset;
    const descText = `A premium, auto-aligned ${styleName.toLowerCase().replace(/_/g, " ")} layout template tailored for ${blueprintCategory.toLowerCase()} content over ${blueprintSlideCount} modular canvas slides.`;
    
    // Slide narrative template auto-layout generator
    let narrative = `--- AUTO-GENERATED VISUAL DIRECTIVES & COORDINATES (ABBREVIATED LAYOUT RULES) ---\n\n`;
    narrative += `Layout Style Archetype: ${styleName}\n`;
    narrative += `Global Canvas Aspect Ratio: ${imageAspectRatio}\n`;
    narrative += `Background Structure: Type of "${backgroundType}" with fill rules (Bg Color: "${backgroundColor}", Gradient: "${backgroundGradient}")\n\n`;

    for (let i = 1; i <= blueprintSlideCount; i++) {
      narrative += `Slide ${i} of ${blueprintSlideCount}:\n`;
      if (i === 1) {
        narrative += `  [Flow Purpose]: Scroll-stopping Hook & Core Statement slide.\n`;
      } else if (i === blueprintSlideCount) {
        narrative += `  [Flow Purpose]: Actionable conversion CTA (Call-to-Action) slide.\n`;
      } else if (i === 2) {
        narrative += `  [Flow Purpose]: Problem context or emotional pivot slide.\n`;
      } else {
        narrative += `  [Flow Purpose]: Step-by-step educational value distribution.\n`;
      }

      // Backdrop underlay systems representation
      if (enableContentPlaque) {
        narrative += `  [Plaque Underlay]: Render solid/glass plaque (Style: "${plaqueStyle}") at bounds {Top: ${plaqueTop}%, Left: ${plaqueLeft}%, Width: ${plaqueWidth}%, Height: ${plaqueHeight}%, Corner Radius: ${plaqueBorderRadius}px}.\n`;
      }
      if (enableHeader) {
        narrative += `  [Header Layout]: Anchor branding bar (Style: "${headerStyle}", Height: ${headerHeight}%) with dummy heading text "[LOREM IPSUM TITLE]".\n`;
      }
      if (enableFooter) {
        narrative += `  [Footer Layout]: Anchor standard indicator (Style: "${footerStyle}", Height: ${footerHeight}%) containing legal disclaimer dummy "[LOREM LEGAL DISCLAIMER]" and page counters aligned "${footerPageNumAlign}".\n`;
      }

      // Bounding text boxes as pure layout tokens
      if (boxes.length > 0) {
        narrative += `  [Visual Grid Blocks]:\n`;
        boxes.forEach((box, bIdx) => {
          let dummyTextLabel = "";
          switch (box.type) {
            case "heading":
              dummyTextLabel = `Heading: "Lorem Ipsum Strategic Heading" (${box.fontSize}px text)`;
              break;
            case "subheading":
              dummyTextLabel = `Subheading: "Lorem Ipsum Accompanying Subtext" (${box.fontSize}px text)`;
              break;
            case "body":
              dummyTextLabel = `Body: "Lorem ipsum dolor sit amet, consectetur adipiscing elit." (${box.fontSize}px text)`;
              break;
            case "icon-list":
              dummyTextLabel = `Icon List: "⚡ Lorem Bullet One\\n⚡ Lorem Bullet Two" (${box.fontSize}px text)`;
              break;
            case "cta":
              dummyTextLabel = `CTA Button: "LOREM ACTION" (${box.fontSize}px text, corner radius ${box.borderRadius || 8}px)`;
              break;
            default:
              dummyTextLabel = `Element: "[LOREM_CONTENT]"`;
          }
          narrative += `    - Block #${bIdx + 1} (${box.type}): Bounds {Top: ${box.top}%, Left: ${box.left}%, Width: ${box.width}%, Height: ${box.height}%} -> ${dummyTextLabel}\n`;
        });
      }
      narrative += `\n`;
    }

    const normalizedName = `${styleName} - ${blueprintCategory} (${blueprintSlideCount} Slides)`;

    // Recursive sanitizer to scrub any undefined properties that Firestore rejects
    const sanitizePayload = (obj: any): any => {
      if (obj === null || obj === undefined) return null;
      if (Array.isArray(obj)) {
        return obj.map(item => sanitizePayload(item));
      }
      if (typeof obj === "object") {
        const cleaned: any = {};
        for (const key of Object.keys(obj)) {
          const val = obj[key];
          if (val !== undefined) {
            cleaned[key] = sanitizePayload(val);
          }
        }
        return cleaned;
      }
      return obj;
    };

    setIsSavingBlueprint(true);
    try {
      const user = auth.currentUser;
      
      const payload = {
        name: normalizedName,
        category: blueprintCategory,
        description: descText,
        slideCount: blueprintSlideCount,
        narrative: narrative,
        active: true,
        createdAt: new Date().toISOString(),
        createdBy: user?.email || "anonymous",
        brandId: brand?.id || null,
        layoutTemplate: {
          backgroundImage: strippedImage,
          backgroundType,
          backgroundColor,
          backgroundGradient,
          enableContentPlaque,
          plaqueStyle,
          plaqueBgColor,
          plaqueOpacity,
          plaqueBorderRadius,
          plaquePadding,
          plaqueTop,
          plaqueLeft,
          plaqueWidth,
          plaqueHeight,
          enableHeader,
          headerStyle,
          headerText: "[LOREM_HEADER]",
          headerTextColor,
          headerBgColor,
          headerHeight,
          headerShowLogo,
          headerShowEyebrow,
          headerFontSize,
          enableFooter,
          footerStyle,
          footerText: "[LOREM_FOOTER]",
          footerTextColor,
          footerBgColor,
          footerHeight,
          footerShowLogo,
          footerShowPageNum,
          footerShowUrl,
          footerUrlAlign,
          footerTextAlign,
          footerPageNumAlign,
          footerFontSize,
          footerUrlBgColor,
          footerUrlBorderColor,
          footerUrlBorderWidth,
          footerUrlBorderRadius,
          footerUrlPadding,
          footerUrlTextColor,
          footerLegalBgColor,
          footerLegalBorderColor,
          footerLegalBorderWidth,
          footerLegalBorderRadius,
          footerLegalPadding,
          footerLegalTextColor,
          footerPageNumBgColor,
          footerPageNumBorderColor,
          footerPageNumBorderWidth,
          footerPageNumBorderRadius,
          footerPageNumPadding,
          footerPageNumTextColor,
          logoPosition,
          logoAlignment,
          logoSize,
          logoTop,
          logoLeft,
          boxes: boxes.map((box) => ({
            ...box,
            text: box.type === "heading" ? "[LOREM_HEADING]"
                : box.type === "subheading" ? "[LOREM_SUBHEADING]"
                : box.type === "body" ? "[LOREM_BODY]"
                : box.type === "cta" ? "[LOREM_CTA]"
                : box.type === "icon-list" ? "⚡ [LOREM_BULLET_ITEM_1]\n⚡ [LOREM_BULLET_ITEM_2]\n⚡ [LOREM_BULLET_ITEM_3]"
                : "",
          })),
        },
      };

      if (initialBlueprint?.id) {
        await updateDoc(doc(db, "carousel_blueprints", initialBlueprint.id), sanitizePayload(payload));
      } else {
        await addDoc(collection(db, "carousel_blueprints"), sanitizePayload(payload));
      }

      setBlueprintSaveSuccess(true);
      setTimeout(() => {
        setBlueprintSaveSuccess(false);
        setIsBlueprintModalOpen(false);
        if (onComplete) onComplete();
      }, 1000);
    } catch (err: any) {
      console.error("Error saving blueprint to firestore:", err);
      alert(`Failed to save: ${err.message || err}`);
    } finally {
      setIsSavingBlueprint(false);
    }
  };

  const resetLayoutOptions = () => {
    setEnableHeader(false);
    setHeaderStyle("minimal");
    setHeaderText("INDUSTRY INSIGHTS");
    setHeaderTextColor("#4b5563");
    setHeaderBgColor("rgba(255, 255, 255, 0.8)");
    setHeaderHeight(8);
    setHeaderShowLogo(false);
    setHeaderShowEyebrow(true);

    setEnableFooter(false);
    setFooterStyle("minimal");
    setFooterText("Swipe to read more");
    setFooterTextColor("#9ca3af");
    setFooterBgColor("rgba(255, 255, 255, 0.8)");
    setFooterHeight(8);
    setFooterShowLogo(false);
    setFooterShowPageNum(true);
    setFooterShowUrl(true);
    setFooterUrlAlign("left");
    setFooterTextAlign("right");
    setFooterPageNumAlign("right");

    setFooterUrlBgColor("rgba(0,0,0,0)");
    setFooterUrlBorderColor("rgba(0,0,0,0)");
    setFooterUrlBorderWidth(0);
    setFooterUrlBorderRadius(4);
    setFooterUrlPadding(0);
    setFooterUrlTextColor("");

    setFooterLegalBgColor("rgba(0,0,0,0)");
    setFooterLegalBorderColor("rgba(0,0,0,0)");
    setFooterLegalBorderWidth(0);
    setFooterLegalBorderRadius(4);
    setFooterLegalPadding(0);
    setFooterLegalTextColor("");

    setFooterPageNumBgColor("rgba(0,0,0,0.05)");
    setFooterPageNumBorderColor("rgba(0,0,0,0)");
    setFooterPageNumBorderWidth(0);
    setFooterPageNumBorderRadius(4);
    setFooterPageNumPadding(4);
    setFooterPageNumTextColor("");

    setLogoPosition("none");
    setLogoAlignment("right");
    setLogoSize(15);
    setLogoTop(6);
    setLogoLeft(79);

    setEnableContentPlaque(false);
    setPlaqueStyle("glass");
    setPlaqueBgColor("rgba(255, 255, 255, 0.95)");
    setPlaqueOpacity(95);
    setPlaqueBorderRadius(16);
    setPlaquePadding(6);
    setPlaqueTop(15);
    setPlaqueLeft(15);
    setPlaqueWidth(70);
    setPlaqueHeight(70);

    setBackgroundType("image-only");
    setBackgroundColor("#ffffff");
    setBackgroundGradient("linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)");
  };

  interface SavedTemplate {
    name: string;
    backgroundImage: string;
    boxes: TextBoundingBox[];

    backgroundType?:
      | "image-only"
      | "solid-color"
      | "gradient"
      | "image-with-solid"
      | "image-with-gradient";
    backgroundColor?: string;
    backgroundGradient?: string;

    enableContentPlaque?: boolean;
    plaqueStyle?:
      | "glass"
      | "split-left"
      | "split-right"
      | "educational-tip"
      | "magazine-diagonal"
      | "floating-modern"
      | "bottom-story"
      | "custom";
    plaqueBgColor?: string;
    plaqueOpacity?: number;
    plaqueBorderRadius?: number;
    plaquePadding?: number;
    plaqueTop?: number;
    plaqueLeft?: number;
    plaqueWidth?: number;
    plaqueHeight?: number;

    enableHeader?: boolean;
    headerStyle?: "minimal" | "bordered" | "glass" | "solid" | "accent-bar";
    headerText?: string;
    headerTextColor?: string;
    headerBgColor?: string;
    headerHeight?: number;
    headerShowLogo?: boolean;
    headerShowEyebrow?: boolean;
    headerFontSize?: number;

    enableFooter?: boolean;
    footerStyle?: "minimal" | "bordered" | "glass" | "solid" | "accent-bar";
    footerText?: string;
    footerTextColor?: string;
    footerBgColor?: string;
    footerHeight?: number;
    footerShowLogo?: boolean;
    footerShowPageNum?: boolean;
    footerShowUrl?: boolean;
    footerUrlAlign?: "left" | "right";
    footerTextAlign?: "left" | "right";
    footerPageNumAlign?: "left" | "right";
    footerFontSize?: number;

    footerUrlBgColor?: string;
    footerUrlBorderColor?: string;
    footerUrlBorderWidth?: number;
    footerUrlBorderRadius?: number;
    footerUrlPadding?: number;
    footerUrlTextColor?: string;

    footerLegalBgColor?: string;
    footerLegalBorderColor?: string;
    footerLegalBorderWidth?: number;
    footerLegalBorderRadius?: number;
    footerLegalPadding?: number;
    footerLegalTextColor?: string;

    footerPageNumBgColor?: string;
    footerPageNumBorderColor?: string;
    footerPageNumBorderWidth?: number;
    footerPageNumBorderRadius?: number;
    footerPageNumPadding?: number;
    footerPageNumTextColor?: string;

    logoPosition?:
      | "none"
      | "header"
      | "footer"
      | "content-top"
      | "content-bottom"
      | "floating";
    logoAlignment?: "left" | "center" | "right";
    logoSize?: number;
    logoTop?: number;
    logoLeft?: number;
  }

  const [savedTemplate, setSavedTemplate] = useState<SavedTemplate | null>(
    () => {
      try {
        const stored = localStorage.getItem("sandbox_template");
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    },
  );

  const [isGeneratingPost, setIsGeneratingPost] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedPostData, setGeneratedPostData] = useState<Record<
    string,
    string
  > | null>(null);
  const [generatedBackgroundImage, setGeneratedBackgroundImage] = useState<
    string | null
  >(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string>(
    brand?.id || "",
  );
  const [sandboxTopic, setSandboxTopic] = useState<string>("");

  const activeBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId) || brand,
    [brands, selectedBrandId, brand],
  );

  const saveTemplate = () => {
    if (!strippedImage || boxes.length === 0) {
      alert("No extracted template to save.");
      return;
    }
    const templateData: SavedTemplate = {
      name: "Draft Template",
      backgroundImage: strippedImage,
      boxes: [...boxes],
      backgroundType,
      backgroundColor,
      backgroundGradient,
      enableContentPlaque,
      plaqueStyle,
      plaqueBgColor,
      plaqueOpacity,
      plaqueBorderRadius,
      plaquePadding,
      plaqueTop,
      plaqueLeft,
      plaqueWidth,
      plaqueHeight,
      enableHeader,
      headerStyle,
      headerText,
      headerTextColor,
      headerBgColor,
      headerHeight,
      headerShowLogo,
      headerShowEyebrow,
      headerFontSize,
      enableFooter,
      footerStyle,
      footerText,
      footerTextColor,
      footerBgColor,
      footerHeight,
      footerShowLogo,
      footerShowPageNum,
      footerShowUrl,
      footerUrlAlign,
      footerTextAlign,
      footerPageNumAlign,
      footerFontSize,
      logoPosition,
      logoAlignment,
      logoSize,
      logoTop,
      logoLeft,
    };
    setSavedTemplate(templateData);
    localStorage.setItem("sandbox_template", JSON.stringify(templateData));
    setGeneratedPostData(null);
  };

  const updateTemplateMeta = (updates: Partial<SavedTemplate>) => {
    if (updates.backgroundType !== undefined)
      setBackgroundType(updates.backgroundType);
    if (updates.backgroundColor !== undefined)
      setBackgroundColor(updates.backgroundColor);
    if (updates.backgroundGradient !== undefined)
      setBackgroundGradient(updates.backgroundGradient);

    if (updates.enableContentPlaque !== undefined)
      setEnableContentPlaque(updates.enableContentPlaque);
    if (updates.plaqueStyle !== undefined) setPlaqueStyle(updates.plaqueStyle);
    if (updates.plaqueBgColor !== undefined)
      setPlaqueBgColor(updates.plaqueBgColor);
    if (updates.plaqueOpacity !== undefined)
      setPlaqueOpacity(updates.plaqueOpacity);
    if (updates.plaqueBorderRadius !== undefined)
      setPlaqueBorderRadius(updates.plaqueBorderRadius);
    if (updates.plaquePadding !== undefined)
      setPlaquePadding(updates.plaquePadding);
    if (updates.plaqueTop !== undefined) setPlaqueTop(updates.plaqueTop);
    if (updates.plaqueLeft !== undefined) setPlaqueLeft(updates.plaqueLeft);
    if (updates.plaqueWidth !== undefined) setPlaqueWidth(updates.plaqueWidth);
    if (updates.plaqueHeight !== undefined)
      setPlaqueHeight(updates.plaqueHeight);

    if (updates.enableHeader !== undefined)
      setEnableHeader(updates.enableHeader);
    if (updates.headerStyle !== undefined) setHeaderStyle(updates.headerStyle);
    if (updates.headerText !== undefined) setHeaderText(updates.headerText);
    if (updates.headerTextColor !== undefined)
      setHeaderTextColor(updates.headerTextColor);
    if (updates.headerBgColor !== undefined)
      setHeaderBgColor(updates.headerBgColor);
    if (updates.headerHeight !== undefined)
      setHeaderHeight(updates.headerHeight);
    if (updates.headerShowLogo !== undefined)
      setHeaderShowLogo(updates.headerShowLogo);
    if (updates.headerShowEyebrow !== undefined)
      setHeaderShowEyebrow(updates.headerShowEyebrow);
    if (updates.headerFontSize !== undefined)
      setHeaderFontSize(updates.headerFontSize);

    if (updates.enableFooter !== undefined)
      setEnableFooter(updates.enableFooter);
    if (updates.footerStyle !== undefined) setFooterStyle(updates.footerStyle);
    if (updates.footerText !== undefined) setFooterText(updates.footerText);
    if (updates.footerTextColor !== undefined)
      setFooterTextColor(updates.footerTextColor);
    if (updates.footerBgColor !== undefined)
      setFooterBgColor(updates.footerBgColor);
    if (updates.footerHeight !== undefined)
      setFooterHeight(updates.footerHeight);
    if (updates.footerShowLogo !== undefined)
      setFooterShowLogo(updates.footerShowLogo);
    if (updates.footerShowPageNum !== undefined)
      setFooterShowPageNum(updates.footerShowPageNum);
    if (updates.footerShowUrl !== undefined)
      setFooterShowUrl(updates.footerShowUrl);
    if (updates.footerUrlAlign !== undefined)
      setFooterUrlAlign(updates.footerUrlAlign);
    if (updates.footerTextAlign !== undefined)
      setFooterTextAlign(updates.footerTextAlign);
    if (updates.footerPageNumAlign !== undefined)
      setFooterPageNumAlign(updates.footerPageNumAlign);
    if (updates.footerFontSize !== undefined)
      setFooterFontSize(updates.footerFontSize);

    if (updates.footerUrlBgColor !== undefined)
      setFooterUrlBgColor(updates.footerUrlBgColor);
    if (updates.footerUrlBorderColor !== undefined)
      setFooterUrlBorderColor(updates.footerUrlBorderColor);
    if (updates.footerUrlBorderWidth !== undefined)
      setFooterUrlBorderWidth(updates.footerUrlBorderWidth);
    if (updates.footerUrlBorderRadius !== undefined)
      setFooterUrlBorderRadius(updates.footerUrlBorderRadius);
    if (updates.footerUrlPadding !== undefined)
      setFooterUrlPadding(updates.footerUrlPadding);
    if (updates.footerUrlTextColor !== undefined)
      setFooterUrlTextColor(updates.footerUrlTextColor);

    if (updates.footerLegalBgColor !== undefined)
      setFooterLegalBgColor(updates.footerLegalBgColor);
    if (updates.footerLegalBorderColor !== undefined)
      setFooterLegalBorderColor(updates.footerLegalBorderColor);
    if (updates.footerLegalBorderWidth !== undefined)
      setFooterLegalBorderWidth(updates.footerLegalBorderWidth);
    if (updates.footerLegalBorderRadius !== undefined)
      setFooterLegalBorderRadius(updates.footerLegalBorderRadius);
    if (updates.footerLegalPadding !== undefined)
      setFooterLegalPadding(updates.footerLegalPadding);
    if (updates.footerLegalTextColor !== undefined)
      setFooterLegalTextColor(updates.footerLegalTextColor);

    if (updates.footerPageNumBgColor !== undefined)
      setFooterPageNumBgColor(updates.footerPageNumBgColor);
    if (updates.footerPageNumBorderColor !== undefined)
      setFooterPageNumBorderColor(updates.footerPageNumBorderColor);
    if (updates.footerPageNumBorderWidth !== undefined)
      setFooterPageNumBorderWidth(updates.footerPageNumBorderWidth);
    if (updates.footerPageNumBorderRadius !== undefined)
      setFooterPageNumBorderRadius(updates.footerPageNumBorderRadius);
    if (updates.footerPageNumPadding !== undefined)
      setFooterPageNumPadding(updates.footerPageNumPadding);
    if (updates.footerPageNumTextColor !== undefined)
      setFooterPageNumTextColor(updates.footerPageNumTextColor);

    if (updates.logoPosition !== undefined)
      setLogoPosition(updates.logoPosition);
    if (updates.logoAlignment !== undefined)
      setLogoAlignment(updates.logoAlignment);
    if (updates.logoSize !== undefined) setLogoSize(updates.logoSize);
    if (updates.logoTop !== undefined) setLogoTop(updates.logoTop);
    if (updates.logoLeft !== undefined) setLogoLeft(updates.logoLeft);

    if (savedTemplate) {
      const updated = { ...savedTemplate, ...updates };
      setSavedTemplate(updated);
      localStorage.setItem("sandbox_template", JSON.stringify(updated));
    }
  };

  useEffect(() => {
    if (savedTemplate) {
      if (savedTemplate.backgroundType !== undefined)
        setBackgroundType(savedTemplate.backgroundType);
      if (savedTemplate.backgroundColor !== undefined)
        setBackgroundColor(savedTemplate.backgroundColor);
      if (savedTemplate.backgroundGradient !== undefined)
        setBackgroundGradient(savedTemplate.backgroundGradient);

      if (savedTemplate.enableContentPlaque !== undefined)
        setEnableContentPlaque(savedTemplate.enableContentPlaque);
      if (savedTemplate.plaqueStyle !== undefined)
        setPlaqueStyle(savedTemplate.plaqueStyle);
      if (savedTemplate.plaqueBgColor !== undefined)
        setPlaqueBgColor(savedTemplate.plaqueBgColor);
      if (savedTemplate.plaqueOpacity !== undefined)
        setPlaqueOpacity(savedTemplate.plaqueOpacity);
      if (savedTemplate.plaqueBorderRadius !== undefined)
        setPlaqueBorderRadius(savedTemplate.plaqueBorderRadius);
      if (savedTemplate.plaquePadding !== undefined)
        setPlaquePadding(savedTemplate.plaquePadding);
      if (savedTemplate.plaqueTop !== undefined)
        setPlaqueTop(savedTemplate.plaqueTop);
      if (savedTemplate.plaqueLeft !== undefined)
        setPlaqueLeft(savedTemplate.plaqueLeft);
      if (savedTemplate.plaqueWidth !== undefined)
        setPlaqueWidth(savedTemplate.plaqueWidth);
      if (savedTemplate.plaqueHeight !== undefined)
        setPlaqueHeight(savedTemplate.plaqueHeight);

      if (savedTemplate.enableHeader !== undefined)
        setEnableHeader(savedTemplate.enableHeader);
      if (savedTemplate.headerStyle !== undefined)
        setHeaderStyle(savedTemplate.headerStyle);
      if (savedTemplate.headerText !== undefined)
        setHeaderText(savedTemplate.headerText);
      if (savedTemplate.headerTextColor !== undefined)
        setHeaderTextColor(savedTemplate.headerTextColor);
      if (savedTemplate.headerBgColor !== undefined)
        setHeaderBgColor(savedTemplate.headerBgColor);
      if (savedTemplate.headerHeight !== undefined)
        setHeaderHeight(savedTemplate.headerHeight);
      if (savedTemplate.headerShowLogo !== undefined)
        setHeaderShowLogo(savedTemplate.headerShowLogo);
      if (savedTemplate.headerShowEyebrow !== undefined)
        setHeaderShowEyebrow(savedTemplate.headerShowEyebrow);
      if (savedTemplate.headerFontSize !== undefined)
        setHeaderFontSize(savedTemplate.headerFontSize);

      if (savedTemplate.enableFooter !== undefined)
        setEnableFooter(savedTemplate.enableFooter);
      if (savedTemplate.footerStyle !== undefined)
        setFooterStyle(savedTemplate.footerStyle);
      if (savedTemplate.footerText !== undefined)
        setFooterText(savedTemplate.footerText);
      if (savedTemplate.footerTextColor !== undefined)
        setFooterTextColor(savedTemplate.footerTextColor);
      if (savedTemplate.footerBgColor !== undefined)
        setFooterBgColor(savedTemplate.footerBgColor);
      if (savedTemplate.footerHeight !== undefined)
        setFooterHeight(savedTemplate.footerHeight);
      if (savedTemplate.footerShowLogo !== undefined)
        setFooterShowLogo(savedTemplate.footerShowLogo);
      if (savedTemplate.footerShowPageNum !== undefined)
        setFooterShowPageNum(savedTemplate.footerShowPageNum);
      if (savedTemplate.footerShowUrl !== undefined)
        setFooterShowUrl(savedTemplate.footerShowUrl);
      if (savedTemplate.footerUrlAlign !== undefined)
        setFooterUrlAlign(savedTemplate.footerUrlAlign);
      if (savedTemplate.footerTextAlign !== undefined)
        setFooterTextAlign(savedTemplate.footerTextAlign);
      if (savedTemplate.footerPageNumAlign !== undefined)
        setFooterPageNumAlign(savedTemplate.footerPageNumAlign);
      if (savedTemplate.footerFontSize !== undefined)
        setFooterFontSize(savedTemplate.footerFontSize);

      if (savedTemplate.footerUrlBgColor !== undefined)
        setFooterUrlBgColor(savedTemplate.footerUrlBgColor);
      if (savedTemplate.footerUrlBorderColor !== undefined)
        setFooterUrlBorderColor(savedTemplate.footerUrlBorderColor);
      if (savedTemplate.footerUrlBorderWidth !== undefined)
        setFooterUrlBorderWidth(savedTemplate.footerUrlBorderWidth);
      if (savedTemplate.footerUrlBorderRadius !== undefined)
        setFooterUrlBorderRadius(savedTemplate.footerUrlBorderRadius);
      if (savedTemplate.footerUrlPadding !== undefined)
        setFooterUrlPadding(savedTemplate.footerUrlPadding);
      if (savedTemplate.footerUrlTextColor !== undefined)
        setFooterUrlTextColor(savedTemplate.footerUrlTextColor);

      if (savedTemplate.footerLegalBgColor !== undefined)
        setFooterLegalBgColor(savedTemplate.footerLegalBgColor);
      if (savedTemplate.footerLegalBorderColor !== undefined)
        setFooterLegalBorderColor(savedTemplate.footerLegalBorderColor);
      if (savedTemplate.footerLegalBorderWidth !== undefined)
        setFooterLegalBorderWidth(savedTemplate.footerLegalBorderWidth);
      if (savedTemplate.footerLegalBorderRadius !== undefined)
        setFooterLegalBorderRadius(savedTemplate.footerLegalBorderRadius);
      if (savedTemplate.footerLegalPadding !== undefined)
        setFooterLegalPadding(savedTemplate.footerLegalPadding);
      if (savedTemplate.footerLegalTextColor !== undefined)
        setFooterLegalTextColor(savedTemplate.footerLegalTextColor);

      if (savedTemplate.footerPageNumBgColor !== undefined)
        setFooterPageNumBgColor(savedTemplate.footerPageNumBgColor);
      if (savedTemplate.footerPageNumBorderColor !== undefined)
        setFooterPageNumBorderColor(savedTemplate.footerPageNumBorderColor);
      if (savedTemplate.footerPageNumBorderWidth !== undefined)
        setFooterPageNumBorderWidth(savedTemplate.footerPageNumBorderWidth);
      if (savedTemplate.footerPageNumBorderRadius !== undefined)
        setFooterPageNumBorderRadius(savedTemplate.footerPageNumBorderRadius);
      if (savedTemplate.footerPageNumPadding !== undefined)
        setFooterPageNumPadding(savedTemplate.footerPageNumPadding);
      if (savedTemplate.footerPageNumTextColor !== undefined)
        setFooterPageNumTextColor(savedTemplate.footerPageNumTextColor);

      if (savedTemplate.logoPosition !== undefined)
        setLogoPosition(savedTemplate.logoPosition);
      if (savedTemplate.logoAlignment !== undefined)
        setLogoAlignment(savedTemplate.logoAlignment);
      if (savedTemplate.logoSize !== undefined)
        setLogoSize(savedTemplate.logoSize);
      if (savedTemplate.logoTop !== undefined)
        setLogoTop(savedTemplate.logoTop);
      if (savedTemplate.logoLeft !== undefined)
        setLogoLeft(savedTemplate.logoLeft);
    }
  }, [savedTemplate]);

  const updateSavedTemplateBox = (
    id: string,
    updates: Partial<TextBoundingBox>,
  ) => {
    if (!savedTemplate) return;
    const newBoxes = savedTemplate.boxes.map((b) =>
      b.id === id ? { ...b, ...updates } : b,
    );
    const updated = { ...savedTemplate, boxes: newBoxes };
    setSavedTemplate(updated);
    localStorage.setItem("sandbox_template", JSON.stringify(updated));
  };

  const clearSavedTemplate = () => {
    setSavedTemplate(null);
    localStorage.removeItem("sandbox_template");
    setGeneratedPostData(null);
    setGeneratedBackgroundImage(null);
  };

  const fetchAsBase64 = async (urlOrBase64: string): Promise<string> => {
    if (urlOrBase64.startsWith("data:")) {
      return urlOrBase64;
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.9));
        } else {
          resolve(urlOrBase64); // fallback but might fail later
        }
      };
      img.onerror = () => resolve(urlOrBase64);
      img.src = urlOrBase64;
    });
  };

  const generateMockPost = async () => {
    if (!savedTemplate) return;
    if (!activeBrand) {
      alert("Please select a brand.");
      return;
    }

    setIsGeneratingPost(true);
    setGeneratedPostData(null);
    setGeneratedBackgroundImage(null);

    try {
      const user = auth.currentUser;
      const token = await user?.getIdToken();

      const schemaProperties: Record<string, any> = {};
      const requiredFields: string[] = [];

      savedTemplate.boxes.forEach((box) => {
        if (box.type !== "logo") {
          schemaProperties[box.id] = { type: "STRING" };
          requiredFields.push(box.id);
        }
      });

      schemaProperties["imagePrompt"] = {
        type: "STRING",
        description:
          "A detailed prompt for Imagen to generate a 1:1 background image for this post based on the graphic template and copy context.",
      };
      requiredFields.push("imagePrompt");

      schemaProperties["colorOverrides"] = {
        type: "OBJECT",
        description:
          "If your generated imagePrompt results in a background with different brightness than the template in certain areas (e.g., a dark photographic footer instead of a light clean one), you MUST override the text color for affected boxes here (map box id to '#FFFFFF' or '#111827') to ensure readability.",
        properties: savedTemplate.boxes.reduce((acc, box) => {
          if (box.type !== "logo") acc[box.id] = { type: "STRING" };
          return acc;
        }, {}),
      };

      const prompt = `
        You are an expert social media copywriter. Generate content for a specific graphic template for the brand: ${activeBrand.companyName}.
        Brand details: ${activeBrand.brandingDetails}
        Target Audience: ${activeBrand.questionnaire.targetAudience}
        Brand Voice: ${activeBrand.questionnaire.brandVoice}
        ${sandboxTopic ? `\n        CRITICAL USER INSTRUCTION: You MUST make this post specifically about the following topic or request: "${sandboxTopic}"\n` : ''}
        The template requires the following text blocks to be filled:
        ${savedTemplate.boxes
          .filter((b) => b.type !== "logo")
          .map(
            (b) =>
              `- ${b.id} (${b.type}): Original length was ~${Math.max(2, b.text.split(" ").length)} words. Suggest a natural, impactful replacement of SIMILAR OR SHORTER length to avoid layout overflow. Original: "${b.text}"`,
          )
          .join("\n")}
        
        CRITICAL LAYOUT RULES:
        1. For headings, write punchy, short copy to prevent text from wrapping incorrectly. Never leave a single word to wrap onto a new line (orphan).
        2. For fields of type 'icon-list', you MUST separate the list items exclusively with the newline character (\\n). If the post content does NOT require a bulleted list, you MUST leave the 'icon-list' field completely empty (an empty string "").
        3. If the type is 'footer', the original text might just be page numbers (e.g. 01 / 06) or generic fillers. Replace 'footer' text with the brand's website URL (e.g., example.com) or relevant tagline.
      `;

      const response = await fetch("/api/gemini/proxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model: "gemini-2.5-flash",
          contents: [{ parts: [{ text: prompt }] }],
          config: {
            responseMimeType: "application/json",
            responseSchema: {
              type: "OBJECT",
              properties: schemaProperties,
              required: requiredFields,
            },
          },
        }),
      });

      if (!response.ok) throw new Error("Failed to generate content");

      const data = await response.json();
      const resultObj =
        typeof data === "string"
          ? JSON.parse(data)
          : data.candidates?.[0]?.content?.parts?.[0]?.text
            ? JSON.parse(data.candidates[0].content.parts[0].text)
            : data;

      setGeneratedPostData(resultObj);

      if (resultObj.colorOverrides) {
        Object.keys(resultObj.colorOverrides).forEach((boxId) => {
          const color = resultObj.colorOverrides[boxId];
          if (color) {
            updateSavedTemplateBox(boxId, { color });
          }
        });
      }

      if (
        resultObj.imagePrompt &&
        (strippedImage || savedTemplate?.backgroundImage)
      ) {
        setIsGeneratingImage(true);
        try {
          const unmodifiedBaseImage =
            strippedImage || savedTemplate.backgroundImage;
          const baseImage = await fetchAsBase64(unmodifiedBaseImage);
          const base64Data = baseImage.split(",")[1];
          const mimeType =
            baseImage.split(";")[0].split(":")[1] || "image/jpeg";
          const imgRes = await fetch("/api/gemini/proxy", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              model: "gemini-3.1-flash-image-preview",
              type: "premium",
              contents: [
                {
                  role: "user",
                  parts: [
                    {
                      text: `Generate a photorealistic ${imageAspectRatio} background image matching this concept: "${resultObj.imagePrompt}". \n\nCRITICAL LAYOUT MANDATE: The provided reference image is a clean, unfilled background layout shell. You MUST use it as a strict layout and style guide. Keep all background colors, generic solid panels, and structural graphics exactly as they appear. ONLY alter the central photographic/illustrative subject matter to match the concept. \n\nCRITICAL STRIP TEXT INSTRUCTION: Do NOT generate any text, numbers, words, text-bubbles, arrows, icons or UI motifs. Return the graphic completely free of text.`,
                    },
                    { inlineData: { data: base64Data, mimeType } },
                  ],
                },
              ],
              config: {
                imageConfig: {
                  aspectRatio: "1:1",
                  imageSize: "1K",
                },
              },
            }),
          });

          if (!imgRes.ok) {
            const errorData = await imgRes.text();
            throw new Error(errorData);
          }

          const imgData = await imgRes.json();
          if (imgData.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data) {
            setGeneratedBackgroundImage(
              `data:image/jpeg;base64,${imgData.candidates[0].content.parts[0].inlineData.data}`,
            );
          } else if (imgData.generatedImages?.[0]?.image?.imageBytes) {
            setGeneratedBackgroundImage(
              `data:image/jpeg;base64,${imgData.generatedImages[0].image.imageBytes}`,
            );
          } else {
            throw new Error("No image data returned from Gemini");
          }
        } catch (e: any) {
          console.error("Background generation failed:", e);
          alert(`Background Generation Failed: ${e.message}`);
        } finally {
          setIsGeneratingImage(false);
        }
      }
    } catch (error) {
      console.error("Error generating mock post:", error);
      alert("Failed to generate mock post copy.");
    } finally {
      setIsGeneratingPost(false);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    if (draggingLogo) {
      if (draggingLogo.type === "move") {
        let newLeft =
          ((e.clientX - rect.left - draggingLogo.offsetX) / rect.width) * 100;
        let newTop =
          ((e.clientY - rect.top - draggingLogo.offsetY) / rect.height) * 100;
        newLeft = Math.max(0, Math.min(newLeft, 100 - logoSize));
        newTop = Math.max(0, Math.min(newTop, 100 - logoSize));
        setLogoLeft(Number(newLeft.toFixed(1)));
        setLogoTop(Number(newTop.toFixed(1)));
        updateTemplateMeta({
          logoLeft: Number(newLeft.toFixed(1)),
          logoTop: Number(newTop.toFixed(1)),
        });
      } else if (draggingLogo.type === "resize") {
        const currentLeftPx = (logoLeft / 100) * rect.width;
        let newSize =
          ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
        newSize = Math.max(5, Math.min(newSize, 100 - logoLeft, 100 - logoTop));
        setLogoSize(Number(newSize.toFixed(1)));
        updateTemplateMeta({ logoSize: Number(newSize.toFixed(1)) });
      }
      return;
    }

    if (!dragging) return;
    const box = boxes.find((b) => b.id === dragging.id);
    if (!box) return;

    if (dragging.type === "move") {
      let newLeft =
        ((e.clientX - rect.left - dragging.offsetX) / rect.width) * 100;
      let newTop =
        ((e.clientY - rect.top - dragging.offsetY) / rect.height) * 100;

      const SNAP_THRESH = 2; // 2%
      let snapX = null;
      let snapY = null;

      boxes.forEach((other) => {
        if (other.id === box.id) return;
        // Snap left to other left
        if (Math.abs(newLeft - other.left) < SNAP_THRESH) {
          newLeft = other.left;
          snapX = other.left;
        }
        // Snap top to other top
        if (Math.abs(newTop - other.top) < SNAP_THRESH) {
          newTop = other.top;
          snapY = other.top;
        }
      });

      setSnapGuide({ x: snapX, y: snapY });

      newLeft = Math.max(0, Math.min(newLeft, 100 - box.width));
      newTop = Math.max(0, Math.min(newTop, 100 - box.height));
      updateBox(box.id, { left: newLeft, top: newTop });
    } else {
      const currentLeftPx = (box.left / 100) * rect.width;
      const currentTopPx = (box.top / 100) * rect.height;
      let newWidth =
        ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
      let newHeight =
        ((e.clientY - rect.top - currentTopPx) / rect.height) * 100;
      newWidth = Math.max(5, Math.min(newWidth, 100 - box.left));
      newHeight = Math.max(5, Math.min(newHeight, 100 - box.top));
      updateBox(box.id, { width: newWidth, height: newHeight });
    }
  };

  const handlePointerUp = () => {
    setDragging(null);
    setDraggingLogo(null);
    setSnapGuide({ x: null, y: null });
  };

  const handleRenderedPointerMove = (e: React.PointerEvent) => {
    if (!renderedContainerRef.current || !savedTemplate) return;
    const rect = renderedContainerRef.current.getBoundingClientRect();

    if (draggingLogoRendered) {
      if (draggingLogoRendered.type === "move") {
        let newLeft =
          ((e.clientX - rect.left - draggingLogoRendered.offsetX) /
            rect.width) *
          100;
        let newTop =
          ((e.clientY - rect.top - draggingLogoRendered.offsetY) /
            rect.height) *
          100;
        newLeft = Math.max(0, Math.min(newLeft, 100 - logoSize));
        newTop = Math.max(0, Math.min(newTop, 100 - logoSize));
        setLogoLeft(Number(newLeft.toFixed(1)));
        setLogoTop(Number(newTop.toFixed(1)));
        updateTemplateMeta({
          logoLeft: Number(newLeft.toFixed(1)),
          logoTop: Number(newTop.toFixed(1)),
        });
      } else if (draggingLogoRendered.type === "resize") {
        const currentLeftPx = (logoLeft / 100) * rect.width;
        let newSize =
          ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
        newSize = Math.max(5, Math.min(newSize, 100 - logoLeft, 100 - logoTop));
        setLogoSize(Number(newSize.toFixed(1)));
        updateTemplateMeta({ logoSize: Number(newSize.toFixed(1)) });
      }
      return;
    }

    if (draggingPlaqueRendered) {
      if (draggingPlaqueRendered.type === "move") {
        let newLeft =
          ((e.clientX - rect.left - draggingPlaqueRendered.offsetX) /
            rect.width) *
          100;
        let newTop =
          ((e.clientY - rect.top - draggingPlaqueRendered.offsetY) /
            rect.height) *
          100;
        newLeft = Math.max(0, Math.min(newLeft, 100 - plaqueWidth));
        newTop = Math.max(0, Math.min(newTop, 100 - plaqueHeight));
        setPlaqueLeft(Number(newLeft.toFixed(1)));
        setPlaqueTop(Number(newTop.toFixed(1)));
        updateTemplateMeta({
          plaqueLeft: Number(newLeft.toFixed(1)),
          plaqueTop: Number(newTop.toFixed(1)),
        });
      } else if (draggingPlaqueRendered.type === "resize") {
        const currentLeftPx = (plaqueLeft / 100) * rect.width;
        const currentTopPx = (plaqueTop / 100) * rect.height;
        let newWidth =
          ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
        let newHeight =
          ((e.clientY - rect.top - currentTopPx) / rect.height) * 100;
        newWidth = Math.max(10, Math.min(newWidth, 100 - plaqueLeft));
        newHeight = Math.max(10, Math.min(newHeight, 100 - plaqueTop));
        setPlaqueWidth(Number(newWidth.toFixed(1)));
        setPlaqueHeight(Number(newHeight.toFixed(1)));
        updateTemplateMeta({
          plaqueWidth: Number(newWidth.toFixed(1)),
          plaqueHeight: Number(newHeight.toFixed(1)),
        });
      }
      return;
    }

    if (!draggingRendered) return;
    const box = savedTemplate.boxes.find((b) => b.id === draggingRendered.id);
    if (!box) return;

    if (draggingRendered.type === "move") {
      let newLeft =
        ((e.clientX - rect.left - draggingRendered.offsetX) / rect.width) * 100;
      let newTop =
        ((e.clientY - rect.top - draggingRendered.offsetY) / rect.height) * 100;

      const SNAP_THRESH = 2; // 2%
      let snapX = null;
      let snapY = null;

      savedTemplate.boxes.forEach((other) => {
        if (other.id === box.id) return;
        // Snap left to other left
        if (Math.abs(newLeft - other.left) < SNAP_THRESH) {
          newLeft = other.left;
          snapX = other.left;
        }
        // Snap top to other top
        if (Math.abs(newTop - other.top) < SNAP_THRESH) {
          newTop = other.top;
          snapY = other.top;
        }
      });

      setSnapGuide({ x: snapX, y: snapY });

      updateSavedTemplateBox(box.id, { left: newLeft, top: newTop });
    } else {
      const currentLeftPx = (box.left / 100) * rect.width;
      const currentTopPx = (box.top / 100) * rect.height;
      let newWidth =
        ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
      let newHeight =
        ((e.clientY - rect.top - currentTopPx) / rect.height) * 100;
      newWidth = Math.max(5, Math.min(newWidth, 100 - box.left));
      newHeight = Math.max(5, Math.min(newHeight, 100 - box.top));
      updateSavedTemplateBox(box.id, { width: newWidth, height: newHeight });
    }
  };

  const handleRenderedPointerUp = () => {
    setDraggingRendered(null);
    setDraggingLogoRendered(null);
    setDraggingPlaqueRendered(null);
    setSnapGuide({ x: null, y: null });
  };

  useEffect(() => {
    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (dragging || draggingLogo) {
        handlePointerMove(e as any);
      }
      if (draggingRendered || draggingLogoRendered || draggingPlaqueRendered) {
        handleRenderedPointerMove(e as any);
      }
    };

    const handleGlobalPointerUp = () => {
      if (dragging || draggingLogo) {
        handlePointerUp();
      }
      if (draggingRendered || draggingLogoRendered || draggingPlaqueRendered) {
        handleRenderedPointerUp();
      }
    };

    const active = !!(
      dragging ||
      draggingLogo ||
      draggingRendered ||
      draggingLogoRendered ||
      draggingPlaqueRendered
    );

    if (active) {
      window.addEventListener("pointermove", handleGlobalPointerMove, { passive: true });
      window.addEventListener("pointerup", handleGlobalPointerUp);
    }

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);
    };
  }, [
    dragging,
    draggingLogo,
    draggingRendered,
    draggingLogoRendered,
    draggingPlaqueRendered,
  ]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const dataUrl = event.target.result as string;

        // Calculate aspect ratio
        const img = new Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          if (ratio > 1.3) setImageAspectRatio("16:9");
          else if (ratio < 0.7) setImageAspectRatio("9:16");
          else setImageAspectRatio("1:1");

          setOriginalImage(dataUrl);
          setStrippedImage(null);
          setBoxes([]);
          clearSavedTemplate();
          resetLayoutOptions();
        };
        img.src = dataUrl;
      }
    };
    reader.readAsDataURL(file);
  };

  const generateGriddedImage = async (
    base64Str: string,
    currentBoxes: TextBoundingBox[] = [],
  ): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(base64Str);

        ctx.drawImage(img, 0, 0);

        // Draw Grid
        ctx.strokeStyle = "rgba(255, 0, 255, 0.4)"; // Magenta grid
        ctx.lineWidth = Math.max(1, img.width / 1000);
        ctx.fillStyle = "rgba(255, 0, 255, 0.9)";
        const fontSize = Math.max(10, img.width / 70);
        ctx.font = `bold ${fontSize}px sans-serif`;

        for (let i = 1; i < 20; i++) {
          const pct = i * 5;
          const x = (img.width / 100) * pct;
          const y = (img.height / 100) * pct;

          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, img.height);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(img.width, y);
          ctx.stroke();

          if (pct % 10 === 0) {
            ctx.fillText(`${pct}%`, x + 2, fontSize + 2);
            ctx.fillText(`${pct}%`, 2, y - 2);
          } else {
            ctx.fillText(`${pct}`, x + 2, fontSize + 2);
            ctx.fillText(`${pct}`, 2, y - 2);
          }
        }

        // Draw current boxes if provided
        if (currentBoxes.length > 0) {
          currentBoxes.forEach((box) => {
            const bx = (box.left / 100) * img.width;
            const by = (box.top / 100) * img.height;
            const bw = (box.width / 100) * img.width;
            const bh = (box.height / 100) * img.height;

            ctx.strokeStyle = "rgba(0, 100, 255, 0.9)";
            ctx.lineWidth = Math.max(2, img.width / 400);
            ctx.strokeRect(bx, by, bw, bh);

            ctx.fillStyle = "rgba(0, 100, 255, 0.2)";
            ctx.fillRect(bx, by, bw, bh);

            ctx.fillStyle = "rgba(0, 100, 255, 1)";
            ctx.fillText(box.id, bx + 4, by + fontSize + 4);
          });
        }

        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = base64Str;
    });
  };

  const sanitizeBoxes = (boxes: TextBoundingBox[]): TextBoundingBox[] => {
    let newBoxes = [...boxes];

    // Group multiple icon-list items into a single container so they render together
    const listItems = newBoxes.filter((b) => b.type === "icon-list");
    if (listItems.length > 1) {
      const minTop = Math.min(...listItems.map((b) => b.top));
      const maxBottom = Math.max(...listItems.map((b) => b.top + b.height));
      const minLeft = Math.min(...listItems.map((b) => b.left));
      const maxRight = Math.max(...listItems.map((b) => b.left + b.width));

      const combinedText = listItems
        .sort((a, b) => a.top - b.top)
        .map((b) => b.text.trim())
        .join("\n");
      const combinedBox: TextBoundingBox = {
        ...listItems[0],
        id: "grouped-icon-list",
        top: minTop,
        left: minLeft,
        width: Math.max(listItems[0].width, maxRight - minLeft),
        height: Math.max(listItems[0].height, maxBottom - minTop),
        text: combinedText,
      };
      newBoxes = newBoxes.filter((b) => b.type !== "icon-list");
      newBoxes.push(combinedBox);
    }

    // Heuristic fixes for sizing
    return newBoxes.map((box) => {
      let { fontSize, width, height, type } = box;

      // If AI outputs absolute pixel values (like 24px) instead of relative %, force a conversion
      if (fontSize > 15) {
        switch (type) {
          case "eyebrow":
            fontSize = 2.0;
            break;
          case "heading":
            fontSize = 8.5;
            break;
          case "subheading":
            fontSize = 3.5;
            break;
          case "body":
            fontSize = 2.8;
            break;
          case "icon-list":
            fontSize = 2.5;
            break;
          case "cta":
            fontSize = 3.2;
            break;
          case "footer":
            fontSize = 1.6;
            break;
          default:
            fontSize = 2.8;
        }
      }

      // Strict boundary clamps for cqw to prevent enormous or invisible text
      if (type === "heading")
        fontSize = Math.min(Math.max(fontSize, 4.5), 11.0);
      if (type === "body") fontSize = Math.min(Math.max(fontSize, 1.8), 4.5);
      if (type === "eyebrow" || type === "footer")
        fontSize = Math.min(Math.max(fontSize, 1.2), 3.0);
      if (type === "icon-list")
        fontSize = Math.min(Math.max(fontSize, 1.8), 3.5);
      if (type === "cta") fontSize = Math.min(Math.max(fontSize, 1.8), 5.0);

      // Force boxes to be wide enough so text doesn't arbitrarily cut off mid-word
      if (type === "heading") width = Math.max(width, 40);
      if (type === "body") width = Math.max(width, 35);

      return { ...box, fontSize: Number(fontSize.toFixed(1)), width, height };
    });
  };

  const processImage = async () => {
    if (!originalImage) return;
    setIsProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Authentication required");
      const token = await user.getIdToken();

      const griddedImage = await generateGriddedImage(originalImage);
      const base64Data = griddedImage.split(",")[1];
      const mimeType = griddedImage.split(";")[0].split(":")[1];

      const prompt = `
        You are a computer vision layout expert. I have uploaded an image of a graphic template WITH a magenta 20x20 coordinate grid overlaid on top. The grid lines indicate exact percentages (5%, 10%, 15%, etc.) from the top and left edges.

        Please extract all distinct text elements and the logo area as bounding boxes.
        CRITICAL: For the brand's primary logo (e.g., logo marks containing text like "Volterra Dental" or a distinct logomark), extract it as a single box with type "logo". Do not extract its text elements separately. Only extract other elements as structural copy (headings, body, footers, eyebrows).
        
        CRITICAL SPATIAL ACCURACY:
        - Box coordinates must encapsulate the ENTIRE text block. 'top' should perfectly hit the highest ascender.
        - You MUST use fractional percentages (e.g., 12.5, 43.8) to be as precise as possible between the 5% grid lines. Do NOT snap to nearest 5%.
        - Left alignment is critical: carefully identify the exact starting edge of the text using the 5% grid lines. Ensure elements that look left-aligned have the EXACT same 'left' value.
        - Width must stretch to the logical right-margin or column edge of the available layout, NOT tightly hugging the right-most character of the specific words. Ensure horizontal space is maximized for new copy. Add a generous buffer to width and height.
        - Ensure "icon-list" and "body" text elements share IDENTICAL "fontSize" and "fontFamily" estimates if they visually belong to the same hierarchy. This is critical for design consistency.

        Return a JSON array of objects.
        Each object must have:
        - "id": a unique short string
        - "type": one of "heading", "subheading", "body", "eyebrow", "footer", "icon-list", "logo", "cta" (specifically for call-to-action buttons or clickable capsule text links - do not classify them as subheading)
        - "top": number from 0 to 100 (percentage from top edge, up to 1 decimal place)
        - "left": number from 0 to 100 (percentage from left edge, up to 1 decimal place)
        - "width": number from 0 to 100 (percentage from left margin to right edge)
        - "height": number from 0 to 100 (percentage height)
        - "text": the exact text content found in that area
        - "color": estimate the hex color of the text (e.g. "#000000")
        - "fontFamily": estimate font category ("font-serif", "font-sans", or "font-mono")
        - "fontSize": estimate font size as a PERCENTAGE of the total canvas width (e.g., 2.0 for very small text, 3.5 for body text, 8.5 for large headings). Large headings need much larger values than you think. Return a number.
        - "lineHeight": estimate the optical line height multiplier (e.g. 1.05 or 1.2). Default 1.1.
        - "letterSpacing": estimate the css letter-spacing (e.g. "-0.04em", "0em", "0.05em"). Default "-0.02em".
        - "yOffset": evaluate if the text visually starts lower than the strict bounding box due to font ascenders. Provide a small offset in em units (e.g. 0.1, -0.15) or 0.
        - "xOffset": same as yOffset, for horizontal. (e.g. 0 or -0.05)
      `;

      const response = await fetch("/api/gemini/proxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model: "gemini-2.5-flash",
          contents: [
            {
              parts: [
                { inlineData: { data: base64Data, mimeType: mimeType } },
                { text: prompt },
              ],
            },
          ],
          config: {
            responseMimeType: "application/json",
            responseSchema: {
              type: "ARRAY",
              items: {
                type: "OBJECT",
                properties: {
                  id: { type: "STRING" },
                  type: {
                    type: "STRING",
                    enum: [
                      "heading",
                      "subheading",
                      "body",
                      "eyebrow",
                      "footer",
                      "icon-list",
                      "logo",
                      "cta",
                    ],
                  },
                  top: { type: "NUMBER" },
                  left: { type: "NUMBER" },
                  width: { type: "NUMBER" },
                  height: { type: "NUMBER" },
                  text: { type: "STRING" },
                  color: { type: "STRING" },
                  fontFamily: {
                    type: "STRING",
                    enum: ["font-serif", "font-sans", "font-mono"],
                  },
                  fontSize: { type: "NUMBER" },
                  lineHeight: { type: "NUMBER" },
                  letterSpacing: { type: "STRING" },
                  yOffset: { type: "NUMBER" },
                  xOffset: { type: "NUMBER" },
                },
                required: [
                  "id",
                  "type",
                  "top",
                  "left",
                  "width",
                  "height",
                  "text",
                  "color",
                  "fontFamily",
                  "fontSize",
                ],
              },
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to extract template bounding boxes");
      }

      const data = await response.json();

      let parsedBoxes = [];
      if (data.text) {
        parsedBoxes = JSON.parse(data.text);
      } else if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
        parsedBoxes = JSON.parse(data.candidates[0].content.parts[0].text);
      } else {
        parsedBoxes = data;
      }

      setBoxes(sanitizeBoxes(parsedBoxes));

      if (parsedBoxes.length > 0) {
        setActiveBoxId(parsedBoxes[0].id);
      }
    } catch (error) {
      console.error("Error extracting boxes:", error);
      alert(
        "Failed to extract layout. Please try again or create boxes manually.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const autoGenerateBgPrompt = (): string => {
    const boxSummaries = boxes.map(b => {
      const t = b.type;
      const textContent = b.text || "";
      return `${t} (${textContent})`;
    }).filter(s => s).join(", ");

    const aspectDesc = imageAspectRatio === "1:1" ? "square 1:1" : imageAspectRatio === "16:9" ? "landscape 16:9" : "vertical portrait 9:16";
    let desc = `A pure scenic, high-quality modern presentation layout background (${aspectDesc}) with zero text, no writeups, no characters, no letters. `;
    if (backgroundType === "solid-color" || backgroundType === "image-with-solid") {
      desc += `Solid background base with color ${backgroundColor}. `;
    } else if (backgroundType === "gradient" || backgroundType === "image-with-gradient") {
      desc += `Gradient background style corresponding to: ${backgroundGradient}. `;
    } else {
      desc += `Clean elegant minimalistic backdrop design. `;
    }

    if (enableContentPlaque) {
      desc += `Featuring a centered soft glassmorphic ${plaqueStyle} plaque card overlay. `;
    }

    if (enableHeader) {
      desc += `Features a top header panel suited for '${headerText || "insights"}'. `;
    }

    if (enableFooter) {
      desc += `Includes a clean footer area style. `;
    }

    if (boxSummaries) {
      desc += `Structure designed for a beautiful layout sequence containing elements like: ${boxSummaries}. `;
    }

    // Embed strict background assurance instructions to abide by the project's quality rules
    desc += ` STRICT RULES: You MUST guarantee a purely scenic, textless, single-image backdrop that spans cleanly from far-left to far-right without vertical bars, layout panels, borders, vertical split lines, or grid dividers. The backdrop is purely structural layout background, and any textual elements, written words, numbers, alphabets, or artificial borders are forbidden.`;
    return desc;
  };

  const generateShellImage = async () => {
    if (bgGenStrategy === "reference" && !originalImage) {
      alert("Please upload a reference image first, or switch to 'AI Prompt-Based Background' inside the Left-hand 'Canvas Background' tab.");
      return;
    }
    setIsGeneratingShell(true);
    try {
      console.log("Generating layout background with strategy:", bgGenStrategy);
      let shellUrl = "";

      if (bgGenStrategy === "prompt") {
        const activePrompt = bgGenPrompt.trim() || autoGenerateBgPrompt();
        shellUrl = await generateArtwork(
          activePrompt,
          undefined,
          imageAspectRatio,
          undefined,
          undefined,
          undefined,
          undefined, // No inspiration image to guarantee full fresh prompt-based generation
          "Continuous text-free background layout illustration. Zero typography, letters, words or captions. High-end social media backdrop."
        );
      } else {
        const mimeType =
          originalImage!.split(";")[0].split(":")[1] || "image/jpeg";
        const base64Data = originalImage!.split(",")[1];

        const shellPrompt = `STRIP ALL TEXT AND SPECIFIC MEDIA CONTENT. Generate an empty, completely text-free background layout shell template from this brand template image. Remove all readable text, words, letters, labels, checkmarks, bullets, logos, copywriting, and photographic faces/portraits, leaving only the pristine solid background panels, cards, empty decorative outlines/motifs, borders, buttons, and layout placement grids exactly as they are. This is a visual background layout template.`;

        shellUrl = await generateArtwork(
          shellPrompt,
          undefined,
          imageAspectRatio,
          undefined,
          undefined,
          undefined,
          `data:${mimeType};base64,${base64Data}`,
          "PRECISE_IMAGE_REFINEMENT_QUICK_FIX. ERASE ALL TEXT. Erase all readable text, numbers, words, paragraphs, letters, icons, and brand logos from the reference image. Replace the erased text areas entirely with the matching solid background color or continuous texture behind it. The result must be a completely blank, text-free layout shell with zero trace of the original typography or overlay graphics.",
        );
      }
      setStrippedImage(shellUrl || originalImage);
    } catch (err) {
      console.warn("Layout background generation failed:", err);
      alert("Layout background generation failed. You can try again.");
    } finally {
      setIsGeneratingShell(false);
    }
  };

  const handleAIFineTune = async () => {
    if (!originalImage || boxes.length === 0) return;
    setIsFineTuning(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Authentication required");
      const token = await user.getIdToken();

      const griddedImageWithBoxes = await generateGriddedImage(
        originalImage,
        boxes,
      );
      const base64Data = griddedImageWithBoxes.split(",")[1];
      const mimeType = griddedImageWithBoxes.split(";")[0].split(":")[1];

      const prompt = `
        You are a layout alignment and typography expert fixing an extracted HTML/CSS layout.
        Attached is the graphic OVERLAID WITH A MAGENTA 20x20 COORDINATE GRID (5% increments) and your CURRENT BOUNDING BOXES (drawn as transparent blue rectangles).
        The current JSON bounding box state is:
        ${JSON.stringify(boxes, null, 2)}

        Look very closely at the background text compared to the blue rectangles:
        1. FONT SIZES: 'fontSize' is a percentage of the total canvas width. Large headings usually need to be 7.5 - 12.0. Body text usually needs to be 3.0 - 4.5.
        2. VERTICAL POSITIONING: If the blue box is slightly off the true text baseline or top-edge, provide an optical offset using 'yOffset' (in 'em' relative units, e.g. 0.1 or -0.1). Also you can adjust 'top' and 'left' using precise fraction percentages (e.g. 14.5). Do NOT snap to nearest 5%.
        3. TYPOGRAPHY: Provide 'lineHeight' (e.g. 1.05 or 1.2) and 'letterSpacing' (e.g. "-0.04em").
        4. Add a 1-2% buffer to 'width' to ensure long text doesn't arbitrarily wrap earlier than the original text did.
        
        Return the perfectly refined JSON array following exactly the same schema as input. Include the new typographic fields.
      `;

      const response = await fetch("/api/gemini/proxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model: "gemini-2.5-flash",
          contents: [
            {
              parts: [
                { inlineData: { data: base64Data, mimeType: mimeType } },
                { text: prompt },
              ],
            },
          ],
          config: {
            responseMimeType: "application/json",
            responseSchema: {
              type: "ARRAY",
              items: {
                type: "OBJECT",
                properties: {
                  id: { type: "STRING" },
                  type: {
                    type: "STRING",
                    enum: [
                      "heading",
                      "subheading",
                      "body",
                      "eyebrow",
                      "footer",
                      "icon-list",
                      "logo",
                      "cta",
                    ],
                  },
                  top: { type: "NUMBER" },
                  left: { type: "NUMBER" },
                  width: { type: "NUMBER" },
                  height: { type: "NUMBER" },
                  text: { type: "STRING" },
                  color: { type: "STRING" },
                  fontFamily: {
                    type: "STRING",
                    enum: ["font-serif", "font-sans", "font-mono"],
                  },
                  fontSize: { type: "NUMBER" },
                  lineHeight: { type: "NUMBER" },
                  letterSpacing: { type: "STRING" },
                  yOffset: { type: "NUMBER" },
                  xOffset: { type: "NUMBER" },
                },
                required: [
                  "id",
                  "type",
                  "top",
                  "left",
                  "width",
                  "height",
                  "text",
                  "color",
                  "fontFamily",
                  "fontSize",
                ],
              },
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fine-tune layout");
      }

      const data = await response.json();

      let parsedBoxes = [];
      if (data.text) {
        parsedBoxes = JSON.parse(data.text);
      } else if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
        parsedBoxes = JSON.parse(data.candidates[0].content.parts[0].text);
      } else {
        parsedBoxes = data;
      }

      setBoxes(sanitizeBoxes(parsedBoxes));
    } catch (error) {
      console.error("Error fine-tuning boxes:", error);
      alert("Failed to auto-align layout using AI. Please try again.");
    } finally {
      setIsFineTuning(false);
    }
  };

  const addBox = (type: TextBoundingBox["type"] = "body") => {
    let text = "New Text Block";
    let fontSize = 16;
    let height = 20;
    let width = 45;
    let color = "#111827";
    let borderWidth = undefined;
    let backgroundColor = undefined;
    let borderRadius = undefined;
    let iconType = undefined;
    let iconPlacement = undefined;

    if (type === "heading") {
      text = "Care that Looks at the Whole Picture";
      fontSize = 32;
      height = 15;
      width = 50;
    } else if (type === "subheading") {
      text = "Subheading / Label";
      fontSize = 18;
      height = 10;
      width = 40;
    } else if (type === "eyebrow") {
      text = "COMPREHENSIVE CARE";
      fontSize = 11;
      height = 6;
      width = 30;
      color = "#4f46e5";
    } else if (type === "body") {
      text =
        "We combine advanced technology with a personalized approach to protect your oral health.";
      fontSize = 14;
      height = 18;
      width = 45;
    } else if (type === "cta") {
      text = "Book Appointment";
      fontSize = 14;
      height = 8;
      width = 35;
      backgroundColor = "#4f46e5";
      color = "#ffffff";
      borderRadius = 8;
      iconType = "arrow";
      iconPlacement = "after";
    } else if (type === "icon-list") {
      text =
        "Preventive & Restorative Care\nAesthetic & Cosmetic Dentistry\nPatient-Centered Experience";
      fontSize = 14;
      height = 22;
      width = 45;
    } else if (type === "divider") {
      text = "";
      fontSize = 14;
      height = 2;
      width = 40;
      borderWidth = 4;
      color = "#e5e7eb";
    } else if (type === "image") {
      text = "";
      fontSize = 14;
      height = 30;
      width = 30;
      color = "#3b82f6";
    }

    const newBox: TextBoundingBox = {
      id: Math.random().toString(36).substring(2, 9),
      type,
      top: 15,
      left: 15,
      width,
      height,
      text,
      color,
      fontFamily: "font-sans",
      fontSize,
      borderWidth,
      backgroundColor,
      borderRadius,
      iconType,
      iconPlacement,
    };
    setBoxes([...boxes, newBox]);
    setActiveBoxId(newBox.id);
  };

  const removeBox = (id: string) => {
    setBoxes(boxes.filter((b) => b.id !== id));
    if (activeBoxId === id) setActiveBoxId(null);
  };

  const updateBox = (id: string, updates: Partial<TextBoundingBox>) => {
    setBoxes(boxes.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const activeBox = boxes.find((b) => b.id === activeBoxId);

  return (
    <div className="space-y-8">
      <header>
        <h2 className="text-3xl font-bold text-gray-900">Template Baker</h2>
        <p className="text-gray-500 mt-1">
          Transform visual artwork into reusable structural HTML templates.
        </p>
      </header>

      {!originalImage ? (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Upload className="w-10 h-10 text-indigo-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Upload Reference Template
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            Upload an existing post design. Our system will strip the text and
            automatically map out the text placement constraints.
          </p>

          <label className="inline-flex items-center justify-center px-6 py-4 bg-indigo-600 text-white rounded-2xl font-bold cursor-pointer hover:bg-indigo-700 transition">
            <span>Select Image File</span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileUpload}
            />
          </label>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Canvas Section */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <Layout className="w-4 h-4 text-indigo-500" />
                Interactive Canvas
              </div>
              <div className="flex gap-2 items-center">
                {strippedImage && (
                  <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 cursor-pointer mr-2 hover:text-gray-700">
                    <input
                      type="checkbox"
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                    />
                    Show Grid
                  </label>
                )}
                {strippedImage && boxes.length > 0 && (
                  <>
                    <button
                      onClick={saveTemplate}
                      className="px-4 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold flex items-center gap-2 transition cursor-pointer"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Test in Sandbox
                    </button>
                    <button
                      onClick={handleDeployToBlueprintEngine}
                      disabled={isSavingBlueprint || blueprintSaveSuccess}
                      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-2 transition cursor-pointer disabled:opacity-50"
                    >
                      {isSavingBlueprint ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      ) : blueprintSaveSuccess ? (
                        <Lucide.Check className="w-3.5 h-3.5" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                      {isSavingBlueprint ? "Generating..." : blueprintSaveSuccess ? "Generated!" : "Generate Template"}
                    </button>
                  </>
                )}
                <button
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50"
                  onClick={() => {
                    setOriginalImage(null);
                    setStrippedImage(null);
                    setBoxes([]);
                    setShowGrid(false);
                    clearSavedTemplate();
                    resetLayoutOptions();
                  }}
                >
                  Reset
                </button>
                {!strippedImage && boxes.length === 0 && (
                  <button
                    onClick={processImage}
                    disabled={isProcessing}
                    className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isProcessing ? (
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    {isProcessing ? "Extracting..." : "Extract Bounding Boxes"}
                  </button>
                )}
                {(boxes.length > 0 || bgGenStrategy === "prompt") && (
                  <button
                    onClick={generateShellImage}
                    disabled={isGeneratingShell}
                    className="px-4 py-1.5 bg-pink-600 text-white rounded-lg text-xs font-bold hover:bg-pink-700 disabled:opacity-50 flex items-center gap-2 transition-all shadow-lg shadow-pink-200 cursor-pointer"
                  >
                    {isGeneratingShell ? (
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    {isGeneratingShell
                      ? "Generating Background..."
                      : strippedImage
                        ? bgGenStrategy === "prompt"
                          ? "Regenerate Full AI Background"
                          : "Regenerate Blank Shell"
                        : bgGenStrategy === "prompt"
                        ? "Generate Full AI Background"
                        : "Generate Blank Shell"}
                  </button>
                )}
              </div>
            </div>

            <div
              className="p-8 bg-gray-100 flex-1 flex items-center justify-center touch-none"
            >
              <div
                ref={containerRef}
                className="w-full aspect-square relative shadow-xl overflow-hidden group @container"
                style={{
                  background:
                    backgroundType === "solid-color" ||
                    backgroundType === "image-with-solid"
                      ? backgroundColor
                      : backgroundType === "gradient" ||
                          backgroundType === "image-with-gradient"
                        ? backgroundGradient
                        : "#ffffff",
                }}
              >
                {/* Background Layer */}
                {(backgroundType === "image-only" ||
                  backgroundType === "image-with-solid" ||
                  backgroundType === "image-with-gradient") &&
                  (strippedImage ? (
                    <img
                      src={strippedImage}
                      className="w-full h-full object-cover opacity-60 pointer-events-none select-none absolute inset-0 z-0"
                      alt="Stripped Background"
                      draggable={false}
                    />
                  ) : (
                    <img
                      src={originalImage}
                      className={`w-full h-full object-cover pointer-events-none select-none absolute inset-0 z-0 ${boxes.length > 0 ? "opacity-40" : ""}`}
                      alt="Original Art"
                      draggable={false}
                    />
                  ))}

                {/* Content Plaque Backdrop Layer */}
                {enableContentPlaque && (
                  <div
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActivePanelTab("layout");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "layout-plaque-section",
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 50);
                    }}
                    title="Click to edit Content Plaque"
                    className="absolute cursor-pointer hover:ring-2 hover:ring-indigo-600 transition-all pointer-events-auto"
                    style={{
                      left: `${plaqueLeft}%`,
                      top: `${plaqueTop}%`,
                      width: `${plaqueWidth}%`,
                      height: `${plaqueHeight}%`,
                      borderRadius: `${plaqueBorderRadius}px`,
                      backgroundColor: plaqueBgColor,
                      backdropFilter:
                        plaqueStyle === "glass" ||
                        plaqueStyle === "floating-modern"
                          ? "blur(16px)"
                          : undefined,
                      boxShadow:
                        plaqueStyle === "glass" ||
                        plaqueStyle === "floating-modern" ||
                        plaqueStyle === "split-left" ||
                        plaqueStyle === "split-right"
                          ? "0 10px 30px rgba(0,0,0,0.08)"
                          : undefined,
                      border:
                        plaqueStyle === "glass"
                          ? "1px solid rgba(255, 255, 255, 0.4)"
                          : "1px solid rgba(0,0,0,0.05)",
                      opacity: plaqueOpacity / 100,
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Modular Header */}
                {enableHeader && (
                  <div
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActivePanelTab("layout");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "layout-header-section",
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 50);
                    }}
                    title="Click to edit Header"
                    className="absolute left-0 right-0 top-0 flex items-center justify-between px-4 z-10 cursor-pointer hover:ring-2 hover:ring-indigo-600 transition-all pointer-events-auto animate-fade-in"
                    style={{
                      height: `${headerHeight}%`,
                      backgroundColor: headerBgColor,
                      borderBottom:
                        headerStyle === "bordered"
                          ? "1px solid rgba(0,0,0,0.08)"
                          : headerStyle === "accent-bar"
                            ? "2px solid #4f46e5"
                            : "none",
                      backdropFilter:
                        headerStyle === "glass" ? "blur(12px)" : undefined,
                      color: headerTextColor,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    <div className="flex items-center gap-2 h-full">
                      {headerShowLogo && activeBrand?.logoUrl ? (
                        <img
                          src={activeBrand.logoUrl}
                          alt="Logo"
                          className="object-contain"
                          style={{ maxHeight: "60%" }}
                        />
                      ) : (
                        headerShowEyebrow && (
                          <span 
                            style={{ fontSize: `${headerFontSize}cqw` }}
                            className="font-black tracking-widest uppercase opacity-75"
                          >
                            {headerText}
                          </span>
                        )
                      )}
                    </div>
                    <div 
                      style={{ fontSize: `calc(${headerFontSize}cqw * 1.1)` }}
                      className="font-bold font-mono opacity-60"
                    >
                      {activeBrand?.companyName || "PlataPost"}
                    </div>
                  </div>
                )}

                {/* Modular Footer */}
                {enableFooter && (
                  <div
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActivePanelTab("layout");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "layout-footer-section",
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 50);
                    }}
                    title="Click to edit Footer"
                    className="absolute left-0 right-0 bottom-0 flex items-center justify-between px-4 z-10 cursor-pointer hover:ring-2 hover:ring-indigo-600 transition-all pointer-events-auto animate-fade-in"
                    style={{
                      height: `${footerHeight}%`,
                      backgroundColor: footerBgColor,
                      borderTop:
                        footerStyle === "bordered"
                          ? "1px solid rgba(0,0,0,0.08)"
                          : footerStyle === "accent-bar"
                            ? "2px solid #4f46e5"
                            : "none",
                      backdropFilter:
                        footerStyle === "glass" ? "blur(12px)" : undefined,
                      color: footerTextColor,
                      fontFamily: "font-sans",
                      fontSize: `${footerFontSize}cqw`,
                    }}
                  >
                    <div className="h-full flex items-center gap-2">
                      {footerShowUrl && footerUrlAlign === "left" && (
                        <span
                          className="font-semibold transition-all inline-block"
                          style={{
                            backgroundColor: footerUrlBgColor,
                            border: `${footerUrlBorderWidth}px solid ${footerUrlBorderColor}`,
                            borderRadius: `${footerUrlBorderRadius}px`,
                            padding: `${footerUrlPadding}px`,
                            color: footerUrlTextColor || footerTextColor,
                          }}
                        >
                          {activeBrand?.companyName
                            ? activeBrand.companyName
                                .toLowerCase()
                                .replace(/\s+/g, "") + ".com"
                            : "example.com"}
                        </span>
                      )}
                      {!footerShowUrl && footerShowLogo && activeBrand?.logoUrl && footerUrlAlign === "left" && (
                        <img
                          src={activeBrand.logoUrl}
                          alt="Logo"
                          className="object-contain"
                          style={{ maxHeight: "60%" }}
                        />
                      )}
                      {footerText && footerTextAlign === "left" && (
                        <span
                          className="opacity-80 font-medium transition-all inline-block"
                          style={{
                            backgroundColor: footerLegalBgColor,
                            border: `${footerLegalBorderWidth}px solid ${footerLegalBorderColor}`,
                            borderRadius: `${footerLegalBorderRadius}px`,
                            padding: `${footerLegalPadding}px`,
                            color: footerLegalTextColor || footerTextColor,
                          }}
                        > {(footerText.includes('[LOREM') || footerText.includes('FOOTER')) ? (activeBrand?.companyName || '') : footerText} </span>
                      )}
                      {footerShowPageNum && footerPageNumAlign === "left" && (
                        <span
                          className="font-mono font-bold transition-all inline-block"
                          style={{
                            fontSize: "0.9em",
                            backgroundColor: footerPageNumBgColor,
                            border: `${footerPageNumBorderWidth}px solid ${footerPageNumBorderColor}`,
                            borderRadius: `${footerPageNumBorderRadius}px`,
                            padding: `${footerPageNumPadding}px`,
                            color: footerPageNumTextColor || footerTextColor,
                          }}
                        >
                          01 / 01
                        </span>
                      )}
                    </div>
                    <div className="h-full flex items-center gap-2 justify-end">
                      {footerShowUrl && footerUrlAlign === "right" && (
                        <span
                          className="font-semibold transition-all inline-block"
                          style={{
                            backgroundColor: footerUrlBgColor,
                            border: `${footerUrlBorderWidth}px solid ${footerUrlBorderColor}`,
                            borderRadius: `${footerUrlBorderRadius}px`,
                            padding: `${footerUrlPadding}px`,
                            color: footerUrlTextColor || footerTextColor,
                          }}
                        >
                          {activeBrand?.companyName
                            ? activeBrand.companyName
                                .toLowerCase()
                                .replace(/\s+/g, "") + ".com"
                            : "example.com"}
                        </span>
                      )}
                      {!footerShowUrl && footerShowLogo && activeBrand?.logoUrl && footerUrlAlign === "right" && (
                        <img
                          src={activeBrand.logoUrl}
                          alt="Logo"
                          className="object-contain"
                          style={{ maxHeight: "60%" }}
                        />
                      )}
                      {footerText && footerTextAlign === "right" && (
                        <span
                          className="opacity-80 font-medium transition-all inline-block"
                          style={{
                            backgroundColor: footerLegalBgColor,
                            border: `${footerLegalBorderWidth}px solid ${footerLegalBorderColor}`,
                            borderRadius: `${footerLegalBorderRadius}px`,
                            padding: `${footerLegalPadding}px`,
                            color: footerLegalTextColor || footerTextColor,
                          }}
                        > {(footerText.includes('[LOREM') || footerText.includes('FOOTER')) ? (activeBrand?.companyName || '') : footerText} </span>
                      )}
                      {footerShowPageNum && footerPageNumAlign === "right" && (
                        <span
                          className="font-mono font-bold transition-all inline-block"
                          style={{
                            fontSize: "0.9em",
                            backgroundColor: footerPageNumBgColor,
                            border: `${footerPageNumBorderWidth}px solid ${footerPageNumBorderColor}`,
                            borderRadius: `${footerPageNumBorderRadius}px`,
                            padding: `${footerPageNumPadding}px`,
                            color: footerPageNumTextColor || footerTextColor,
                          }}
                        >
                          01 / 01
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Modular Logo */}
                {logoPosition !== "none" &&
                  logoPosition !== "header" &&
                  logoPosition !== "footer" && (
                    <div
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActivePanelTab("layout");
                        setTimeout(() => {
                          const element = document.getElementById(
                            "layout-logo-section",
                          );
                          if (element) {
                            element.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }
                        }, 50);
                        if (logoPosition === "floating") {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDraggingLogo({
                            offsetX: e.clientX - rect.left,
                            offsetY: e.clientY - rect.top,
                            type: "move",
                          });
                        }
                      }}
                      title="Click to edit Brand Logo"
                      className="absolute z-10 cursor-pointer pointer-events-auto hover:ring-2 hover:ring-indigo-600 ring-1 ring-indigo-500/30 transition-all flex h-auto p-0.5 rounded border border-indigo-500/20 min-h-[30px]"
                      style={{
                        left:
                          logoPosition === "floating"
                            ? `${logoLeft}%`
                            : logoAlignment === "left"
                              ? "5%"
                              : logoAlignment === "center"
                                ? "42.5%"
                                : "80%",
                        top:
                          logoPosition === "floating"
                            ? `${logoTop}%`
                            : logoPosition === "content-top"
                              ? "5%"
                              : "85%",
                        width: `${logoSize}%`,
                        height: `${logoSize}%`,
                        justifyContent:
                          logoAlignment === "center"
                            ? "center"
                            : logoAlignment === "right"
                              ? "flex-end"
                              : "flex-start",
                      }}
                    >
                      {logoPosition === "floating" && (
                        <div
                          className="absolute -bottom-2 -right-2 w-5 h-5 bg-indigo-600 rounded-full shadow border-2 border-white cursor-se-resize flex items-center justify-center opacity-100 z-20 pointer-events-auto"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDraggingLogo({
                              offsetX: 0,
                              offsetY: 0,
                              type: "resize",
                            });
                          }}
                        />
                      )}
                      {activeBrand?.logoUrl ? (
                        <img
                          src={activeBrand.logoUrl}
                          alt="Brand Logo"
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <div className="bg-black/10 text-black px-2 py-1 rounded text-[8px] font-black tracking-wider uppercase border border-dashed border-black/20 text-center flex items-center justify-center">
                          {activeBrand?.companyName || "Logo"}
                        </div>
                      )}
                    </div>
                  )}

                {/* Magenta Coordinate Grid Overlay */}
                {showGrid && (
                  <div className="absolute inset-0 pointer-events-none z-10 opacity-70">
                    {Array.from({ length: 19 }).map((_, i) => {
                      const pct = (i + 1) * 5;
                      return (
                        <React.Fragment key={i}>
                          <div
                            className="absolute top-0 bottom-0 border-l border-fuchsia-500 origin-left"
                            style={{
                              left: `${pct}%`,
                              borderWidth: "max(1px, 0.1vw)",
                            }}
                          >
                            <span className="absolute top-2 left-1 text-[9px] font-bold text-fuchsia-600 bg-white/70 px-0.5 rounded leading-none">
                              {pct % 10 === 0 ? `${pct}%` : pct}
                            </span>
                          </div>
                          <div
                            className="absolute left-0 right-0 border-t border-fuchsia-500 origin-top"
                            style={{
                              top: `${pct}%`,
                              borderWidth: "max(1px, 0.1vw)",
                            }}
                          >
                            <span className="absolute left-2 top-0.5 text-[9px] font-bold text-fuchsia-600 bg-white/70 px-0.5 rounded leading-none">
                              {pct % 10 === 0 ? `${pct}%` : pct}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {/* Snap Guides */}
                {snapGuide.x !== null && (
                  <div
                    className="absolute top-0 bottom-0 border-l border-blue-400 border-dashed z-20 pointer-events-none"
                    style={{ left: `${snapGuide.x}%` }}
                  />
                )}
                {snapGuide.y !== null && (
                  <div
                    className="absolute left-0 right-0 border-t border-blue-400 border-dashed z-20 pointer-events-none"
                    style={{ top: `${snapGuide.y}%` }}
                  />
                )}

                {/* Bounding Boxes Layer */}
                {boxes.map((box) => (
                  <div
                    key={box.id}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDragging({
                        id: box.id,
                        offsetX: e.clientX - rect.left,
                        offsetY: e.clientY - rect.top,
                        type: "move",
                      });
                      setActiveBoxId(box.id);
                      setActivePanelTab("boxes");
                    }}
                    className={`absolute border-2 transition-all ${activeBoxId === box.id ? "border-blue-500 bg-blue-500/10 z-30" : "border-gray-400 border-dashed bg-black/5 hover:border-blue-400 z-20"}`}
                    style={{
                      top: `${box.top}%`,
                      left: `${box.left}%`,
                      width: `${box.width}%`,
                      height: `${box.height}%`,
                      boxSizing: "border-box",
                      cursor:
                        dragging?.type === "move" && dragging.id === box.id
                          ? "grabbing"
                          : "grab",
                    }}
                  >
                    {/* Resize Handle */}
                    {activeBoxId === box.id && (
                      <div
                        className="absolute -bottom-2 -right-2 w-6 h-6 bg-blue-500 rounded-full shadow-md border-2 border-white cursor-se-resize flex items-center justify-center hover:scale-110 transition-transform"
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragging({
                            id: box.id,
                            offsetX: 0,
                            offsetY: 0,
                            type: "resize",
                          });
                        }}
                      />
                    )}

                    <div className="absolute -top-6 left-0 bg-blue-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap pointer-events-none">
                      {box.type}
                    </div>
                    <div
                      className={`w-full h-full flex overflow-visible pointer-events-none select-none ${box.fontFamily} ${box.type === "icon-list" ? "flex-col gap-2" : ""}`}
                      style={{
                        color: box.color,
                        fontSize: `${box.fontSize}cqw`,
                        lineHeight: box.lineHeight ?? 1.1,
                        letterSpacing: box.letterSpacing ?? "-0.02em",
                        transform: `translate(${box.xOffset || 0}em, ${box.yOffset || 0}em)`,
                      }}
                    >
                      {box.type === "icon-list" ? (
                        box.text
                          .split("\n")
                          .filter(Boolean)
                          .map((line, i) => {
                            const parsed = parseListLine(
                              line,
                              box.iconType,
                              box.customIcon,
                              i,
                            );
                            return (
                              <div key={i} className="flex items-start gap-2">
                                {!parsed.noIcon && (
                                  <RenderListIcon
                                    iconType={parsed.iconType}
                                    customIcon={parsed.customIcon}
                                    containerShape={parsed.containerShape}
                                    containerStyle={parsed.containerStyle}
                                    iconColor={parsed.iconColor || box.color}
                                    backgroundColor={
                                      parsed.backgroundColor || box.color
                                    }
                                    index={i}
                                    textColor={box.color}
                                  />
                                )}
                                <div className="w-full flex-1 min-w-0">
                                  <AutoFitText
                                    text={parsed.text}
                                    box={{
                                      ...box,
                                      type: "eyebrow",
                                      text: parsed.text,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })
                      ) : box.type === "cta" || box.renderAsButton ? (
                        <div
                          className="w-full h-full flex items-center gap-2.5 transition-all px-3 py-1.5 overflow-hidden"
                          style={{
                            backgroundColor: box.backgroundColor || "transparent",
                            borderRadius: `${box.borderRadius ?? 8}px`,
                            border:
                              box.borderWidth && box.borderColor
                                ? `${box.borderWidth}px solid ${box.borderColor}`
                                : "none",
                            justifyContent:
                              box.textAlign === "center"
                                ? "center"
                                : box.textAlign === "right"
                                  ? "flex-end"
                                  : "flex-start",
                          }}
                        >
                          {box.iconPlacement === "before" && (
                            <RenderListIcon box={box} />
                          )}
                          <div
                            className="min-w-0 flex-1 flex items-center shadow-none animate-none animate-none"
                            style={{
                              height: "100%",
                              justifyContent:
                                box.textAlign === "center"
                                  ? "center"
                                  : box.textAlign === "right"
                                    ? "flex-end"
                                    : "flex-start",
                            }}
                          >
                            <AutoFitText text={box.text} box={box} />
                          </div>
                          {(box.iconPlacement === "after" ||
                            !box.iconPlacement) &&
                            box.iconPlacement !== "none" && (
                              <RenderListIcon box={box} />
                            )}
                        </div>
                      ) : box.type === "divider" ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <div
                            style={{
                              width: "100%",
                              height: `${box.borderWidth || 4}px`,
                              backgroundColor: box.color || "#e5e7eb",
                              borderRadius: "9999px",
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className="w-full h-full flex items-center transition-all overflow-hidden"
                          style={{
                            backgroundColor: box.backgroundColor || undefined,
                            borderRadius: box.backgroundColor || box.borderWidth ? `${box.borderRadius ?? 8}px` : undefined,
                            border:
                              box.borderWidth && box.borderColor
                                ? `${box.borderWidth}px solid ${box.borderColor}`
                                : undefined,
                            padding: box.padding !== undefined ? `${box.padding}px` : box.backgroundColor ? "12px" : undefined,
                            justifyContent:
                              box.textAlign === "center"
                                ? "center"
                                : box.textAlign === "right"
                                  ? "flex-end"
                                  : "flex-start",
                          }}
                        >
                          <div
                            className="min-w-0 flex-1 flex items-center h-full"
                            style={{
                              justifyContent:
                                box.textAlign === "center"
                                  ? "center"
                                  : box.textAlign === "right"
                                    ? "flex-end"
                                    : "flex-start",
                            }}
                          >
                            <AutoFitText text={box.text} box={box} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Configuration Panel */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 flex flex-col h-[700px]">
            {(originalImage && boxes.length > 0) || strippedImage ? (
              <>
                <div className="flex border-b border-gray-100 bg-gray-50/50 rounded-t-3xl overflow-hidden shrink-0">
                  <button
                    onClick={() => setActivePanelTab("boxes")}
                    className={`flex-1 py-3 text-center text-xs font-bold transition-all ${activePanelTab === "boxes" ? "text-indigo-600 border-b-2 border-indigo-600 bg-white" : "text-gray-500 hover:text-gray-700 hover:bg-white/50"}`}
                  >
                    1. Bounding Boxes
                  </button>
                  <button
                    onClick={() => setActivePanelTab("layout")}
                    className={`flex-1 py-3 text-center text-xs font-bold transition-all ${activePanelTab === "layout" ? "text-indigo-600 border-b-2 border-indigo-600 bg-white" : "text-gray-500 hover:text-gray-700 hover:bg-white/50"}`}
                  >
                    2. Modular Layout & Underlays
                  </button>
                </div>

                {activePanelTab === "boxes" ? (
                  <>
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white text-xs shrink-0">
                      <div className="flex items-center gap-1.5 font-bold text-gray-700">
                        <Type className="w-3.5 h-3.5 text-indigo-500" />
                        Map bounding constraints
                      </div>
                      <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-lg border border-gray-200">
                        <span className="text-[10px] font-bold text-gray-500 uppercase px-1">
                          Add:
                        </span>
                        <select
                          className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 font-bold text-indigo-600 cursor-pointer"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              addBox(e.target.value as any);
                              e.target.value = ""; // Reset
                            }
                          }}
                        >
                          <option value="" disabled>
                            Choose...
                          </option>
                          <option value="heading">Heading</option>
                          <option value="subheading">Subheading</option>
                          <option value="eyebrow">Eyebrow</option>
                          <option value="body">Body</option>
                          <option value="cta">CTA Button</option>
                          <option value="image">Image Placeholder</option>
                          <option value="icon-list">List w/ Icons</option>
                          <option value="divider">Divider Line</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                      {/* List of elements */}
                      <div className="flex flex-col gap-2">
                        {boxes.map((box) => (
                          <div key={box.id} className="flex gap-2">
                            <button
                              onClick={() => setActiveBoxId(box.id)}
                              className={`flex-1 flex items-center justify-between p-3 rounded-xl border transition-all ${activeBoxId === box.id ? "border-blue-500 bg-blue-50 text-blue-900 ring-1 ring-blue-500" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded bg-white border border-gray-100 shadow-sm flex items-center justify-center font-bold text-[10px] text-gray-400 font-mono">
                                  {box.type === "heading"
                                    ? "H1"
                                    : box.type === "subheading"
                                      ? "H2"
                                      : box.type === "cta"
                                        ? "CTA"
                                        : box.type === "divider"
                                          ? "—"
                                          : "P"}
                                </div>
                                <div className="text-left">
                                  <div className="text-xs font-bold uppercase tracking-wider opacity-60 mb-0.5">
                                    {box.type}
                                  </div>
                                  <div className="text-sm font-medium truncate max-w-[125px]">
                                    {box.type === "divider"
                                      ? "Divider Line"
                                      : box.text || "Empty box"}
                                  </div>
                                </div>
                              </div>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeBox(box.id);
                              }}
                              className="bg-white hover:bg-red-50 text-gray-400 hover:text-red-500 p-3 rounded-xl border border-gray-200 hover:border-red-200 transition shrink-0"
                              title="Delete Element"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {activeBox ? (
                        <div className="bg-gray-50 p-5 rounded-2xl border border-gray-200 space-y-5">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                              Edit Constraints
                            </h4>
                            <button
                              onClick={() => removeBox(activeBox.id)}
                              className="text-red-500 hover:bg-red-50 p-1.5 rounded transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                Text Type
                              </label>
                              <select
                                className="w-full text-sm border-gray-300 rounded-lg p-2 bg-white"
                                value={activeBox.type}
                                onChange={(e) =>
                                  updateBox(activeBox.id, {
                                    type: e.target.value as any,
                                  })
                                }
                              >
                                <option value="eyebrow">
                                  Eyebrow (Top Label)
                                </option>
                                <option value="heading">Heading block</option>
                                <option value="subheading">
                                  Subheading / Label
                                </option>
                                <option value="body">Body / Paragraphs</option>
                                <option value="cta">
                                  Call to Action (Button)
                                </option>
                                <option value="icon-list">
                                  List with Icons
                                </option>
                                <option value="divider">Divider Line</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                Color HEX
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="color"
                                  className="w-8 h-8 rounded border-0 p-0 shadow-sm"
                                  value={activeBox.color}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      color: e.target.value,
                                    })
                                  }
                                />
                                <input
                                  type="text"
                                  className="w-full text-sm border-gray-300 rounded-lg p-1.5 px-3 bg-white"
                                  value={activeBox.color}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      color: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                Typography
                              </label>
                              <select
                                className="w-full text-sm border-gray-300 rounded-lg p-2 bg-white"
                                value={activeBox.fontFamily}
                                onChange={(e) =>
                                  updateBox(activeBox.id, {
                                    fontFamily: e.target.value,
                                  })
                                }
                              >
                                <option value="font-sans">Sans Serif</option>
                                <option value="font-serif">Serif</option>
                                <option value="font-mono">Monospace</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                Alignment
                              </label>
                              <select
                                className="w-full text-sm border-gray-300 rounded-lg p-2 bg-white"
                                value={activeBox.textAlign || "left"}
                                onChange={(e) =>
                                  updateBox(activeBox.id, {
                                    textAlign: e.target.value as any,
                                  })
                                }
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                Font Size (px)
                              </label>
                              <input
                                type="number"
                                className="w-full text-sm border-gray-300 rounded-lg p-2 bg-white"
                                value={activeBox.fontSize}
                                onChange={(e) =>
                                  updateBox(activeBox.id, {
                                    fontSize: Number(e.target.value),
                                  })
                                }
                              />
                            </div>

                            {activeBox.type === "icon-list" && (
                              <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                  List Icon Type
                                </label>
                                <select
                                  className="w-full text-sm border-gray-300 rounded-lg p-2 bg-white"
                                  value={activeBox.iconType || "check"}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      iconType: e.target.value as any,
                                    })
                                  }
                                >
                                  <option value="check">Check</option>
                                  <option value="star">Star</option>
                                  <option value="arrow">Arrow</option>
                                  <option value="plus">Plus</option>
                                </select>
                              </div>
                            )}

                            {activeBox.type !== "divider" && activeBox.type !== "image" && (
                              <div className="col-span-2 space-y-3 pt-3 border-t border-gray-200">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">
                                  Container backdrop & borders
                                </span>

                                <div className="grid grid-cols-1 gap-2">
                                  <CompactColorPicker
                                    label="Fill Background Color"
                                    value={activeBox.backgroundColor}
                                    onChange={(color) =>
                                      updateBox(activeBox.id, {
                                        backgroundColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <CompactNumberInput
                                    label="Corner Radius"
                                    value={activeBox.borderRadius ?? 8}
                                    min={0}
                                    max={32}
                                    onChange={(val) =>
                                      updateBox(activeBox.id, {
                                        borderRadius: val,
                                      })
                                    }
                                  />
                                  <CompactNumberInput
                                    label="Inner Padding"
                                    value={activeBox.padding ?? 0}
                                    min={0}
                                    max={40}
                                    onChange={(val) =>
                                      updateBox(activeBox.id, {
                                        padding: val,
                                      })
                                    }
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-3 pt-1">
                                  <CompactColorPicker
                                    label="Border Color"
                                    value={activeBox.borderColor}
                                    onChange={(color) =>
                                      updateBox(activeBox.id, {
                                        borderColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <div className="flex flex-col justify-end">
                                    <CompactNumberInput
                                      label="Border Width"
                                      value={activeBox.borderWidth ?? 0}
                                      min={0}
                                      max={8}
                                      onChange={(val) =>
                                        updateBox(activeBox.id, {
                                          borderWidth: val,
                                        })
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="pt-2">
                                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="rounded text-indigo-600 border-gray-300"
                                      checked={!!activeBox.renderAsButton || activeBox.type === "cta"}
                                      onChange={(e) =>
                                        updateBox(activeBox.id, {
                                          renderAsButton: e.target.checked,
                                          ...(e.target.checked ? {
                                            iconPlacement: activeBox.iconPlacement || "after",
                                            iconType: activeBox.iconType || "arrow"
                                          } : {})
                                        })
                                      }
                                    />
                                    <span>Render elements inside as interactive Button / Action link</span>
                                  </label>
                                </div>

                                {(activeBox.type === "cta" || !!activeBox.renderAsButton) && (
                                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                                    <div>
                                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                        Icon Position
                                      </label>
                                      <select
                                        className="w-full text-xs border border-gray-300 rounded-lg p-2 bg-white text-gray-950 font-medium"
                                        value={activeBox.iconPlacement || "after"}
                                        onChange={(e) =>
                                          updateBox(activeBox.id, {
                                            iconPlacement: e.target.value as any,
                                          })
                                        }
                                      >
                                        <option value="none">No Icon</option>
                                        <option value="before">Before Text</option>
                                        <option value="after">After Text</option>
                                      </select>
                                    </div>
                                    {activeBox.iconPlacement !== "none" && (
                                      <div>
                                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                          Button Icon
                                        </label>
                                        <select
                                          className="w-full text-xs border border-gray-300 rounded-lg p-2 bg-white text-gray-950 font-medium"
                                          value={activeBox.iconType || "arrow"}
                                          onChange={(e) =>
                                            updateBox(activeBox.id, {
                                              iconType: e.target.value as any,
                                            })
                                          }
                                        >
                                          <option value="arrow">Arrow</option>
                                          <option value="star">Star</option>
                                          <option value="check">Check</option>
                                          <option value="plus">Plus</option>
                                          <option value="sparkles">Sparkles</option>
                                          <option value="heart">Heart</option>
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {activeBox.type === "divider" && (
                              <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                  Line Thickness (px)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-sm border-gray-300 rounded-lg p-2 bg-white"
                                  value={activeBox.borderWidth || 4}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      borderWidth: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                            )}

                            <div className="col-span-2 grid grid-cols-4 gap-2 pt-2 border-t border-gray-200">
                              <div>
                                <label className="text-[9px] font-bold text-gray-500 uppercase text-center block mb-1">
                                  Y (%)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-xs border-gray-300 rounded-md p-1.5 text-center bg-white"
                                  value={activeBox.top}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      top: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-bold text-gray-500 uppercase text-center block mb-1">
                                  X (%)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-xs border-gray-300 rounded-md p-1.5 text-center bg-white"
                                  value={activeBox.left}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      left: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-bold text-gray-500 uppercase text-center block mb-1">
                                  Width (%)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-xs border-gray-300 rounded-md p-1.5 text-center bg-white"
                                  value={activeBox.width}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      width: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-bold text-gray-500 uppercase text-center block mb-1">
                                  Height (%)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-xs border-gray-300 rounded-md p-1.5 text-center bg-white"
                                  value={activeBox.height}
                                  onChange={(e) =>
                                    updateBox(activeBox.id, {
                                      height: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                            </div>

                            {activeBox.type !== "divider" && (
                              <div className="col-span-2 mt-2">
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                  Default Fallback Text
                                </label>
                                {activeBox.type === "icon-list" ? (
                                  <ListItemsEditor
                                    value={activeBox.text}
                                    onChange={(val) =>
                                      updateBox(activeBox.id, { text: val })
                                    }
                                    defaultIconType={activeBox.iconType}
                                    defaultCustomIcon={activeBox.customIcon}
                                  />
                                ) : (
                                  <textarea
                                    className="w-full text-sm border-gray-300 rounded-lg p-3 bg-white h-20 font-sans text-gray-905"
                                    value={activeBox.text}
                                    onChange={(e) =>
                                      updateBox(activeBox.id, {
                                        text: e.target.value,
                                      })
                                    }
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center p-8 text-gray-400">
                          Select a box to edit constraints
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Tab 2: Layout Elements Customization */}
                    {/* Background Selection */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Layout className="w-4 h-4 text-indigo-500" />
                        <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                          Canvas Background
                        </h4>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                          Background Type
                        </label>
                        <select
                          className="w-full text-sm border border-gray-200 rounded-lg p-2.5 bg-white text-gray-950 font-medium"
                          value={backgroundType}
                          onChange={(e) =>
                            updateTemplateMeta({
                              backgroundType: e.target.value as any,
                            })
                          }
                        >
                          <option value="image-only">
                            Reference Image Only
                          </option>
                          <option value="solid-color">Solid Color Only</option>
                          <option value="gradient">Gradient Only</option>
                          <option value="image-with-solid">
                            Image Overlay Solid Color
                          </option>
                          <option value="image-with-gradient">
                            Image Overlay Gradient
                          </option>
                        </select>
                      </div>
                      {(backgroundType === "solid-color" ||
                        backgroundType === "image-with-solid") && (
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 tracking-wider block mb-1">
                            Solid Color
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200"
                              value={backgroundColor}
                              onChange={(e) =>
                                updateTemplateMeta({
                                  backgroundColor: e.target.value,
                                })
                              }
                            />
                            <input
                              type="text"
                              className="flex-1 text-sm border border-gray-205 rounded-lg px-3 bg-white text-gray-950 font-mono"
                              value={backgroundColor}
                              onChange={(e) =>
                                updateTemplateMeta({
                                  backgroundColor: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      )}
                      {(backgroundType === "gradient" ||
                        backgroundType === "image-with-gradient") && (
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 tracking-wider block mb-1">
                            CSS Gradient Formula
                          </label>
                          <input
                            type="text"
                            className="w-full text-sm border border-gray-200 rounded-lg p-2.5 bg-white text-gray-950 font-mono"
                            value={backgroundGradient}
                            onChange={(e) =>
                              updateTemplateMeta({
                                    backgroundGradient: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}

                      {/* AI Background Generation Strategy Configuration */}
                      <div className="mt-4 p-3.5 bg-indigo-50 border border-indigo-100 rounded-xl space-y-3.5">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-800 uppercase tracking-wide">
                          <Sparkles className="w-3.5 h-3.5 text-indigo-600 animate-pulse" />
                          AI Backdrop Generator
                        </div>
                        
                        <div>
                          <label className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider block mb-1">
                            Generation Source Strategy
                          </label>
                          <select
                            className="w-full text-xs border border-indigo-200 rounded-lg p-2 bg-white text-gray-900 font-medium"
                            value={bgGenStrategy}
                            onChange={(e) => setBgGenStrategy(e.target.value as any)}
                          >
                            <option value="reference">
                              Strip Reference Image (Precise Layout Retention)
                            </option>
                            <option value="prompt">
                              Generate Scenic Background (Pure Prompt/Layout Content)
                            </option>
                          </select>
                        </div>

                        {bgGenStrategy === "prompt" && (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center bg-transparent">
                              <label className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider block">
                                Custom Scenic Prompt
                              </label>
                              <button
                                onClick={() => setBgGenPrompt(autoGenerateBgPrompt())}
                                className="text-[10px] font-extrabold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
                                title="Auto-synchronize layout content into a scenic textless prompt"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                                Synchronize Context
                              </button>
                            </div>
                            <textarea
                              className="w-full h-24 text-xs border border-indigo-200 rounded-lg p-2 bg-white text-gray-900 placeholder-indigo-300 focus:ring-1 focus:ring-indigo-400 focus:outline-none"
                              placeholder="Describe your visual theme or keep blank to auto-compile layout contents..."
                              value={bgGenPrompt}
                              onChange={(e) => setBgGenPrompt(e.target.value)}
                            />
                            <p className="text-[10px] text-indigo-500 leading-relaxed font-medium">
                              This uses your active text card areas, color codes, aspect ratio, & underlays to request a modern text-free image background.
                            </p>
                          </div>
                        )}

                        <button
                          onClick={generateShellImage}
                          disabled={isGeneratingShell}
                          className="w-full py-2 px-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs font-black flex items-center justify-center gap-2 transition-all shadow-sm cursor-pointer"
                        >
                          {isGeneratingShell ? (
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                          ) : (
                            <Sparkles className="w-3.5 h-3.5" />
                          )}
                          {isGeneratingShell
                            ? "Generating Backdrop..."
                            : bgGenStrategy === "prompt"
                            ? "Generate Full AI Background"
                            : "Generate Blank Layout Shell"}
                        </button>
                      </div>
                    </div>

                    {/* Plaque Backdrop */}
                    <div className="border-t border-gray-100 pt-6 space-y-4">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-indigo-500" />
                          <span className="text-sm font-black text-gray-900 uppercase tracking-wider">
                            Content Plaque Backdrop
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={enableContentPlaque}
                          onChange={(e) =>
                            updateTemplateMeta({
                              enableContentPlaque: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                        />
                      </label>
                      {enableContentPlaque && (
                        <div className="space-y-4 bg-gray-50 p-4 rounded-2xl border border-gray-150">
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Plaque Layout Style
                            </label>
                            <select
                              className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950"
                              value={plaqueStyle}
                              onChange={(e) => {
                                const style = e.target.value as any;
                                let updates: Partial<SavedTemplate> = {
                                  plaqueStyle: style,
                                };
                                if (style === "split-left") {
                                  updates.plaqueLeft = 0;
                                  updates.plaqueTop = 0;
                                  updates.plaqueWidth = 55;
                                  updates.plaqueHeight = 100;
                                  updates.plaqueBorderRadius = 0;
                                  updates.plaqueBgColor =
                                    "rgba(255, 255, 255, 0.95)";
                                } else if (style === "split-right") {
                                  updates.plaqueLeft = 45;
                                  updates.plaqueTop = 0;
                                  updates.plaqueWidth = 55;
                                  updates.plaqueHeight = 100;
                                  updates.plaqueBorderRadius = 0;
                                  updates.plaqueBgColor =
                                    "rgba(255, 255, 255, 0.95)";
                                } else if (style === "educational-tip") {
                                  updates.plaqueLeft = 0;
                                  updates.plaqueTop = 0;
                                  updates.plaqueWidth = 55;
                                  updates.plaqueHeight = 100;
                                  updates.plaqueBorderRadius = 0;
                                  updates.plaqueBgColor = "#778a78";
                                } else if (style === "glass") {
                                  updates.plaqueLeft = 15;
                                  updates.plaqueTop = 15;
                                  updates.plaqueWidth = 70;
                                  updates.plaqueHeight = 70;
                                  updates.plaqueBorderRadius = 16;
                                  updates.plaqueBgColor =
                                    "rgba(255, 255, 255, 0.25)";
                                } else if (style === "floating-modern") {
                                  updates.plaqueLeft = 15;
                                  updates.plaqueTop = 15;
                                  updates.plaqueWidth = 70;
                                  updates.plaqueHeight = 70;
                                  updates.plaqueBorderRadius = 24;
                                  updates.plaqueBgColor =
                                    "rgba(255, 255, 255, 0.95)";
                                }
                                updateTemplateMeta(updates);
                              }}
                            >
                              <option value="glass">
                                Glassmorphism Overlay (Frosted Blur)
                              </option>
                              <option value="split-left">
                                Creative Left Slide (55% Split)
                              </option>
                              <option value="split-right">
                                Creative Right Slide (55% Split)
                              </option>
                              <option value="educational-tip">
                                Warm Sage Green Tip Card
                              </option>
                              <option value="floating-modern">
                                Centrist Floating (Gloss Shadow)
                              </option>
                              <option value="custom">
                                Fully Customizable Box
                              </option>
                            </select>
                          </div>

                          <div className="grid grid-cols-2 gap-3 pt-2">
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                Width (%)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={plaqueWidth}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    plaqueWidth: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                Height (%)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={plaqueHeight}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    plaqueHeight: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                Top (%)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={plaqueTop}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    plaqueTop: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                Left (%)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={plaqueLeft}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    plaqueLeft: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                Radius (px)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={plaqueBorderRadius}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    plaqueBorderRadius: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                BG Color
                              </label>
                              <div className="flex gap-1 items-center">
                                <div
                                  className="w-6 h-6 border border-gray-200 rounded overflow-hidden relative shrink-0 shadow-sm"
                                  style={{
                                    backgroundColor: plaqueBgColor.startsWith(
                                      "#",
                                    )
                                      ? plaqueBgColor
                                      : "#ffffff",
                                  }}
                                >
                                  <input
                                    type="color"
                                    className="absolute opacity-0 inset-0 cursor-pointer w-full h-full"
                                    value={
                                      plaqueBgColor.startsWith("#")
                                        ? plaqueBgColor
                                        : "#ffffff"
                                    }
                                    onChange={(e) =>
                                      updateTemplateMeta({
                                        plaqueBgColor: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <input
                                  type="text"
                                  className="w-full text-[11px] border border-gray-200 rounded p-1 font-mono text-gray-950 bg-white"
                                  value={plaqueBgColor}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      plaqueBgColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <CoreBrandPaletteSelect
                                palette={activeBrand?.colorPalette || []}
                                selectedColor={plaqueBgColor}
                                onSelect={(color) =>
                                  updateTemplateMeta({
                                    plaqueBgColor: color,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <CompactNumberInput
                            label="Plaque Opacity"
                            value={plaqueOpacity}
                            min={10}
                            max={100}
                            unit="%"
                            onChange={(val) =>
                              updateTemplateMeta({
                                plaqueOpacity: val,
                              })
                            }
                          />
                        </div>
                      )}
                    </div>

                    {/* Header Builder */}
                    <div className="border-t border-gray-100 pt-6 space-y-4">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Menu className="w-4 h-4 text-indigo-500" />
                          <span className="text-sm font-black text-gray-900 uppercase tracking-wider">
                            Modular Header
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={enableHeader}
                          onChange={(e) =>
                            updateTemplateMeta({
                              enableHeader: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                        />
                      </label>
                      {enableHeader && (
                        <div className="space-y-4 bg-gray-50 p-4 rounded-2xl border border-gray-150">
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Header Style Preset
                            </label>
                            <select
                              className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950"
                              value={headerStyle}
                              onChange={(e) =>
                                updateTemplateMeta({
                                  headerStyle: e.target.value as any,
                                })
                              }
                            >
                              <option value="clean">
                                Minimalist (No border)
                              </option>
                              <option value="bordered">
                                Subtle Border Separator
                              </option>
                              <option value="accent-bar">
                                Indigo Brand Accent Line
                              </option>
                              <option value="glass">
                                Floating Glass Header
                              </option>
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase font-mono mb-1 block">
                                Height (%)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={headerHeight}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    headerHeight: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 block mb-1">
                                Text / Elements Fill
                              </label>
                              <div className="flex gap-1 items-center mb-1">
                                <div
                                  className="w-5 h-5 border border-gray-200 rounded overflow-hidden relative shrink-0 shadow-sm"
                                  style={{
                                    backgroundColor: headerTextColor.startsWith("#") ? headerTextColor : "#ffffff",
                                  }}
                                >
                                  <input
                                    type="color"
                                    className="absolute opacity-0 inset-0 cursor-pointer w-full h-full"
                                    value={headerTextColor.startsWith("#") ? headerTextColor : "#111827"}
                                    onChange={(e) =>
                                      updateTemplateMeta({
                                        headerTextColor: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <input
                                  type="text"
                                  className="w-full text-xs border border-gray-200 rounded p-1 font-mono text-gray-950 bg-white"
                                  value={headerTextColor}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      headerTextColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <CoreBrandPaletteSelect
                                palette={activeBrand?.colorPalette || []}
                                selectedColor={headerTextColor}
                                onSelect={(color) =>
                                  updateTemplateMeta({
                                    headerTextColor: color,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Header Background Fill Hex
                            </label>
                            <div className="flex gap-1 items-center mb-1">
                              <div
                                className="w-6 h-6 border border-gray-200 rounded overflow-hidden relative shrink-0 shadow-sm"
                                style={{
                                  backgroundColor: headerBgColor.startsWith("#") ? headerBgColor : "#ffffff",
                                }}
                              >
                                <input
                                  type="color"
                                  className="absolute opacity-0 inset-0 cursor-pointer w-full h-full"
                                  value={headerBgColor.startsWith("#") ? headerBgColor : "#ffffff"}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      headerBgColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <input
                                type="text"
                                className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white font-mono text-gray-950"
                                value={headerBgColor}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    headerBgColor: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <CoreBrandPaletteSelect
                              palette={activeBrand?.colorPalette || []}
                              selectedColor={headerBgColor}
                              onSelect={(color) =>
                                updateTemplateMeta({
                                  headerBgColor: color,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Eyebrow Brand Label Text
                            </label>
                            <input
                              type="text"
                              className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950"
                              placeholder="e.g. INSIDER ANALYSIS"
                              value={headerText}
                              onChange={(e) =>
                                updateTemplateMeta({
                                  headerText: e.target.value,
                                })
                              }
                            />
                          </div>
                          <CompactNumberInput
                            label="Header Font Size"
                            value={headerFontSize}
                            min={0.5}
                            max={5}
                            unit="cqw"
                            step={0.1}
                            onChange={(val) =>
                              updateTemplateMeta({
                                headerFontSize: val,
                              })
                            }
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={headerShowLogo}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    headerShowLogo: e.target.checked,
                                  })
                                }
                                className="rounded text-indigo-600 border-gray-300"
                              />
                              <span>Show logo</span>
                            </label>
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={headerShowEyebrow}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    headerShowEyebrow: e.target.checked,
                                  })
                                }
                                className="rounded text-indigo-600 border-gray-300"
                              />
                              <span>Show Eyebrow</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Builder */}
                    <div className="border-t border-gray-100 pt-6 space-y-4">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-indigo-500" />
                          <span className="text-sm font-black text-gray-900 uppercase tracking-wider">
                            Modular Footer
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={enableFooter}
                          onChange={(e) =>
                            updateTemplateMeta({
                              enableFooter: e.target.checked,
                            })
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                        />
                      </label>
                      {enableFooter && (
                        <div className="space-y-4 bg-gray-50 p-4 rounded-2xl border border-gray-150">
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Footer Style Preset
                            </label>
                            <select
                              className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950"
                              value={footerStyle}
                              onChange={(e) =>
                                updateTemplateMeta({
                                  footerStyle: e.target.value as any,
                                })
                              }
                            >
                              <option value="clean">
                                Minimalist (No border)
                              </option>
                              <option value="bordered">
                                Subtle Border Separator
                              </option>
                              <option value="accent-bar">
                                Indigo Brand Accent Line
                              </option>
                              <option value="glass">
                                Floating Glass Footer
                              </option>
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase font-mono mb-1 block">
                                Height (%)
                              </label>
                              <input
                                type="number"
                                className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950 bg-white"
                                value={footerHeight}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    footerHeight: Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 block mb-1">
                                Text / Elements Fill
                              </label>
                              <div className="flex gap-1 items-center mb-1">
                                <div
                                  className="w-5 h-5 border border-gray-200 rounded overflow-hidden relative shrink-0 shadow-sm"
                                  style={{
                                    backgroundColor: footerTextColor.startsWith("#") ? footerTextColor : "#ffffff",
                                  }}
                                >
                                  <input
                                    type="color"
                                    className="absolute opacity-0 inset-0 cursor-pointer w-full h-full"
                                    value={footerTextColor.startsWith("#") ? footerTextColor : "#111827"}
                                    onChange={(e) =>
                                      updateTemplateMeta({
                                        footerTextColor: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <input
                                  type="text"
                                  className="w-full text-xs border border-gray-200 rounded p-1 font-mono text-gray-950 bg-white"
                                  value={footerTextColor}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      footerTextColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <CoreBrandPaletteSelect
                                palette={activeBrand?.colorPalette || []}
                                selectedColor={footerTextColor}
                                onSelect={(color) =>
                                  updateTemplateMeta({
                                    footerTextColor: color,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Footer Background Fill Hex
                            </label>
                            <div className="flex gap-1 items-center mb-1">
                              <div
                                className="w-6 h-6 border border-gray-200 rounded overflow-hidden relative shrink-0 shadow-sm"
                                style={{
                                  backgroundColor: footerBgColor.startsWith("#") ? footerBgColor : "#ffffff",
                                }}
                              >
                                <input
                                  type="color"
                                  className="absolute opacity-0 inset-0 cursor-pointer w-full h-full"
                                  value={footerBgColor.startsWith("#") ? footerBgColor : "#ffffff"}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      footerBgColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <input
                                type="text"
                                className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white font-mono text-gray-950"
                                value={footerBgColor}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    footerBgColor: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <CoreBrandPaletteSelect
                              palette={activeBrand?.colorPalette || []}
                              selectedColor={footerBgColor}
                              onSelect={(color) =>
                                updateTemplateMeta({
                                  footerBgColor: color,
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              Footer Legal/Label text
                            </label>
                            <input
                              type="text"
                              className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950"
                              placeholder="e.g. Save for later"
                              value={footerText}
                              onChange={(e) =>
                                updateTemplateMeta({
                                  footerText: e.target.value,
                                })
                              }
                            />
                          </div>
                          <CompactNumberInput
                            label="Footer Font Size"
                            value={footerFontSize ?? 1.8}
                            min={0.5}
                            max={5}
                            unit="cqw"
                            step={0.1}
                            onChange={(val) =>
                              updateTemplateMeta({
                                footerFontSize: val,
                              })
                            }
                          />
                          <div className="grid grid-cols-3 gap-2">
                            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={footerShowLogo}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    footerShowLogo: e.target.checked,
                                  })
                                }
                                className="rounded text-indigo-600 border-gray-300"
                              />
                              <span>Logo</span>
                            </label>
                            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={footerShowUrl}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    footerShowUrl: e.target.checked,
                                  })
                                }
                                className="rounded text-indigo-600 border-gray-300"
                              />
                              <span>URL</span>
                            </label>
                            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={footerShowPageNum}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    footerShowPageNum: e.target.checked,
                                  })
                                }
                                className="rounded text-indigo-600 border-gray-300"
                              />
                              <span>Pages</span>
                            </label>
                          </div>

                          {/* Element Custom Style Editors */}
                          <div className="border-t border-gray-150 pt-3 space-y-4">
                            <span className="text-[9px] font-bold text-gray-400 uppercase block">
                              Detailed Element Styles
                            </span>

                            {/* URL/Logo Element Styling */}
                            {footerShowUrl && (
                              <div className="bg-gray-50 border border-gray-200 p-2.5 rounded-lg space-y-2">
                                <span className="text-[10px] font-bold text-gray-700 block uppercase">
                                  URL / Brand Label Styles
                                </span>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactColorPicker
                                    label="Background Color"
                                    value={footerUrlBgColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerUrlBgColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <CompactColorPicker
                                    label="Text Color"
                                    value={footerUrlTextColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerUrlTextColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactColorPicker
                                    label="Border Color"
                                    value={footerUrlBorderColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerUrlBorderColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <CompactNumberInput
                                    label="Border Radius"
                                    value={footerUrlBorderRadius ?? 0}
                                    min={0}
                                    max={24}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerUrlBorderRadius: val,
                                      })
                                    }
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactNumberInput
                                    label="Border Width"
                                    value={footerUrlBorderWidth ?? 0}
                                    min={0}
                                    max={4}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerUrlBorderWidth: val,
                                      })
                                    }
                                  />
                                  <CompactNumberInput
                                    label="Padding"
                                    value={footerUrlPadding ?? 0}
                                    min={0}
                                    max={12}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerUrlPadding: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            )}

                            {/* Legal Line Element Styling */}
                            {footerText && (
                              <div className="bg-gray-50 border border-gray-200 p-2.5 rounded-lg space-y-2">
                                <span className="text-[10px] font-bold text-gray-700 block uppercase">
                                  Legal Text Element Styles
                                </span>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactColorPicker
                                    label="Background Color"
                                    value={footerLegalBgColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerLegalBgColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <CompactColorPicker
                                    label="Text Color"
                                    value={footerLegalTextColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerLegalTextColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactColorPicker
                                    label="Border Color"
                                    value={footerLegalBorderColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerLegalBorderColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <CompactNumberInput
                                    label="Border Radius"
                                    value={footerLegalBorderRadius ?? 0}
                                    min={0}
                                    max={24}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerLegalBorderRadius: val,
                                      })
                                    }
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactNumberInput
                                    label="Border Width"
                                    value={footerLegalBorderWidth ?? 0}
                                    min={0}
                                    max={4}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerLegalBorderWidth: val,
                                      })
                                    }
                                  />
                                  <CompactNumberInput
                                    label="Padding"
                                    value={footerLegalPadding ?? 0}
                                    min={0}
                                    max={12}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerLegalPadding: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            )}

                            {/* Page Numbers Element Styling */}
                            {footerShowPageNum && (
                              <div className="bg-gray-50 border border-gray-200 p-2.5 rounded-lg space-y-2">
                                <span className="text-[10px] font-bold text-gray-700 block uppercase">
                                  Page Numbers Element Styles
                                </span>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactColorPicker
                                    label="Background Color"
                                    value={footerPageNumBgColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerPageNumBgColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <CompactColorPicker
                                    label="Text Color"
                                    value={footerPageNumTextColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerPageNumTextColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactColorPicker
                                    label="Border Color"
                                    value={footerPageNumBorderColor}
                                    onChange={(color) =>
                                      updateTemplateMeta({
                                        footerPageNumBorderColor: color,
                                      })
                                    }
                                    palette={activeBrand?.colorPalette || []}
                                  />
                                  <CompactNumberInput
                                    label="Border Radius"
                                    value={footerPageNumBorderRadius ?? 0}
                                    min={0}
                                    max={24}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerPageNumBorderRadius: val,
                                      })
                                    }
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  <CompactNumberInput
                                    label="Border Width"
                                    value={footerPageNumBorderWidth ?? 0}
                                    min={0}
                                    max={4}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerPageNumBorderWidth: val,
                                      })
                                    }
                                  />
                                  <CompactNumberInput
                                    label="Padding"
                                    value={footerPageNumPadding ?? 0}
                                    min={0}
                                    max={12}
                                    onChange={(val) =>
                                      updateTemplateMeta({
                                        footerPageNumPadding: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Element Alignment Placements in Footer */}
                          <div className="border-t border-gray-150 pt-3 space-y-2">
                            <span className="text-[9px] font-bold text-gray-400 uppercase block">
                              Footer Element Placements
                            </span>

                            {/* URL/Logo Alignment */}
                            <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-150 text-xs">
                              <span className="font-semibold text-gray-700">
                                URL / Logo Align
                              </span>
                              <div className="flex rounded-md shadow-sm">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateTemplateMeta({
                                      footerUrlAlign: "left",
                                    })
                                  }
                                  className={`px-2.5 py-1 text-[10px] font-bold rounded-l-md border ${
                                    footerUrlAlign === "left"
                                      ? "bg-indigo-50 border-indigo-500 text-indigo-600 z-10"
                                      : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                                  }`}
                                >
                                  Left
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateTemplateMeta({
                                      footerUrlAlign: "right",
                                    })
                                  }
                                  className={`px-2.5 py-1 text-[10px] font-bold rounded-r-md border-y border-r ${
                                    footerUrlAlign === "right"
                                      ? "bg-indigo-50 border-indigo-500 text-indigo-600 z-10"
                                      : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                                  }`}
                                >
                                  Right
                                </button>
                              </div>
                            </div>

                            {/* Legal Text Alignment */}
                            <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-150 text-xs">
                              <span className="font-semibold text-gray-700">
                                Legal Line Align
                              </span>
                              <div className="flex rounded-md shadow-sm">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateTemplateMeta({
                                      footerTextAlign: "left",
                                    })
                                  }
                                  className={`px-2.5 py-1 text-[10px] font-bold rounded-l-md border ${
                                    footerTextAlign === "left"
                                      ? "bg-indigo-50 border-indigo-500 text-indigo-600 z-10"
                                      : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                                  }`}
                                >
                                  Left
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateTemplateMeta({
                                      footerTextAlign: "right",
                                    })
                                  }
                                  className={`px-2.5 py-1 text-[10px] font-bold rounded-r-md border-y border-r ${
                                    footerTextAlign === "right"
                                      ? "bg-indigo-50 border-indigo-500 text-indigo-600 z-10"
                                      : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                                  }`}
                                >
                                  Right
                                </button>
                              </div>
                            </div>

                            {/* Page Number Alignment */}
                            <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-150 text-xs">
                              <span className="font-semibold text-gray-700">
                                Page Numbers Align
                              </span>
                              <div className="flex rounded-md shadow-sm">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateTemplateMeta({
                                      footerPageNumAlign: "left",
                                    })
                                  }
                                  className={`px-2.5 py-1 text-[10px] font-bold rounded-l-md border ${
                                    footerPageNumAlign === "left"
                                      ? "bg-indigo-50 border-indigo-500 text-indigo-600 z-10"
                                      : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                                  }`}
                                >
                                  Left
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateTemplateMeta({
                                      footerPageNumAlign: "right",
                                    })
                                  }
                                  className={`px-2.5 py-1 text-[10px] font-bold rounded-r-md border-y border-r ${
                                    footerPageNumAlign === "right"
                                      ? "bg-indigo-50 border-indigo-500 text-indigo-600 z-10"
                                      : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                                  }`}
                                >
                                  Right
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Independent Logo */}
                    <div className="border-t border-gray-100 pt-6 space-y-4">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-indigo-500" />
                        <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                          Independent Brand Logo
                        </h4>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                          Overlay Logo Position
                        </label>
                        <select
                          className="w-full text-sm border border-gray-205 rounded-lg p-2.5 bg-white text-gray-950 font-medium"
                          value={logoPosition}
                          onChange={(e) =>
                            updateTemplateMeta({
                              logoPosition: e.target.value as any,
                            })
                          }
                        >
                          <option value="none">
                            Disabled / Handled by header/footer
                          </option>
                          <option value="content-top">
                            Standard Top Overlay (Y: 5%)
                          </option>
                          <option value="content-bottom">
                            Standard Bottom Overlay (Y: 85%)
                          </option>
                          <option value="floating">
                            Fully Customizable Floating Logo
                          </option>
                        </select>
                      </div>
                      {logoPosition !== "none" && (
                        <div className="space-y-4 bg-gray-50 p-4 rounded-2xl border border-gray-150">
                          {logoPosition !== "floating" && (
                            <div>
                              <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                                Horizontal Alignment
                              </label>
                              <select
                                className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950 font-semibold"
                                value={logoAlignment}
                                onChange={(e) =>
                                  updateTemplateMeta({
                                    logoAlignment: e.target.value as any,
                                  })
                                }
                              >
                                <option value="left">Float Left</option>
                                <option value="center">Center Aligned</option>
                                <option value="right">Float Right</option>
                              </select>
                            </div>
                          )}
                          <CompactNumberInput
                            label="Max Box Dimension"
                            value={logoSize}
                            min={3}
                            max={30}
                            unit="%"
                            onChange={(val) =>
                              updateTemplateMeta({
                                logoSize: val,
                              })
                            }
                          />
                          {logoPosition === "floating" && (
                            <div className="grid grid-cols-2 gap-3 pt-2">
                              <div>
                                <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                  Top Pos (%)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950"
                                  value={logoTop}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      logoTop: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                  Left Pos (%)
                                </label>
                                <input
                                  type="number"
                                  className="w-full text-xs border border-gray-200 rounded p-1 text-gray-950"
                                  value={logoLeft}
                                  onChange={(e) =>
                                    updateTemplateMeta({
                                      logoLeft: Number(e.target.value),
                                    })
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-gray-400">
                <ShieldAlert className="w-12 h-12 mb-4 opacity-50" />
                <p>
                  Upload and extract an image to unlock the configuration panel.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Generation Section */}
      {savedTemplate && (
        <div className="mt-12 border-t border-gray-200 pt-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-1">
                Test Template Generation
              </h2>
              <p className="text-gray-500 text-sm mb-3">
                Generate mock copy using a brand profile and render it onto the
                saved template.
              </p>
              <input 
                type="text" 
                placeholder="Optional topic (e.g., Launch of new product)" 
                value={sandboxTopic}
                onChange={(e) => setSandboxTopic(e.target.value)}
                className="w-full sm:w-64 border border-gray-300 rounded-lg p-2 text-sm bg-white focus:ring-1 focus:ring-indigo-500"
              />
              <button
                onClick={clearSavedTemplate}
                className="block text-[10px] uppercase font-bold tracking-wider text-red-500 hover:text-red-700 mt-2"
              >
                Clear Saved Template
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <select
                className="border border-gray-300 rounded-lg p-2 text-sm bg-white"
                value={selectedBrandId}
                onChange={(e) => setSelectedBrandId(e.target.value)}
              >
                <option value="">Select a Brand...</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.companyName}
                  </option>
                ))}
              </select>
              <button
                onClick={generateMockPost}
                disabled={isGeneratingPost || !selectedBrandId}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 transition"
              >
                {isGeneratingPost ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                {isGeneratingPost ? "Generating..." : "Generate Mock Post"}
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Generated Data Debug */}
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">
                Generated Content Payload
              </h3>
              {isGeneratingPost ? (
                <div className="flex items-center justify-center h-40 text-gray-400">
                  <div className="flex flex-col items-center">
                    <div className="w-6 h-6 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin mb-3" />
                    Waiting for Gemini 2.5 Flash...
                  </div>
                </div>
              ) : generatedPostData ? (
                <div className="space-y-4">
                  {savedTemplate.boxes
                    .filter((b) => b.type !== "logo")
                    .map((box) => {
                      const isExpanded = expandedPayloadIds.includes(box.id);
                      return (
                        <div
                          key={box.id}
                          className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                        >
                          <button
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                            onClick={() =>
                              setExpandedPayloadIds((prev) =>
                                prev.includes(box.id)
                                  ? prev.filter((id) => id !== box.id)
                                  : [...prev, box.id],
                              )
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                {box.type}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                ({box.id})
                              </span>
                            </div>
                            <div className="text-gray-400">
                              {isExpanded ? (
                                <X className="w-4 h-4" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="p-3 border-t border-gray-200">
                              {box.type === "icon-list" ? (
                                <div className="mb-2">
                                  <ListItemsEditor
                                    value={generatedPostData[box.id] || ""}
                                    onChange={(val) =>
                                      setGeneratedPostData((prev) =>
                                        prev
                                          ? { ...prev, [box.id]: val }
                                          : prev,
                                      )
                                    }
                                    defaultIconType={box.iconType}
                                    defaultCustomIcon={box.customIcon}
                                  />
                                </div>
                              ) : box.type === "image" ? (
                                <div className="space-y-4 mb-2">
                                  {/* Direct Image URL Input */}
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                                      Image Source URL
                                    </label>
                                    <input
                                      type="text"
                                      id={`input-${box.id}`}
                                      className="w-full text-xs border border-gray-300 rounded-lg p-2 bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Paste URL (or choose from brand library)..."
                                      value={box.imageUrl || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        updateSavedTemplateBox(box.id, { imageUrl: val });
                                        setGeneratedPostData((prev) =>
                                          prev ? { ...prev, [box.id]: val } : prev
                                        );
                                      }}
                                    />
                                  </div>

                                  {/* Manual File Uploader */}
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                                      Upload Custom Image
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="file"
                                        accept="image/*"
                                        id={`file-upload-${box.id}`}
                                        className="hidden"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) {
                                            const reader = new FileReader();
                                            reader.onload = (event) => {
                                              const base64 = event.target?.result as string;
                                              updateSavedTemplateBox(box.id, { imageUrl: base64 });
                                              setGeneratedPostData((prev) =>
                                                prev ? { ...prev, [box.id]: base64 } : prev
                                              );
                                            };
                                            reader.readAsDataURL(file);
                                          }
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => document.getElementById(`file-upload-${box.id}`)?.click()}
                                        className="flex items-center justify-center gap-2 px-3 py-1.5 border border-indigo-200 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-all w-full"
                                      >
                                        <Upload className="w-3.5 h-3.5" />
                                        Upload from computer
                                      </button>
                                    </div>
                                  </div>

                                  {/* Approved Media Select */}
                                  <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                                      Approved Brand Media Assets
                                    </label>
                                    {activeBrand?.approvedMedia && activeBrand.approvedMedia.length > 0 ? (
                                      <div className="grid grid-cols-4 gap-2 border border-gray-100 rounded-lg p-2 bg-gray-50 max-h-[150px] overflow-y-auto">
                                        {activeBrand.approvedMedia.map((media) => {
                                          const isSelected = box.imageUrl === media.url;
                                          return (
                                            <button
                                              key={media.id || media.url}
                                              type="button"
                                              onClick={() => {
                                                updateSavedTemplateBox(box.id, { imageUrl: media.url });
                                                setGeneratedPostData((prev) =>
                                                  prev ? { ...prev, [box.id]: media.url } : prev
                                                );
                                              }}
                                              title={media.name}
                                              className={`relative aspect-square rounded-md overflow-hidden bg-white border-2 hover:border-indigo-400 transition-all ${isSelected ? "border-indigo-600 ring-2 ring-indigo-500/20" : "border-gray-200"}`}
                                            >
                                              <img
                                                src={media.url}
                                                alt={media.name}
                                                className="w-full h-full object-cover"
                                              />
                                            </button>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="text-[10px] text-gray-400 bg-gray-50 border border-gray-100 rounded-lg p-3 text-center">
                                        No assets in Brand Media storage yet. Upload them under the "Approved Media" tab.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <textarea
                                  id={`input-${box.id}`}
                                  className="w-full text-sm border-gray-300 rounded-lg p-3 bg-white min-h-[80px] focus:ring-2 focus:ring-indigo-500 transition-all text-gray-950"
                                  value={generatedPostData[box.id] || ""}
                                  onChange={(e) =>
                                    setGeneratedPostData((prev) =>
                                      prev
                                        ? { ...prev, [box.id]: e.target.value }
                                        : prev,
                                    )
                                  }
                                />
                              )}
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 pt-3 border-t border-gray-100 mt-2">
                                <div className="space-y-1">
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block">
                                    Color
                                  </label>
                                  <div className="flex bg-white rounded border border-gray-200 p-0.5 items-center gap-1">
                                    <div
                                      className="w-3.5 h-3.5 rounded border border-gray-100 flex-shrink-0"
                                      style={{ backgroundColor: box.color || "#000000" }}
                                    />
                                    <input
                                      type="text"
                                      className="w-full text-[10px] border-none focus:ring-0 p-0 bg-transparent font-mono text-gray-950"
                                      value={box.color || ""}
                                      onChange={(e) =>
                                        updateSavedTemplateBox(box.id, {
                                          color: e.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                  <CoreBrandPaletteSelect
                                    palette={activeBrand?.colorPalette || []}
                                    selectedColor={box.color || ""}
                                    onSelect={(color) =>
                                      updateSavedTemplateBox(box.id, {
                                        color,
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Align
                                  </label>
                                  <select
                                    className="w-full text-xs border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.textAlign || "left"}
                                    onChange={(e) =>
                                      updateSavedTemplateBox(box.id, {
                                        textAlign: e.target.value as any,
                                      })
                                    }
                                  >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Size (cqw)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    className="w-full text-xs border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.fontSize}
                                    onChange={(e) =>
                                      updateSavedTemplateBox(box.id, {
                                        fontSize: Number(e.target.value),
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Y Offset
                                  </label>
                                  <input
                                    type="number"
                                    step="0.05"
                                    className="w-full text-xs text-gray-900 border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.yOffset ?? 0}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateSavedTemplateBox(box.id, {
                                        yOffset: parseFloat(val) || 0,
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Top (%)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    className="w-full text-xs text-gray-900 border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.top ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateSavedTemplateBox(box.id, {
                                        top: parseFloat(val) || 0,
                                      });
                                    }}
                                    onBlur={(e) => {
                                      const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                      updateSavedTemplateBox(box.id, { top: Number(val.toFixed(1)) });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Left (%)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    className="w-full text-xs text-gray-900 border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.left ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateSavedTemplateBox(box.id, {
                                        left: parseFloat(val) || 0,
                                      });
                                    }}
                                    onBlur={(e) => {
                                      const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                      updateSavedTemplateBox(box.id, { left: Number(val.toFixed(1)) });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Width (%)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    className="w-full text-xs text-gray-900 border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.width ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateSavedTemplateBox(box.id, {
                                        width: parseFloat(val) || 0,
                                      });
                                    }}
                                    onBlur={(e) => {
                                      const val = Math.max(5, Math.min(100, parseFloat(e.target.value) || 5));
                                      updateSavedTemplateBox(box.id, { width: Number(val.toFixed(1)) });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Height (%)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.1"
                                    className="w-full text-xs text-gray-900 border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.height ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateSavedTemplateBox(box.id, {
                                        height: parseFloat(val) || 0,
                                      });
                                    }}
                                    onBlur={(e) => {
                                      const val = Math.max(5, Math.min(100, parseFloat(e.target.value) || 5));
                                      updateSavedTemplateBox(box.id, { height: Number(val.toFixed(1)) });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                    Height Mode
                                  </label>
                                  <select
                                    className="w-full text-xs border-gray-200 rounded p-1 bg-gray-50"
                                    value={box.flowNaturally ? "flow" : "fit"}
                                    onChange={(e) =>
                                      updateSavedTemplateBox(box.id, {
                                        flowNaturally:
                                          e.target.value === "flow",
                                      })
                                    }
                                  >
                                    <option value="fit">Shrink to Fit</option>
                                    <option value="flow">
                                      Flow / Auto Height
                                    </option>
                                  </select>
                                </div>
                                {box.type === "icon-list" && (
                                  <div>
                                    <label className="text-[9px] font-bold text-gray-400 uppercase block mb-1">
                                      List Icon
                                    </label>
                                    <select
                                      className="w-full text-xs border-gray-200 rounded p-1 bg-gray-50"
                                      value={box.iconType || "check"}
                                      onChange={(e) =>
                                        updateSavedTemplateBox(box.id, {
                                          iconType: e.target.value as any,
                                        })
                                      }
                                    >
                                      <option value="check">Check</option>
                                      <option value="star">Star</option>
                                      <option value="arrow">Arrow</option>
                                      <option value="plus">Plus</option>
                                    </select>
                                  </div>
                                )}
                              </div>

                              {box.type === "icon-list" && (
                                <div className="col-span-2 lg:col-span-4 mt-3 bg-indigo-50 border border-indigo-100 p-2.5 rounded-lg text-indigo-700 text-[10px] leading-relaxed">
                                  💡 **Pro-Tip & Multi-Icon Cards:** You can
                                  define a different icon for each bullet in
                                  this list! Start each text line with an emoji
                                  (e.g. 📞, ✉️, 👉) or an icon tag like{" "}
                                  <code>[star]</code> or <code>[none]</code>.
                                </div>
                              )}

                              {box.type !== "logo" && (
                                <div className="col-span-2 lg:col-span-4 mt-3 pt-3 border-t border-gray-150">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                      Advanced Editing
                                    </span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={!!box.renderAsButton}
                                        onChange={(e) =>
                                          updateSavedTemplateBox(box.id, {
                                            renderAsButton: e.target.checked,
                                          })
                                        }
                                      />
                                      <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-indigo-600"></div>
                                    </label>
                                  </div>

                                  {box.renderAsButton && (
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-2 bg-gray-50 p-2 border border-gray-200 rounded-lg">
                                      <div>
                                        <label className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">
                                          BG Color
                                        </label>
                                        <div className="flex gap-1.5 items-center">
                                          <div
                                            className="w-6 h-6 rounded border border-gray-200 overflow-hidden relative shrink-0"
                                            style={{
                                              backgroundColor:
                                                box.backgroundColor ||
                                                "transparent",
                                            }}
                                          >
                                            <input
                                              type="color"
                                              className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                                              value={
                                                box.backgroundColor || "#4f46e5"
                                              }
                                              onChange={(e) =>
                                                updateSavedTemplateBox(box.id, {
                                                  backgroundColor:
                                                    e.target.value,
                                                })
                                              }
                                            />
                                          </div>
                                          <input
                                            type="text"
                                            className="flex-1 min-w-0 text-[11px] border border-gray-200 rounded p-1 bg-white font-mono text-gray-950"
                                            placeholder="e.g. #4f46e5"
                                            value={box.backgroundColor || ""}
                                            onChange={(e) =>
                                              updateSavedTemplateBox(box.id, {
                                                backgroundColor: e.target.value,
                                              })
                                            }
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">
                                          Radius (px)
                                        </label>
                                        <select
                                          className="w-full text-xs border-gray-200 rounded p-1 bg-white text-gray-950"
                                          value={box.borderRadius ?? 8}
                                          onChange={(e) =>
                                            updateSavedTemplateBox(box.id, {
                                              borderRadius: Number(
                                                e.target.value,
                                              ),
                                            })
                                          }
                                        >
                                          <option value="0">
                                            Square (0px)
                                          </option>
                                          <option value="4">Small (4px)</option>
                                          <option value="8">
                                            Medium (8px)
                                          </option>
                                          <option value="12">
                                            Large (12px)
                                          </option>
                                          <option value="20">
                                            XLarge (20px)
                                          </option>
                                          <option value="9999">
                                            Pill (9999px)
                                          </option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">
                                          Icon Position
                                        </label>
                                        <select
                                          className="w-full text-xs border-gray-200 rounded p-1 bg-white text-gray-950"
                                          value={box.iconPlacement || "after"}
                                          onChange={(e) =>
                                            updateSavedTemplateBox(box.id, {
                                              iconPlacement: e.target
                                                .value as any,
                                            })
                                          }
                                        >
                                          <option value="none">No Icon</option>
                                          <option value="before">
                                            Before Text
                                          </option>
                                          <option value="after">
                                            After Text
                                          </option>
                                        </select>
                                      </div>
                                      {box.iconPlacement !== "none" && (
                                        <div>
                                          <label className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">
                                            Button Icon
                                          </label>
                                          <select
                                            className="w-full text-xs border-gray-200 rounded p-1 bg-white text-gray-950"
                                            value={box.iconType || "arrow"}
                                            onChange={(e) =>
                                              updateSavedTemplateBox(box.id, {
                                                iconType: e.target.value as any,
                                              })
                                            }
                                          >
                                            <option value="arrow">Arrow</option>
                                            <option value="star">Star</option>
                                            <option value="check">Check</option>
                                            <option value="plus">Plus</option>
                                            <option value="sparkles">
                                              Sparkles
                                            </option>
                                            <option value="heart">Heart</option>
                                          </select>
                                        </div>
                                      )}
                                      <div>
                                        <label className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">
                                          Border (px)
                                        </label>
                                        <input
                                          type="number"
                                          className="w-full text-xs border-gray-200 rounded p-1 bg-white text-gray-950"
                                          value={box.borderWidth || 0}
                                          onChange={(e) =>
                                            updateSavedTemplateBox(box.id, {
                                              borderWidth: Number(
                                                e.target.value,
                                              ),
                                            })
                                          }
                                        />
                                      </div>
                                      <div>
                                        <label className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">
                                          Border Color
                                        </label>
                                        <input
                                          type="text"
                                          className="w-full text-xs border-gray-200 rounded p-1 bg-white font-mono text-gray-950"
                                          placeholder="e.g. #ffffff"
                                          value={box.borderColor || ""}
                                          onChange={(e) =>
                                            updateSavedTemplateBox(box.id, {
                                              borderColor: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
                  Click Generate Mock Post to populate.
                </div>
              )}
            </div>

            {/* Generated Canva Output */}
            <div className="bg-white p-6 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col items-center">
              <div className="w-full flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                  Rendered Output
                </h3>
                <button
                  onClick={async () => {
                    setIsExporting(true);
                    setActiveRenderedBoxId(null);
                    await new Promise((r) => setTimeout(r, 100));
                    const el = document.getElementById("rendered-post-output");
                    if (el) {
                      try {
                        const dataUrl = await htmlToImage.toPng(el, {
                          cacheBust: true,
                          pixelRatio: 2,
                        });
                        const link = document.createElement("a");
                        link.download = "mock-post.png";
                        link.href = dataUrl;
                        link.click();
                      } catch (e) {
                        console.error("Failed to capture image", e);
                        alert(
                          "Failed to capture image. Check console for details.",
                        );
                      }
                    }
                    setIsExporting(false);
                  }}
                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                  title="Download Image"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
              <div
                id="rendered-post-output"
                ref={renderedContainerRef}
                className="w-full relative aspect-square overflow-hidden rounded-lg shadow-inner @container touch-none select-none"
                onClick={() => setActiveRenderedBoxId(null)}
                style={{
                  background:
                    backgroundType === "solid-color" ||
                    backgroundType === "image-with-solid"
                      ? backgroundColor
                      : backgroundType === "gradient" ||
                          backgroundType === "image-with-gradient"
                        ? backgroundGradient
                        : "#ffffff",
                }}
              >
                {isGeneratingImage ? (
                  <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-30">
                    <div className="flex flex-col items-center text-gray-500">
                      <div className="w-8 h-8 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin mb-3" />
                      <span className="text-sm font-bold tracking-wider uppercase">
                        Generating Background...
                      </span>
                    </div>
                  </div>
                ) : (
                  (backgroundType === "image-only" ||
                    backgroundType === "image-with-solid" ||
                    backgroundType === "image-with-gradient") && (
                    <img
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      src={
                        generatedBackgroundImage ||
                        savedTemplate.backgroundImage
                      }
                      className="w-full h-full object-cover pointer-events-none select-none absolute inset-0 z-0"
                      alt="Template Context"
                    />
                  )
                )}

                {/* Content Plaque Backdrop Layer */}
                {enableContentPlaque && (
                  <div
                    className={`absolute transition-all ${isExporting ? "" : "group hover:outline hover:outline-2 hover:outline-indigo-500/50 hover:bg-slate-100/5 cursor-grab pointer-events-auto ring-1 ring-indigo-500/20 hover:ring-indigo-500/50"}`}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setActivePanelTab("layout");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "layout-plaque-section",
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 50);

                      const rect = e.currentTarget.getBoundingClientRect();
                      setDraggingPlaqueRendered({
                        offsetX: e.clientX - rect.left,
                        offsetY: e.clientY - rect.top,
                        type: "move",
                      });
                    }}
                    style={{
                      left: `${plaqueLeft}%`,
                      top: `${plaqueTop}%`,
                      width: `${plaqueWidth}%`,
                      height: `${plaqueHeight}%`,
                      borderRadius: `${plaqueBorderRadius}px`,
                      backgroundColor: plaqueBgColor,
                      backdropFilter:
                        plaqueStyle === "glass" ||
                        plaqueStyle === "floating-modern"
                          ? "blur(16px)"
                          : undefined,
                      boxShadow:
                        plaqueStyle === "glass" ||
                        plaqueStyle === "floating-modern" ||
                        plaqueStyle === "split-left" ||
                        plaqueStyle === "split-right"
                          ? "0 10px 30px rgba(0,0,0,0.08)"
                          : undefined,
                      border:
                        plaqueStyle === "glass"
                          ? "1px solid rgba(255, 255, 255, 0.4)"
                          : "1px solid rgba(0,0,0,0.05)",
                      opacity: plaqueOpacity / 100,
                      zIndex: 1,
                    }}
                  >
                    {!isExporting && (
                      <div
                        className="absolute -bottom-1.5 -right-1.5 w-4.5 h-4.5 bg-indigo-600 rounded-full shadow border-2 border-white cursor-se-resize flex items-center justify-center opacity-100 z-20 pointer-events-auto"
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDraggingPlaqueRendered({
                            offsetX: 0,
                            offsetY: 0,
                            type: "resize",
                          });
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Modular Header */}
                {enableHeader && (
                  <div
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActivePanelTab("layout");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "layout-header-section",
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 50);
                    }}
                    title="Click to edit Header"
                    className="absolute left-0 right-0 top-0 flex items-center justify-between px-4 z-10 cursor-pointer hover:ring-2 hover:ring-indigo-600 transition-all pointer-events-auto animate-fade-in"
                    style={{
                      height: `${headerHeight}%`,
                      backgroundColor: headerBgColor,
                      borderBottom:
                        headerStyle === "bordered"
                          ? "1px solid rgba(0,0,0,0.08)"
                          : headerStyle === "accent-bar"
                            ? "2px solid #4f46e5"
                            : "none",
                      backdropFilter:
                        headerStyle === "glass" ? "blur(12px)" : undefined,
                      color: headerTextColor,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    <div className="flex items-center gap-2 h-full">
                      {headerShowLogo && activeBrand?.logoUrl ? (
                        <img
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          src={activeBrand.logoUrl}
                          alt="Logo"
                          className="object-contain"
                          style={{ maxHeight: "60%" }}
                        />
                      ) : (
                        headerShowEyebrow && (
                          <span 
                            style={{ fontSize: `${headerFontSize}cqw` }}
                            className="font-black tracking-widest uppercase opacity-75"
                          >
                            {headerText}
                          </span>
                        )
                      )}
                    </div>
                    <div 
                      style={{ fontSize: `calc(${headerFontSize}cqw * 1.1)` }}
                      className="font-bold font-mono opacity-60"
                    >
                      {activeBrand?.companyName || "PlataPost"}
                    </div>
                  </div>
                )}

                {/* Modular Footer */}
                {enableFooter && (
                  <div
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActivePanelTab("layout");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "layout-footer-section",
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 50);
                    }}
                    title="Click to edit Footer"
                    className="absolute left-0 right-0 bottom-0 flex items-center justify-between px-4 z-10 cursor-pointer hover:ring-2 hover:ring-indigo-600 transition-all pointer-events-auto animate-fade-in"
                    style={{
                      height: `${footerHeight}%`,
                      backgroundColor: footerBgColor,
                      borderTop:
                        footerStyle === "bordered"
                          ? "1px solid rgba(0,0,0,0.08)"
                          : footerStyle === "accent-bar"
                            ? "2px solid #4f46e5"
                            : "none",
                      backdropFilter:
                        footerStyle === "glass" ? "blur(12px)" : undefined,
                      color: footerTextColor,
                      fontFamily: "font-sans",
                      fontSize: `${footerFontSize}cqw`,
                    }}
                  >
                    <div className="h-full flex items-center gap-2">
                      {footerShowUrl && footerUrlAlign === "left" && (
                        <span
                          className="font-semibold transition-all inline-block"
                          style={{
                            backgroundColor: footerUrlBgColor,
                            border: `${footerUrlBorderWidth}px solid ${footerUrlBorderColor}`,
                            borderRadius: `${footerUrlBorderRadius}px`,
                            padding: `${footerUrlPadding}px`,
                            color: footerUrlTextColor || footerTextColor,
                          }}
                        >
                          {activeBrand?.companyName
                            ? activeBrand.companyName
                                .toLowerCase()
                                .replace(/\s+/g, "") + ".com"
                            : "example.com"}
                        </span>
                      )}
                      {!footerShowUrl && footerShowLogo && activeBrand?.logoUrl && footerUrlAlign === "left" && (
                        <img
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          src={activeBrand.logoUrl}
                          alt="Logo"
                          className="object-contain"
                          style={{ maxHeight: "60%" }}
                        />
                      )}
                      {footerText && footerTextAlign === "left" && (
                        <span
                          className="opacity-80 font-medium transition-all inline-block"
                          style={{
                            backgroundColor: footerLegalBgColor,
                            border: `${footerLegalBorderWidth}px solid ${footerLegalBorderColor}`,
                            borderRadius: `${footerLegalBorderRadius}px`,
                            padding: `${footerLegalPadding}px`,
                            color: footerLegalTextColor || footerTextColor,
                          }}
                        > {(footerText.includes('[LOREM') || footerText.includes('FOOTER')) ? (activeBrand?.companyName || '') : footerText} </span>
                      )}
                      {footerShowPageNum && footerPageNumAlign === "left" && (
                        <span
                          className="font-mono font-bold transition-all inline-block"
                          style={{
                            fontSize: "0.9em",
                            backgroundColor: footerPageNumBgColor,
                            border: `${footerPageNumBorderWidth}px solid ${footerPageNumBorderColor}`,
                            borderRadius: `${footerPageNumBorderRadius}px`,
                            padding: `${footerPageNumPadding}px`,
                            color: footerPageNumTextColor || footerTextColor,
                          }}
                        >
                          01 / 01
                        </span>
                      )}
                    </div>
                    <div className="h-full flex items-center gap-2 justify-end">
                      {footerShowUrl && footerUrlAlign === "right" && (
                        <span
                          className="font-semibold transition-all inline-block"
                          style={{
                            backgroundColor: footerUrlBgColor,
                            border: `${footerUrlBorderWidth}px solid ${footerUrlBorderColor}`,
                            borderRadius: `${footerUrlBorderRadius}px`,
                            padding: `${footerUrlPadding}px`,
                            color: footerUrlTextColor || footerTextColor,
                          }}
                        >
                          {activeBrand?.companyName
                            ? activeBrand.companyName
                                .toLowerCase()
                                .replace(/\s+/g, "") + ".com"
                            : "example.com"}
                        </span>
                      )}
                      {!footerShowUrl && footerShowLogo && activeBrand?.logoUrl && footerUrlAlign === "right" && (
                        <img
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          src={activeBrand.logoUrl}
                          alt="Logo"
                          className="object-contain"
                          style={{ maxHeight: "60%" }}
                        />
                      )}
                      {footerText && footerTextAlign === "right" && (
                        <span
                          className="opacity-80 font-medium transition-all inline-block"
                          style={{
                            backgroundColor: footerLegalBgColor,
                            border: `${footerLegalBorderWidth}px solid ${footerLegalBorderColor}`,
                            borderRadius: `${footerLegalBorderRadius}px`,
                            padding: `${footerLegalPadding}px`,
                            color: footerLegalTextColor || footerTextColor,
                          }}
                        > {(footerText.includes('[LOREM') || footerText.includes('FOOTER')) ? (activeBrand?.companyName || '') : footerText} </span>
                      )}
                      {footerShowPageNum && footerPageNumAlign === "right" && (
                        <span
                          className="font-mono font-bold transition-all inline-block"
                          style={{
                            fontSize: "0.9em",
                            backgroundColor: footerPageNumBgColor,
                            border: `${footerPageNumBorderWidth}px solid ${footerPageNumBorderColor}`,
                            borderRadius: `${footerPageNumBorderRadius}px`,
                            padding: `${footerPageNumPadding}px`,
                            color: footerPageNumTextColor || footerTextColor,
                          }}
                        >
                          01 / 01
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Modular Logo */}
                {logoPosition !== "none" &&
                  logoPosition !== "header" &&
                  logoPosition !== "footer" && (
                    <div
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActivePanelTab("layout");
                        setTimeout(() => {
                          const element = document.getElementById(
                            "layout-logo-section",
                          );
                          if (element) {
                            element.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }
                        }, 50);
                        if (logoPosition === "floating") {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDraggingLogoRendered({
                            offsetX: e.clientX - rect.left,
                            offsetY: e.clientY - rect.top,
                            type: "move",
                          });
                        }
                      }}
                      title="Click to edit Brand Logo"
                      className={`absolute z-10 cursor-pointer pointer-events-auto transition-all flex h-auto p-0.5 rounded group min-h-[30px] ${
                        isExporting
                          ? ""
                          : "hover:ring-2 hover:ring-indigo-600 ring-1 ring-indigo-500/30 hover:ring-indigo-500/60 border border-indigo-500/20"
                      }`}
                      style={{
                        left:
                          logoPosition === "floating"
                            ? `${logoLeft}%`
                            : logoAlignment === "left"
                              ? "5%"
                              : logoAlignment === "center"
                                ? "42.5%"
                                : "80%",
                        top:
                          logoPosition === "floating"
                            ? `${logoTop}%`
                            : logoPosition === "content-top"
                              ? "5%"
                              : "85%",
                        width: `${logoSize}%`,
                        height: `${logoSize}%`,
                        justifyContent:
                          logoAlignment === "center"
                            ? "center"
                            : logoAlignment === "right"
                              ? "flex-end"
                              : "flex-start",
                      }}
                    >
                      {logoPosition === "floating" && !isExporting && (
                        <div
                          className="absolute -bottom-2 -right-2 w-5 h-5 bg-indigo-600 rounded-full shadow border-2 border-white cursor-se-resize flex items-center justify-center opacity-100 z-20 pointer-events-auto"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDraggingLogoRendered({
                              offsetX: 0,
                              offsetY: 0,
                              type: "resize",
                            });
                          }}
                        />
                      )}
                      {activeBrand?.logoUrl ? (
                        <img
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          src={activeBrand.logoUrl}
                          alt="Brand Logo"
                          className="max-w-full max-h-full object-contain pointer-events-none select-none"
                        />
                      ) : (
                        <div className="bg-black/10 text-black px-2 py-1 rounded text-[8px] font-black tracking-wider uppercase border border-dashed border-black/20 text-center flex items-center justify-center pointer-events-none select-none">
                          {activeBrand?.companyName || "Logo"}
                        </div>
                      )}
                    </div>
                  )}

                {savedTemplate.boxes.map((box) => {
                  const textContent = generatedPostData
                    ? generatedPostData[box.id]
                    : box.text;

                  // Completely hide empty icon-lists so body text can expand into the space
                  if (
                    generatedPostData &&
                    box.type === "icon-list" &&
                    !String(textContent).trim()
                  ) {
                    return null;
                  }

                  const isIconList = box.type === "icon-list";
                  const hasBullets =
                    isIconList && String(textContent).includes("\n");
                  const flowNaturally =
                    box.flowNaturally ||
                    (generatedPostData && box.type === "body");
                  const isButton = box.type === "cta" || !!box.renderAsButton;

                  return (
                    <div
                      id={`rendered-box-${box.id}`}
                      key={box.id}
                      className={`absolute group hover:outline hover:outline-2 hover:outline-blue-500/50 hover:bg-blue-500/5 transition-all ${activeRenderedBoxId === box.id ? "z-40 ring-1 ring-blue-500/30" : "z-20"}`}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDraggingRendered({
                          id: box.id,
                          offsetX: e.clientX - rect.left,
                          offsetY: e.clientY - rect.top,
                          type: "move",
                        });
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveRenderedBoxId(box.id);
                        if (!expandedPayloadIds.includes(box.id)) {
                          setExpandedPayloadIds((prev) => [...prev, box.id]);
                        }
                        setTimeout(() => {
                          const el = document.getElementById(`input-${box.id}`);
                          if (el) {
                            el.focus();
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }
                        }, 50);
                      }}
                      style={{
                        top: `${box.top}%`,
                        left: `${box.left}%`,
                        width: `${box.width}%`,
                        height: flowNaturally ? "auto" : `${box.height}%`,
                        minHeight: flowNaturally ? `${box.height}%` : undefined,
                        boxSizing: "border-box",
                        cursor:
                          draggingRendered?.type === "move" &&
                          draggingRendered.id === box.id
                            ? "grabbing"
                            : "grab",
                      }}
                    >
                      <div
                        className="absolute -bottom-2 -right-2 w-5 h-5 bg-blue-500 rounded-full shadow border-2 border-white cursor-se-resize flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20"
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDraggingRendered({
                            id: box.id,
                            offsetX: 0,
                            offsetY: 0,
                            type: "resize",
                          });
                        }}
                      />
                      {box.type === "image" ? (
                        <div className="w-full h-full overflow-hidden flex items-center justify-center rounded-lg border border-dashed border-indigo-200 bg-indigo-50/15 shadow-inner select-none pointer-events-none">
                          {box.imageUrl ? (
                            <img
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              src={box.imageUrl}
                              alt="Dynamic Media"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center p-4 text-center text-indigo-400 gap-1">
                              <ImageIcon className="w-6 h-6 stroke-[1.5]" />
                              <span className="text-[8px] font-black tracking-widest uppercase font-mono">
                                Image Placeholder
                              </span>
                              <span className="text-[7px] font-sans">
                                Choose layout OR upload below
                              </span>
                            </div>
                          )}
                        </div>
                      ) : box.type === "logo" ? (
                        <div className="w-full h-full flex items-center justify-center overflow-hidden pointer-events-none select-none">
                          {activeBrand?.logoUrl ? (
                            <img
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              src={activeBrand.logoUrl}
                              alt="Brand Logo"
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full bg-black/5 flex items-center justify-center border border-dashed border-black/10 text-black/40 font-bold text-[10px] p-2 text-center break-words opacity-80 backdrop-blur-sm shadow-inner rounded-md">
                              {activeBrand?.companyName || "Brand Logo"}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className={`w-full flex overflow-visible ${activeRenderedBoxId === box.id ? "pointer-events-auto" : "pointer-events-none select-none"} ${box.fontFamily} ${isIconList ? "flex-col gap-2" : ""}`}
                          style={{
                            height: flowNaturally ? "auto" : "100%",
                            color: box.color,
                            fontSize: `${box.fontSize}cqw`,
                            lineHeight: box.lineHeight ?? 1.1,
                            letterSpacing: box.letterSpacing ?? "-0.02em",
                            transform: `translate(${box.xOffset || 0}em, ${box.yOffset || 0}em)`,
                            textAlign: box.textAlign || "left",
                          }}
                        >
                          {activeRenderedBoxId === box.id && (
                            <div
                              className={`absolute ${box.top < 15 ? "-bottom-14" : "-top-14"} left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white rounded-lg shadow-xl border border-gray-200 p-1.5 z-50 !font-sans !font-normal !normal-case !tracking-normal !text-gray-900 pointer-events-auto`}
                              style={{
                                fontSize: "14px",
                                letterSpacing: "normal",
                                textTransform: "none",
                                lineHeight: "normal",
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              <label
                                className="flex items-center gap-1 cursor-pointer hover:bg-gray-50 p-1 rounded"
                                title="Color"
                              >
                                <div
                                  className="w-5 h-5 rounded border border-gray-200 overflow-hidden"
                                  style={{ backgroundColor: box.color }}
                                >
                                  <input
                                    type="color"
                                    className="opacity-0 w-full h-full"
                                    value={box.color}
                                    onChange={(e) =>
                                      updateSavedTemplateBox(box.id, {
                                        color: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </label>
                              <div className="w-px h-6 bg-gray-200 mx-1" />
                              <input
                                type="number"
                                className="w-14 text-sm border-gray-200 rounded px-1.5 py-1"
                                value={box.fontSize}
                                onChange={(e) =>
                                  updateSavedTemplateBox(box.id, {
                                    fontSize: Number(e.target.value),
                                  })
                                }
                                title="Font Size"
                              />
                              <div className="w-px h-6 bg-gray-200 mx-1" />
                              <div className="flex border border-gray-200 rounded overflow-hidden">
                                <button
                                  className={`p-1.5 ${box.textAlign === "left" || !box.textAlign ? "bg-gray-100 text-indigo-600" : "text-gray-500 hover:bg-gray-50"}`}
                                  onClick={() =>
                                    updateSavedTemplateBox(box.id, {
                                      textAlign: "left",
                                    })
                                  }
                                >
                                  <AlignLeft className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className={`p-1.5 ${box.textAlign === "center" ? "bg-gray-100 text-indigo-600" : "text-gray-500 hover:bg-gray-50"}`}
                                  onClick={() =>
                                    updateSavedTemplateBox(box.id, {
                                      textAlign: "center",
                                    })
                                  }
                                >
                                  <AlignCenter className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  className={`p-1.5 ${box.textAlign === "right" ? "bg-gray-100 text-indigo-600" : "text-gray-500 hover:bg-gray-50"}`}
                                  onClick={() =>
                                    updateSavedTemplateBox(box.id, {
                                      textAlign: "right",
                                    })
                                  }
                                >
                                  <AlignRight className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              {box.type === "icon-list" && (
                                <>
                                  <div className="w-px h-6 bg-gray-200 mx-1" />
                                  <select
                                    className="flex-1 text-sm border-gray-200 rounded px-2 py-1 leading-none"
                                    value={box.iconType || "check"}
                                    onChange={(e) =>
                                      updateSavedTemplateBox(box.id, {
                                        iconType: e.target.value as any,
                                      })
                                    }
                                  >
                                    <option value="check">Check</option>
                                    <option value="star">Star</option>
                                    <option value="arrow">Arrow</option>
                                    <option value="plus">Plus</option>
                                  </select>
                                </>
                              )}
                            </div>
                          )}

                          {isIconList ? (
                            String(textContent)
                              .split("\n")
                              .filter(Boolean)
                              .map((line, i) => {
                                const parsed = parseListLine(
                                  line,
                                  box.iconType,
                                  box.customIcon,
                                  i,
                                );
                                return (
                                  <div
                                    key={i}
                                    className="flex items-start gap-2"
                                  >
                                    {!parsed.noIcon && (
                                      <RenderListIcon
                                        iconType={parsed.iconType}
                                        customIcon={parsed.customIcon}
                                        containerShape={parsed.containerShape}
                                        containerStyle={parsed.containerStyle}
                                        iconColor={
                                          parsed.iconColor || box.color
                                        }
                                        backgroundColor={
                                          parsed.backgroundColor || box.color
                                        }
                                        index={i}
                                        textColor={box.color}
                                      />
                                    )}
                                    <div className="opacity-90 leading-tight w-full flex-1 min-w-0">
                                      <AutoFitText
                                        text={parsed.text}
                                        box={{
                                          ...box,
                                          type: "eyebrow",
                                          text: parsed.text,
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })
                          ) : isButton ? (
                            <div
                              className="w-full h-full flex items-center gap-2.5 transition-all px-3 py-1.5 overflow-hidden"
                              style={{
                                backgroundColor:
                                  box.backgroundColor || "transparent",
                                borderRadius: `${box.borderRadius ?? 8}px`,
                                border:
                                  box.borderWidth && box.borderColor
                                    ? `${box.borderWidth}px solid ${box.borderColor}`
                                    : "none",
                                justifyContent:
                                  box.textAlign === "center"
                                    ? "center"
                                    : box.textAlign === "right"
                                      ? "flex-end"
                                      : "flex-start",
                              }}
                            >
                              {box.iconPlacement === "before" && (
                                <RenderListIcon box={box} />
                              )}
                              <div
                                className="min-w-0 flex-1 flex items-center"
                                style={{
                                  height: flowNaturally ? "auto" : "100%",
                                  justifyContent:
                                    box.textAlign === "center"
                                      ? "center"
                                      : box.textAlign === "right"
                                        ? "flex-end"
                                        : "flex-start",
                                }}
                              >
                                {flowNaturally ? (
                                  <div
                                    style={{
                                      whiteSpace: "pre-wrap",
                                      width: "100%",
                                      textAlign: box.textAlign || "left",
                                    }}
                                  >
                                    {String(textContent)}
                                  </div>
                                ) : (
                                  <AutoFitText
                                    text={String(textContent)}
                                    box={box}
                                  />
                                )}
                              </div>
                              {(box.iconPlacement === "after" ||
                                !box.iconPlacement) &&
                                box.iconPlacement !== "none" && (
                                  <RenderListIcon box={box} />
                                )}
                            </div>
                          ) : box.type === "divider" ? (
                            <div className="w-full h-full flex items-center justify-center pointer-events-none">
                              <div
                                style={{
                                  width: "100%",
                                  height: `${box.borderWidth || 4}px`,
                                  backgroundColor: box.color || "#e5e7eb",
                                  borderRadius: "9999px",
                                }}
                              />
                            </div>
                          ) : (
                            <div
                              className="w-full opacity-90 flex items-center overflow-hidden"
                              style={{
                                height: flowNaturally ? "auto" : "100%",
                                backgroundColor: box.backgroundColor || undefined,
                                borderRadius: box.backgroundColor || box.borderWidth ? `${box.borderRadius ?? 8}px` : undefined,
                                border:
                                  box.borderWidth && box.borderColor
                                    ? `${box.borderWidth}px solid ${box.borderColor}`
                                    : undefined,
                                padding: box.padding !== undefined ? `${box.padding}px` : box.backgroundColor ? "12px" : undefined,
                                justifyContent:
                                  box.textAlign === "center"
                                    ? "center"
                                    : box.textAlign === "right"
                                      ? "flex-end"
                                      : "flex-start",
                              }}
                            >
                              <div
                                className="min-w-0 flex-1 flex items-center h-full"
                                style={{
                                  justifyContent:
                                    box.textAlign === "center"
                                      ? "center"
                                      : box.textAlign === "right"
                                        ? "flex-end"
                                        : "flex-start",
                                }}
                              >
                                {flowNaturally ? (
                                  <div
                                    style={{
                                      whiteSpace: "pre-wrap",
                                      width: "100%",
                                      textAlign: box.textAlign || "left",
                                    }}
                                  >
                                    {String(textContent)}
                                  </div>
                                ) : (
                                  <AutoFitText
                                    text={String(textContent)}
                                    box={box}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deploy to Blueprint Engine Modal */}
      {isBlueprintModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in font-sans">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-indigo-50/10">
              <div className="flex items-center gap-2">
                <div className="p-1 px-1.5 bg-indigo-100 text-indigo-700 rounded-md font-black text-xs">
                  PRO
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Deploy Layout Blueprint
                  </h3>
                  <p className="text-[11px] text-gray-500 font-medium">
                    Publish this visual structure to your hidden Blueprint Engine
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsBlueprintModalOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="text-[11px] p-3.5 bg-indigo-50/40 rounded-xl text-indigo-950 font-medium leading-relaxed border border-indigo-50/80">
                ⚡ <strong className="text-indigo-900 font-bold">Standardized Layout Extraction:</strong> To make this design fully dynamic and multi-tenant friendly across brands, we automatically <strong className="text-indigo-900 font-bold">sanitize & abstract</strong> localized copywriting, brand typography, logo graphics, and footer sentences into clean programmatic placeholders (like <code className="bg-white/80 px-1 py-0.2 rounded font-mono border border-indigo-100">[LOREM_HEADING]</code> or <code className="bg-white/80 px-1 py-0.2 rounded font-mono border border-indigo-100">[LOREM_ICON_LIST]</code>). Only structural box bounds, colors, plaques, headers, and backgrounds are persisted.
              </div>

              {/* Grid properties */}
              <div className="grid grid-cols-2 gap-4">
                {/* naming convention inputs */}
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                    Blueprint Archetype Style
                  </label>
                  <select
                    value={blueprintStylePreset}
                    onChange={(e) => setBlueprintStylePreset(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg p-2 bg-white text-gray-900 font-sans focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="BENTO_GRID">BENTO_GRID (Bento Modern Grid)</option>
                    <option value="MINIMAL_CLEAN">MINIMAL_CLEAN (Minimal White Layout)</option>
                    <option value="ACCENT_BAR">ACCENT_BAR (Golden Accent-Bar Layout)</option>
                    <option value="GLASS_PLAQUE">GLASS_PLAQUE (Glass Plaque Overlay)</option>
                    <option value="COMPACT">COMPACT (Compact Side Story)</option>
                    <option value="CUSTOM">Custom Style...</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                    Use Case Category
                  </label>
                  <select
                    value={blueprintCategory}
                    onChange={(e) => setBlueprintCategory(e.target.value as any)}
                    className="w-full text-xs border border-gray-200 rounded-lg p-2 bg-white text-gray-900 font-sans focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="Educational">Educational Content</option>
                    <option value="Promotional">Promotional Conversion</option>
                    <option value="Tension Challenge">Tension/Challenge Hook</option>
                    <option value="Technical Anatomy">Technical Anatomy Mapping</option>
                    <option value="Case Study">Case Study / Quote Slide</option>
                    <option value="Generic">Generic Layout</option>
                  </select>
                </div>
              </div>

              {blueprintStylePreset === "CUSTOM" && (
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                    Custom Style Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. CYAN_NEON_GRID"
                    value={blueprintCustomStyle}
                    onChange={(e) => setBlueprintCustomStyle(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg p-2 bg-white text-gray-900 font-sans focus:ring-1 focus:ring-indigo-500"
                  />
                  <p className="text-[10px] text-gray-400">
                    Use uppercase letters, numbers, and underscores only.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                    Target Slide Count
                  </label>
                  <input
                    type="number"
                    min={2}
                    max={12}
                    value={blueprintSlideCount}
                    onChange={(e) => setBlueprintSlideCount(Math.max(2, parseInt(e.target.value) || 5))}
                    className="w-full text-xs border border-gray-200 rounded-lg p-2 bg-white text-gray-900 font-sans focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                    Persistent Naming Output
                  </label>
                  <div className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-indigo-700 font-bold overflow-x-auto select-all">
                    {blueprintStylePreset === "CUSTOM"
                      ? `${(blueprintCustomStyle || "CUSTOM_STYLE").toUpperCase().replace(/\s+/g, "_")} - ${blueprintCategory} (${blueprintSlideCount} Slides)`
                      : `${blueprintStylePreset} - ${blueprintCategory} (${blueprintSlideCount} Slides)`
                    }
                  </div>
                  <p className="text-[10px] text-gray-400">
                    Enforces clean nomenclature to prevent runtime lookup failure.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                  Blueprint Intent/Description
                </label>
                <input
                  type="text"
                  value={blueprintDescription}
                  onChange={(e) => setBlueprintDescription(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg p-2 bg-white text-gray-900 font-sans focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Narrative Visual directives */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] uppercase font-black text-gray-400 tracking-wider">
                    Programmatic Narrative & Directives
                  </label>
                  <span className="text-[9.5px] text-gray-400 font-medium">
                    Calculated from current sandbox coordinates
                  </span>
                </div>
                <textarea
                  rows={6}
                  value={blueprintNarrative}
                  onChange={(e) => setBlueprintNarrative(e.target.value)}
                  className="w-full text-[11px] font-mono border border-gray-200 rounded-lg p-2.5 bg-gray-900 text-green-400 focus:outline-none focus:ring-1 focus:ring-green-500 leading-relaxed"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setIsBlueprintModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-800 transition cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={isSavingBlueprint || blueprintSaveSuccess}
                onClick={handleDeployToBlueprintEngine}
                className={`px-5 py-2 text-xs font-bold text-white rounded-lg transition-all flex items-center gap-1.5 shadow-sm cursor-pointer ${
                  blueprintSaveSuccess
                    ? "bg-green-600"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isSavingBlueprint ? (
                  <>
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Deploying...
                  </>
                ) : blueprintSaveSuccess ? (
                  <>
                    <Lucide.Check className="w-4 h-4" />
                    Successfully Deployed!
                  </>
                ) : (
                  <>
                    <Lucide.UploadCloud className="w-4 h-4" />
                    Deploy to AI Engine
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
